"use client";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/hooks/use-mobile.ts
import * as React from "react";
var MOBILE_BREAKPOINT = 1024;
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(void 0);
  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return !!isMobile;
}

// src/components/ui/animated-check-icon.tsx
import * as React2 from "react";
import { jsx } from "react/jsx-runtime";
function AnimatedCheckIcon({ checked }) {
  const hasMounted = React2.useRef(false);
  const [animation, setAnimation] = React2.useState("none");
  React2.useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    setAnimation(checked ? "draw" : "reverse");
  }, [checked]);
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className: cn(
        "size-4 text-primary",
        animation === "draw" && "animate-check-draw",
        animation === "reverse" && "animate-check-draw-reverse"
      ),
      viewBox: "0 0 20 20",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      style: {
        strokeDasharray: 1,
        strokeDashoffset: animation === "none" ? checked ? 0 : 1 : void 0
      },
      children: /* @__PURE__ */ jsx("path", { d: "M14.75 6.5L8.25 13.5L5.25 10.3636", pathLength: "1" })
    }
  );
}

// src/components/ui/accordion.tsx
import { ChevronDownIcon } from "lucide-react";

// src/components/animate-ui/primitives/radix/accordion.tsx
import * as React5 from "react";
import { Accordion as AccordionPrimitive } from "radix-ui";
import { motion, AnimatePresence } from "motion/react";

// src/hooks/use-controlled-state.tsx
import * as React3 from "react";
function useControlledState(props) {
  const { value, defaultValue, onChange } = props;
  const [state, setInternalState] = React3.useState(
    value !== void 0 ? value : defaultValue
  );
  React3.useEffect(() => {
    if (value !== void 0) setInternalState(value);
  }, [value]);
  const setState = React3.useCallback(
    (next, ...args) => {
      setInternalState(next);
      onChange?.(next, ...args);
    },
    [onChange]
  );
  return [state, setState];
}

// src/lib/get-strict-context.tsx
import * as React4 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
function getStrictContext(name) {
  const Context = React4.createContext(void 0);
  const Provider = ({
    value,
    children
  }) => /* @__PURE__ */ jsx2(Context.Provider, { value, children });
  const useSafeContext = () => {
    const ctx = React4.useContext(Context);
    if (ctx === void 0) {
      throw new Error(`useContext must be used within ${name ?? "a Provider"}`);
    }
    return ctx;
  };
  return [Provider, useSafeContext];
}

// src/components/animate-ui/primitives/radix/accordion.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var [AccordionProvider, useAccordion] = getStrictContext("AccordionContext");
var [AccordionItemProvider, useAccordionItem] = getStrictContext("AccordionItemContext");
function Accordion(props) {
  const [value, setValue] = useControlledState({
    value: props?.value,
    defaultValue: props?.defaultValue,
    onChange: props?.onValueChange
  });
  return /* @__PURE__ */ jsx3(AccordionProvider, { value: { value, setValue }, children: /* @__PURE__ */ jsx3(
    AccordionPrimitive.Root,
    {
      "data-slot": "accordion",
      ...props,
      onValueChange: setValue
    }
  ) });
}
function AccordionItem(props) {
  const { value } = useAccordion();
  const [isOpen, setIsOpen] = React5.useState(
    value?.includes(props?.value) ?? false
  );
  React5.useEffect(() => {
    setIsOpen(value?.includes(props?.value) ?? false);
  }, [value, props?.value]);
  return /* @__PURE__ */ jsx3(AccordionItemProvider, { value: { isOpen, setIsOpen, value: props.value }, children: /* @__PURE__ */ jsx3(AccordionPrimitive.Item, { "data-slot": "accordion-item", ...props }) });
}
function AccordionHeader(props) {
  return /* @__PURE__ */ jsx3(AccordionPrimitive.Header, { "data-slot": "accordion-header", ...props });
}
function AccordionTrigger(props) {
  return /* @__PURE__ */ jsx3(AccordionPrimitive.Trigger, { "data-slot": "accordion-trigger", ...props });
}
function AccordionContent({
  keepRendered = false,
  transition = { duration: 0.175, ease: "easeInOut" },
  ...props
}) {
  const { isOpen } = useAccordionItem();
  return /* @__PURE__ */ jsx3(AnimatePresence, { children: keepRendered ? /* @__PURE__ */ jsx3(AccordionPrimitive.Content, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx3(
    motion.div,
    {
      "data-slot": "accordion-content",
      initial: { height: 0, opacity: 0, "--mask-stop": "0%", y: 20 },
      animate: isOpen ? { height: "auto", opacity: 1, "--mask-stop": "100%", y: 0 } : { height: 0, opacity: 0, "--mask-stop": "0%", y: 20 },
      transition,
      style: {
        maskImage: "linear-gradient(black var(--mask-stop), transparent var(--mask-stop))",
        WebkitMaskImage: "linear-gradient(black var(--mask-stop), transparent var(--mask-stop))",
        overflow: "hidden"
      },
      ...props
    },
    "accordion-content"
  ) }) : isOpen && /* @__PURE__ */ jsx3(AccordionPrimitive.Content, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx3(
    motion.div,
    {
      "data-slot": "accordion-content",
      initial: { height: 0, opacity: 0, "--mask-stop": "0%", y: 20 },
      animate: {
        height: "auto",
        opacity: 1,
        "--mask-stop": "100%",
        y: 0
      },
      exit: { height: 0, opacity: 0, "--mask-stop": "0%", y: 20 },
      transition,
      style: {
        maskImage: "linear-gradient(black var(--mask-stop), transparent var(--mask-stop))",
        WebkitMaskImage: "linear-gradient(black var(--mask-stop), transparent var(--mask-stop))",
        overflow: "hidden"
      },
      ...props
    },
    "accordion-content"
  ) }) });
}

// src/components/ui/accordion.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
function Accordion2({
  ...props
}) {
  return /* @__PURE__ */ jsx4(Accordion, { ...props });
}
function AccordionItem2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx4(
    AccordionItem,
    {
      "data-slot": "accordion-item",
      className: cn("border-b last:border-b-0", className),
      ...props
    }
  );
}
function AccordionTrigger2({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx4(AccordionHeader, { className: "flex", children: /* @__PURE__ */ jsxs(
    AccordionTrigger,
    {
      "data-slot": "accordion-trigger",
      className: cn(
        "flex flex-1 cursor-pointer items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx4(ChevronDownIcon, { className: "pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200" })
      ]
    }
  ) });
}
function AccordionContent2({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx4(
    AccordionContent,
    {
      "data-slot": "accordion-content",
      className: "text-sm text-muted-foreground",
      ...props,
      children: /* @__PURE__ */ jsx4("div", { className: cn("pt-0 pb-4", className), children })
    }
  );
}

// src/components/ui/alert.tsx
import { cva } from "class-variance-authority";
import { jsx as jsx5 } from "react/jsx-runtime";
var alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive: "bg-card text-destructive *:data-[slot=alert-description]:text-destructive/75 [&>svg]:text-current"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Alert({
  className,
  variant,
  ...props
}) {
  return /* @__PURE__ */ jsx5(
    "div",
    {
      "data-slot": "alert",
      role: "alert",
      className: cn(alertVariants({ variant }), className),
      ...props
    }
  );
}
function AlertTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx5(
    "div",
    {
      "data-slot": "alert-title",
      className: cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      ),
      ...props
    }
  );
}
function AlertDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx5(
    "div",
    {
      "data-slot": "alert-description",
      className: cn(
        "col-start-2 grid justify-items-start gap-1 text-sm font-normal text-muted-foreground [&_p]:leading-relaxed",
        className
      ),
      ...props
    }
  );
}

// src/components/animate-ui/primitives/radix/alert-dialog.tsx
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence2, motion as motion2 } from "motion/react";
import { jsx as jsx6 } from "react/jsx-runtime";
var [AlertDialogProvider, useAlertDialog] = getStrictContext("AlertDialogContext");
function AlertDialog(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx6(AlertDialogProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Root,
    {
      "data-slot": "alert-dialog",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function AlertDialogTrigger(props) {
  return /* @__PURE__ */ jsx6(AlertDialogPrimitive.Trigger, { "data-slot": "alert-dialog-trigger", ...props });
}
function AlertDialogPortal(props) {
  const { isOpen } = useAlertDialog();
  return /* @__PURE__ */ jsx6(AnimatePresence2, { children: isOpen && /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Portal,
    {
      "data-slot": "alert-dialog-portal",
      forceMount: true,
      ...props
    }
  ) });
}
function AlertDialogOverlay({
  transition = { duration: 0.1, ease: "easeInOut" },
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Overlay,
    {
      "data-slot": "alert-dialog-overlay",
      asChild: true,
      forceMount: true,
      children: /* @__PURE__ */ jsx6(
        motion2.div,
        {
          initial: { opacity: 0, filter: "blur(4px)" },
          animate: { opacity: 1, filter: "blur(0px)" },
          exit: { opacity: 0, filter: "blur(4px)" },
          transition,
          ...props
        },
        "alert-dialog-overlay"
      )
    }
  );
}
function AlertDialogContent({
  from = "top",
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  transition = { type: "spring", stiffness: 300, damping: 30 },
  ...props
}) {
  const initialRotation = from === "bottom" || from === "left" ? "20deg" : "-20deg";
  const isVertical = from === "top" || from === "bottom";
  const rotateAxis = isVertical ? "rotateX" : "rotateY";
  return /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Content,
    {
      asChild: true,
      forceMount: true,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      children: /* @__PURE__ */ jsx6(
        motion2.div,
        {
          "data-slot": "alert-dialog-content",
          initial: {
            opacity: 0,
            filter: "blur(4px)",
            transform: `perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`
          },
          animate: {
            opacity: 1,
            filter: "blur(0px)",
            transform: `perspective(500px) ${rotateAxis}(0deg) scale(1)`
          },
          exit: {
            opacity: 0,
            filter: "blur(4px)",
            transform: `perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`
          },
          transition,
          ...props
        },
        "alert-dialog-content"
      )
    }
  );
}
function AlertDialogCancel(props) {
  return /* @__PURE__ */ jsx6(AlertDialogPrimitive.Cancel, { "data-slot": "alert-dialog-cancel", ...props });
}
function AlertDialogAction(props) {
  return /* @__PURE__ */ jsx6(AlertDialogPrimitive.Action, { "data-slot": "alert-dialog-action", ...props });
}
function AlertDialogTitle(props) {
  return /* @__PURE__ */ jsx6(AlertDialogPrimitive.Title, { "data-slot": "alert-dialog-title", ...props });
}
function AlertDialogDescription(props) {
  return /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Description,
    {
      "data-slot": "alert-dialog-description",
      ...props
    }
  );
}

// src/components/ui/button.tsx
import * as React6 from "react";
import { cva as cva2 } from "class-variance-authority";
import { Slot } from "radix-ui";
import { jsx as jsx7 } from "react/jsx-runtime";
var buttonVariants = cva2(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Variant names match the legacy ui-kit Button API so dashboard
        // call-sites can migrate without renaming.
        primary: "bg-primary text-white shadow-btn inset-shadow-btn hover:bg-primary-hover active:bg-primary-active dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)]",
        danger: "bg-destructive text-white shadow-btn inset-shadow-btn hover:bg-destructive-hover active:bg-destructive-active focus-visible:ring-destructive/20 dark:bg-red-600 dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:bg-destructive-hover dark:active:bg-destructive-active dark:focus-visible:ring-destructive/40",
        // Dark mode mirrors the Select/Input secondary surface (bg-input/30 + a
        // flat white hairline, no drop shadow) so a secondary button sits flush
        // beside those controls; in light both already share shadow-btn-secondary.
        secondary: "bg-white text-gray-600 shadow-btn-secondary hover:shadow-btn-secondary-hover dark:bg-input/30 dark:text-gray-100 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.25)]",
        tertiary: "bg-white text-gray-600 shadow-btn-tertiary hover:shadow-btn-tertiary-hover dark:bg-gray-800 dark:text-gray-100",
        muted: "bg-transparent text-gray-600 shadow-btn-muted hover:shadow-btn-muted-hover hover:bg-[rgb(252_252_252)] hover:text-gray-700 dark:text-gray-100 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:bg-white/5",
        "subtle-primary": "text-primary underline-offset-4 hover:underline dark:text-[oklch(0.725_0.16_259.7)]"
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-lg px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 rounded-lg px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-lg px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "default"
    }
  }
);
var Button = React6.forwardRef(function Button2({ className, variant = "primary", size = "default", asChild = false, ...props }, ref) {
  const Comp = asChild ? Slot.Root : "button";
  return /* @__PURE__ */ jsx7(
    Comp,
    {
      ref,
      "data-slot": "button",
      "data-variant": variant,
      "data-size": size,
      className: cn(buttonVariants({ variant, size, className })),
      ...props
    }
  );
});

// src/components/ui/alert-dialog.tsx
import { jsx as jsx8, jsxs as jsxs2 } from "react/jsx-runtime";
function AlertDialog2({
  ...props
}) {
  return /* @__PURE__ */ jsx8(AlertDialog, { ...props });
}
function AlertDialogTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx8(AlertDialogTrigger, { "data-slot": "alert-dialog-trigger", ...props });
}
function AlertDialogPortal2({
  ...props
}) {
  return /* @__PURE__ */ jsx8(AlertDialogPortal, { "data-slot": "alert-dialog-portal", ...props });
}
function AlertDialogOverlay2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    AlertDialogOverlay,
    {
      "data-slot": "alert-dialog-overlay",
      className: cn(
        "fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
function AlertDialogContent2({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsxs2(AlertDialogPortal2, { children: [
    /* @__PURE__ */ jsx8(AlertDialogOverlay2, {}),
    /* @__PURE__ */ jsx8(
      AlertDialogContent,
      {
        "data-slot": "alert-dialog-content",
        "data-size": size,
        className: cn(
          "group/alert-dialog-content fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg bg-background p-6 shadow-md ring-1 ring-black/5 duration-200 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[size=sm]:max-w-xs data-[size=default]:sm:max-w-lg",
          className
        ),
        ...props
      }
    )
  ] });
}
function AlertDialogHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    "div",
    {
      "data-slot": "alert-dialog-header",
      className: cn(
        "grid grid-rows-[auto_1fr] place-items-center gap-1.5 text-center has-data-[slot=alert-dialog-media]:grid-rows-[auto_auto_1fr] has-data-[slot=alert-dialog-media]:gap-x-6 sm:group-data-[size=default]/alert-dialog-content:place-items-start sm:group-data-[size=default]/alert-dialog-content:text-left sm:group-data-[size=default]/alert-dialog-content:has-data-[slot=alert-dialog-media]:grid-rows-[auto_1fr]",
        className
      ),
      ...props
    }
  );
}
function AlertDialogFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    "div",
    {
      "data-slot": "alert-dialog-footer",
      className: cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end group-data-[size=sm]/alert-dialog-content:grid group-data-[size=sm]/alert-dialog-content:grid-cols-2",
        className
      ),
      ...props
    }
  );
}
function AlertDialogTitle2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    AlertDialogTitle,
    {
      "data-slot": "alert-dialog-title",
      className: cn(
        "text-lg font-medium tracking-tight sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2",
        className
      ),
      ...props
    }
  );
}
function AlertDialogDescription2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    AlertDialogDescription,
    {
      "data-slot": "alert-dialog-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function AlertDialogMedia({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    "div",
    {
      "data-slot": "alert-dialog-media",
      className: cn(
        "mb-2 inline-flex size-16 items-center justify-center rounded-md bg-muted sm:group-data-[size=default]/alert-dialog-content:row-span-2 *:[svg:not([class*='size-'])]:size-8",
        className
      ),
      ...props
    }
  );
}
function AlertDialogAction2({
  className,
  variant = "primary",
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx8(Button, { variant, size, asChild: true, children: /* @__PURE__ */ jsx8(
    AlertDialogAction,
    {
      "data-slot": "alert-dialog-action",
      className: cn(className),
      ...props
    }
  ) });
}
function AlertDialogCancel2({
  className,
  variant = "muted",
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx8(Button, { variant, size, asChild: true, children: /* @__PURE__ */ jsx8(
    AlertDialogCancel,
    {
      "data-slot": "alert-dialog-cancel",
      className: cn(className),
      ...props
    }
  ) });
}

// src/components/ui/aspect-ratio.tsx
import { AspectRatio as AspectRatioPrimitive } from "radix-ui";
import { jsx as jsx9 } from "react/jsx-runtime";
function AspectRatio({
  ...props
}) {
  return /* @__PURE__ */ jsx9(AspectRatioPrimitive.Root, { "data-slot": "aspect-ratio", ...props });
}

// src/components/ui/avatar.tsx
import { Avatar as AvatarPrimitive } from "radix-ui";
import { jsx as jsx10 } from "react/jsx-runtime";
function Avatar({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    AvatarPrimitive.Root,
    {
      "data-slot": "avatar",
      "data-size": size,
      className: cn(
        "group/avatar relative flex size-8 shrink-0 overflow-hidden rounded-full select-none data-[size=lg]:size-10 data-[size=sm]:size-6",
        className
      ),
      ...props
    }
  );
}
function AvatarImage({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    AvatarPrimitive.Image,
    {
      "data-slot": "avatar-image",
      className: cn("aspect-square size-full", className),
      ...props
    }
  );
}
function AvatarFallback({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    AvatarPrimitive.Fallback,
    {
      "data-slot": "avatar-fallback",
      className: cn(
        "flex size-full items-center justify-center rounded-full bg-muted text-sm text-muted-foreground group-data-[size=sm]/avatar:text-xs",
        className
      ),
      ...props
    }
  );
}
function AvatarBadge({ className, ...props }) {
  return /* @__PURE__ */ jsx10(
    "span",
    {
      "data-slot": "avatar-badge",
      className: cn(
        "absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background select-none",
        "group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden",
        "group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2",
        "group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2",
        className
      ),
      ...props
    }
  );
}
function AvatarGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx10(
    "div",
    {
      "data-slot": "avatar-group",
      className: cn(
        "group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background",
        className
      ),
      ...props
    }
  );
}
function AvatarGroupCount({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    "div",
    {
      "data-slot": "avatar-group-count",
      className: cn(
        "relative flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm text-muted-foreground ring-2 ring-background group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/badge.tsx
import { cva as cva3 } from "class-variance-authority";
import { Slot as Slot2 } from "radix-ui";
import { jsx as jsx11 } from "react/jsx-runtime";
var badgeVariants = cva3(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive: "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline: "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot2.Root : "span";
  return /* @__PURE__ */ jsx11(
    Comp,
    {
      "data-slot": "badge",
      "data-variant": variant,
      className: cn(badgeVariants({ variant }), className),
      ...props
    }
  );
}

// src/components/ui/breadcrumb.tsx
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { Slot as Slot3 } from "radix-ui";
import { jsx as jsx12, jsxs as jsxs3 } from "react/jsx-runtime";
function Breadcrumb({ ...props }) {
  return /* @__PURE__ */ jsx12("nav", { "aria-label": "breadcrumb", "data-slot": "breadcrumb", ...props });
}
function BreadcrumbList({ className, ...props }) {
  return /* @__PURE__ */ jsx12(
    "ol",
    {
      "data-slot": "breadcrumb-list",
      className: cn(
        "flex flex-wrap items-center gap-1.5 text-sm font-medium break-words text-muted-foreground sm:gap-2.5",
        className
      ),
      ...props
    }
  );
}
function BreadcrumbItem({ className, ...props }) {
  return /* @__PURE__ */ jsx12(
    "li",
    {
      "data-slot": "breadcrumb-item",
      className: cn("inline-flex items-center gap-1.5", className),
      ...props
    }
  );
}
function BreadcrumbLink({
  asChild,
  className,
  ...props
}) {
  const Comp = asChild ? Slot3.Root : "a";
  return /* @__PURE__ */ jsx12(
    Comp,
    {
      "data-slot": "breadcrumb-link",
      className: cn("transition-colors hover:text-foreground", className),
      ...props
    }
  );
}
function BreadcrumbPage({ className, ...props }) {
  return /* @__PURE__ */ jsx12(
    "span",
    {
      "data-slot": "breadcrumb-page",
      role: "link",
      "aria-disabled": "true",
      "aria-current": "page",
      className: cn("font-medium text-foreground", className),
      ...props
    }
  );
}
function BreadcrumbSeparator({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx12(
    "li",
    {
      "data-slot": "breadcrumb-separator",
      role: "presentation",
      "aria-hidden": "true",
      className: cn("[&>svg]:size-3.5", className),
      ...props,
      children: children ?? /* @__PURE__ */ jsx12(ChevronRight, {})
    }
  );
}
function BreadcrumbEllipsis({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs3(
    "span",
    {
      "data-slot": "breadcrumb-ellipsis",
      role: "presentation",
      "aria-hidden": "true",
      className: cn("flex size-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx12(MoreHorizontal, { className: "size-4" }),
        /* @__PURE__ */ jsx12("span", { className: "sr-only", children: "More" })
      ]
    }
  );
}

// src/components/ui/button-group.tsx
import { cva as cva4 } from "class-variance-authority";
import { Slot as Slot4 } from "radix-ui";

// src/components/ui/separator.tsx
import { Separator as SeparatorPrimitive } from "radix-ui";
import { jsx as jsx13 } from "react/jsx-runtime";
function Separator({
  className,
  orientation = "horizontal",
  decorative = true,
  ...props
}) {
  return /* @__PURE__ */ jsx13(
    SeparatorPrimitive.Root,
    {
      "data-slot": "separator",
      decorative,
      orientation,
      className: cn(
        "shrink-0 bg-border data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/button-group.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var buttonGroupVariants = cva4(
  [
    "flex w-fit items-stretch rounded-lg",
    // Match secondary button shadow: outer drop shadow + bottom inset for depth
    "shadow-[0_1px_2px_0_rgb(0_0_0/0.05)] inset-shadow-[0_-1px_1px_0_rgb(0_0_0/0.07)]",
    "dark:shadow-none dark:inset-shadow-none",
    // Strip child shadow layers — we replace them with CSS border
    // Use !important to override button dark: variants which have higher specificity
    "[&>*:not([data-slot=button-group-separator])]:shadow-none! [&>*:not([data-slot=button-group-separator])]:inset-shadow-none!",
    // Add CSS border to every direct child (except separators)
    "[&>*:not([data-slot=button-group-separator])]:border [&>*:not([data-slot=button-group-separator])]:border-[rgb(0_0_0/0.1)]",
    // Dark mode border — match secondary button dark inset-shadow opacity (0.08)
    "dark:[&>*:not([data-slot=button-group-separator])]:border-[rgb(255_255_255/0.08)]",
    // Dark mode: apply secondary button appearance to children (bg, gradient, text, hover, active)
    "dark:[&>*:not([data-slot=button-group-separator])]:bg-gray-800!",
    "dark:[&>*:not([data-slot=button-group-separator])]:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.06),rgb(255_255_255/0))]!",
    "dark:[&>*:not([data-slot=button-group-separator])]:text-gray-100!",
    "dark:[&>*:not([data-slot=button-group-separator])]:hover:bg-secondary-hover!",
    "dark:[&>*:not([data-slot=button-group-separator])]:active:bg-secondary-active!",
    // Focus: raise above siblings so ring isn't clipped
    "[&>*]:focus-visible:relative [&>*]:focus-visible:z-10",
    // Nested button groups get a gap
    "has-[>[data-slot=button-group]]:gap-2",
    // Select / input tweaks
    "has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md",
    "[&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit",
    "[&>input]:flex-1"
  ].join(" "),
  {
    variants: {
      orientation: {
        horizontal: [
          // Round only the outer edges
          "[&>*]:rounded-none [&>:first-child:not([data-slot=button-group-separator])]:rounded-l-lg [&>:last-child:not([data-slot=button-group-separator])]:rounded-r-lg",
          // Remove left border on non-first children to prevent doubling (skip separators & items after separators)
          "[&>*:not(:first-child):not([data-slot=button-group-separator]):not([data-slot=button-group-separator]+*)]:border-l-0",
          // After a separator, reset rounding
          "[&>[data-slot=button-group-separator]+*]:rounded-l-none",
          // Before a separator, reset rounding (via has-selector on the child before it)
          "[&>*:has(+[data-slot=button-group-separator])]:rounded-r-none"
        ].join(" "),
        vertical: [
          // Round only the outer edges
          "flex-col [&>*]:rounded-none [&>:first-child:not([data-slot=button-group-separator])]:rounded-t-lg [&>:last-child:not([data-slot=button-group-separator])]:rounded-b-lg",
          // Remove top border on non-first children to prevent doubling (skip separators & items after separators)
          "[&>*:not(:first-child):not([data-slot=button-group-separator]):not([data-slot=button-group-separator]+*)]:border-t-0",
          // After a separator, reset rounding
          "[&>[data-slot=button-group-separator]+*]:rounded-t-none",
          // Before a separator, reset rounding
          "[&>*:has(+[data-slot=button-group-separator])]:rounded-b-none"
        ].join(" ")
      }
    },
    defaultVariants: {
      orientation: "horizontal"
    }
  }
);
function ButtonGroup({
  className,
  orientation,
  ...props
}) {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      role: "group",
      "data-slot": "button-group",
      "data-orientation": orientation,
      className: cn(buttonGroupVariants({ orientation }), className),
      ...props
    }
  );
}
function ButtonGroupText({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot4.Root : "div";
  return /* @__PURE__ */ jsx14(
    Comp,
    {
      className: cn(
        "flex items-center gap-2 rounded-md bg-white px-4 text-sm font-medium text-secondary-foreground dark:bg-gray-800 dark:text-gray-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function ButtonGroupSeparator({
  className,
  orientation = "vertical",
  ...props
}) {
  return /* @__PURE__ */ jsx14(
    Separator,
    {
      "data-slot": "button-group-separator",
      orientation,
      className: cn(
        "relative self-stretch !border-0",
        // Vertical separator (used in horizontal groups)
        "data-[orientation=vertical]:h-auto data-[orientation=vertical]:!w-1.5 data-[orientation=vertical]:my-px data-[orientation=vertical]:bg-gray-25 data-[orientation=vertical]:shadow-[0_1px_0_var(--tw-shadow-color,var(--color-gray-300)),0_-1px_0_var(--tw-shadow-color,var(--color-gray-200))] dark:data-[orientation=vertical]:bg-white/[0.03] dark:data-[orientation=vertical]:shadow-[0_1px_0_var(--color-gray-800),0_-1px_0_var(--color-gray-800)]",
        // Horizontal separator (used in vertical groups)
        "data-[orientation=horizontal]:!h-1.5 data-[orientation=horizontal]:w-auto data-[orientation=horizontal]:mx-px data-[orientation=horizontal]:bg-gray-25 data-[orientation=horizontal]:shadow-[1px_0_0_var(--tw-shadow-color,var(--color-gray-200)),-1px_0_0_var(--tw-shadow-color,var(--color-gray-200))] dark:data-[orientation=horizontal]:bg-white/[0.03] dark:data-[orientation=horizontal]:shadow-[1px_0_0_var(--color-gray-800),-1px_0_0_var(--color-gray-800)]",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/calendar.tsx
import * as React7 from "react";
import {
  ChevronDownIcon as ChevronDownIcon2,
  ChevronLeftIcon,
  ChevronRightIcon
} from "lucide-react";
import {
  DayPicker,
  getDefaultClassNames
} from "react-day-picker";
import { jsx as jsx15 } from "react/jsx-runtime";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "muted",
  formatters,
  components,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  return /* @__PURE__ */ jsx15(
    DayPicker,
    {
      showOutsideDays,
      className: cn(
        "group/calendar rounded-lg bg-background p-3 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      ),
      captionLayout,
      formatters: {
        formatMonthDropdown: (date) => date.toLocaleString("default", { month: "short" }),
        ...formatters
      },
      classNames: {
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) rounded-lg p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) rounded-lg p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative rounded-lg border border-input shadow-xs has-focus:border-ring has-focus:ring-[3px] has-focus:ring-ring/50 dark:shadow-none",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "absolute inset-0 bg-popover opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "font-medium select-none",
          captionLayout === "label" ? "text-sm" : "flex h-8 items-center gap-1 rounded-lg pr-1 pl-2 text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "flex-1 rounded-lg text-[0.8rem] font-normal text-muted-foreground select-none",
          defaultClassNames.weekday
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-(--cell-size) select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-[0.8rem] text-muted-foreground select-none",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative aspect-square h-full w-full p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-lg",
          props.showWeekNumber ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-lg" : "[&:first-child[data-selected=true]_button]:rounded-l-lg",
          defaultClassNames.day
        ),
        range_start: cn(
          defaultClassNames.range_start,
          "!rounded-l-lg bg-blue-100 dark:bg-blue-900/40"
        ),
        range_middle: cn(defaultClassNames.range_middle, "rounded-none"),
        range_end: cn(defaultClassNames.range_end, "!rounded-r-lg bg-blue-100 dark:bg-blue-900/40"),
        today: cn(
          defaultClassNames.today,
          "rounded-lg bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200 data-[selected=true]:rounded-none data-[selected=true]:bg-transparent"
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames
      },
      components: {
        Root: ({ className: className2, rootRef, ...props2 }) => {
          return /* @__PURE__ */ jsx15(
            "div",
            {
              "data-slot": "calendar",
              ref: rootRef,
              className: cn(className2),
              ...props2
            }
          );
        },
        Chevron: ({ className: className2, orientation, ...props2 }) => {
          if (orientation === "left") {
            return /* @__PURE__ */ jsx15(ChevronLeftIcon, { className: cn("size-4", className2), ...props2 });
          }
          if (orientation === "right") {
            return /* @__PURE__ */ jsx15(
              ChevronRightIcon,
              {
                className: cn("size-4", className2),
                ...props2
              }
            );
          }
          return /* @__PURE__ */ jsx15(ChevronDownIcon2, { className: cn("size-4", className2), ...props2 });
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props2 }) => {
          return /* @__PURE__ */ jsx15("td", { ...props2, children: /* @__PURE__ */ jsx15("div", { className: "flex size-(--cell-size) items-center justify-center text-center", children }) });
        },
        ...components
      },
      ...props
    }
  );
}
function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  const ref = React7.useRef(null);
  React7.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);
  return /* @__PURE__ */ jsx15(
    Button,
    {
      ref,
      variant: "muted",
      size: "icon",
      "data-day": day.date.toLocaleDateString(),
      "data-selected-single": modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle,
      "data-range-start": modifiers.range_start,
      "data-range-end": modifiers.range_end,
      "data-range-middle": modifiers.range_middle,
      className: cn(
        "flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-medium group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50 data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-blue-100 data-[range-middle=true]:text-blue-900 dark:data-[range-middle=true]:bg-blue-900/40 dark:data-[range-middle=true]:text-blue-200 data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-accent-foreground [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className
      ),
      ...props
    }
  );
}

// src/components/ui/card.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
function Card({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx16(
    "div",
    {
      "data-slot": "card",
      "data-size": size,
      className: cn(
        "group/card flex flex-col gap-6 bg-card text-card-foreground shadow-surface-raised dark:bg-gray-800 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]",
        size === "sm" ? "rounded-lg gap-4 py-4" : "rounded-xl py-6",
        className
      ),
      ...props
    }
  );
}
function CardHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx16(
    "div",
    {
      "data-slot": "card-header",
      className: cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 group-data-[size=sm]/card:px-4 group-data-[size=sm]/card:[.border-b]:pb-4",
        className
      ),
      ...props
    }
  );
}
function CardTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx16(
    "div",
    {
      "data-slot": "card-title",
      className: cn("leading-none font-semibold", className),
      ...props
    }
  );
}
function CardDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx16(
    "div",
    {
      "data-slot": "card-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function CardAction({ className, ...props }) {
  return /* @__PURE__ */ jsx16(
    "div",
    {
      "data-slot": "card-action",
      className: cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      ),
      ...props
    }
  );
}
function CardContent({ className, ...props }) {
  return /* @__PURE__ */ jsx16(
    "div",
    {
      "data-slot": "card-content",
      className: cn("px-6 text-sm group-data-[size=sm]/card:px-4", className),
      ...props
    }
  );
}
function CardFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx16(
    "div",
    {
      "data-slot": "card-footer",
      className: cn("flex items-center px-6 [.border-t]:pt-6 group-data-[size=sm]/card:px-4 group-data-[size=sm]/card:[.border-t]:pt-4", className),
      ...props
    }
  );
}

// src/components/ui/carousel.tsx
import * as React8 from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { jsx as jsx17, jsxs as jsxs4 } from "react/jsx-runtime";
var CarouselContext = React8.createContext(null);
function useCarousel() {
  const context = React8.useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}
function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y"
    },
    plugins
  );
  const [canScrollPrev, setCanScrollPrev] = React8.useState(false);
  const [canScrollNext, setCanScrollNext] = React8.useState(false);
  const onSelect = React8.useCallback((api2) => {
    if (!api2) return;
    setCanScrollPrev(api2.canScrollPrev());
    setCanScrollNext(api2.canScrollNext());
  }, []);
  const scrollPrev = React8.useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = React8.useCallback(() => {
    api?.scrollNext();
  }, [api]);
  const handleKeyDown = React8.useCallback(
    (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );
  React8.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);
  React8.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);
  return /* @__PURE__ */ jsx17(
    CarouselContext.Provider,
    {
      value: {
        carouselRef,
        api,
        opts,
        orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext
      },
      children: /* @__PURE__ */ jsx17(
        "div",
        {
          onKeyDownCapture: handleKeyDown,
          className: cn("relative", className),
          role: "region",
          "aria-roledescription": "carousel",
          "data-slot": "carousel",
          ...props,
          children
        }
      )
    }
  );
}
function CarouselContent({ className, ...props }) {
  const { carouselRef, orientation } = useCarousel();
  return /* @__PURE__ */ jsx17(
    "div",
    {
      ref: carouselRef,
      className: "overflow-hidden",
      "data-slot": "carousel-content",
      children: /* @__PURE__ */ jsx17(
        "div",
        {
          className: cn(
            "flex",
            orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
            className
          ),
          ...props
        }
      )
    }
  );
}
function CarouselItem({ className, ...props }) {
  const { orientation } = useCarousel();
  return /* @__PURE__ */ jsx17(
    "div",
    {
      role: "group",
      "aria-roledescription": "slide",
      "data-slot": "carousel-item",
      className: cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className
      ),
      ...props
    }
  );
}
function CarouselPrevious({
  className,
  variant = "muted",
  size = "icon",
  ...props
}) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  return /* @__PURE__ */ jsxs4(
    Button,
    {
      "data-slot": "carousel-previous",
      variant,
      size,
      className: cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal" ? "top-1/2 -left-12 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      ),
      disabled: !canScrollPrev,
      onClick: scrollPrev,
      ...props,
      children: [
        /* @__PURE__ */ jsx17(ArrowLeft, {}),
        /* @__PURE__ */ jsx17("span", { className: "sr-only", children: "Previous slide" })
      ]
    }
  );
}
function CarouselNext({
  className,
  variant = "muted",
  size = "icon",
  ...props
}) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  return /* @__PURE__ */ jsxs4(
    Button,
    {
      "data-slot": "carousel-next",
      variant,
      size,
      className: cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal" ? "top-1/2 -right-12 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      ),
      disabled: !canScrollNext,
      onClick: scrollNext,
      ...props,
      children: [
        /* @__PURE__ */ jsx17(ArrowRight, {}),
        /* @__PURE__ */ jsx17("span", { className: "sr-only", children: "Next slide" })
      ]
    }
  );
}

// src/components/ui/chart.tsx
import * as React9 from "react";
import * as RechartsPrimitive from "recharts";
import { Fragment, jsx as jsx18, jsxs as jsxs5 } from "react/jsx-runtime";
var THEMES = { light: "", dark: ".dark" };
var ChartContext = React9.createContext(null);
function useChart() {
  const context = React9.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}
function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}) {
  const uniqueId = React9.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;
  return /* @__PURE__ */ jsx18(ChartContext.Provider, { value: { config }, children: /* @__PURE__ */ jsxs5(
    "div",
    {
      "data-slot": "chart",
      "data-chart": chartId,
      className: cn(
        "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx18(ChartStyle, { id: chartId, config }),
        /* @__PURE__ */ jsx18(RechartsPrimitive.ResponsiveContainer, { children })
      ]
    }
  ) });
}
var ChartStyle = ({ id, config }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config2]) => config2.theme || config2.color
  );
  if (!colorConfig.length) {
    return null;
  }
  return /* @__PURE__ */ jsx18(
    "style",
    {
      dangerouslySetInnerHTML: {
        __html: Object.entries(THEMES).map(
          ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig.map(([key, itemConfig]) => {
            const color = itemConfig.theme?.[theme] || itemConfig.color;
            return color ? `  --color-${key}: ${color};` : null;
          }).join("\n")}
}
`
        ).join("\n")
      }
    }
  );
};
var ChartTooltip = RechartsPrimitive.Tooltip;
function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey
}) {
  const { config } = useChart();
  const tooltipLabel = React9.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null;
    }
    const [item] = payload;
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value = !labelKey && typeof label === "string" ? config[label]?.label || label : itemConfig?.label;
    if (labelFormatter) {
      return /* @__PURE__ */ jsx18("div", { className: cn("font-normal", labelClassName), children: labelFormatter(value, payload) });
    }
    if (!value) {
      return null;
    }
    return /* @__PURE__ */ jsx18("div", { className: cn("font-normal", labelClassName), children: value });
  }, [
    label,
    labelFormatter,
    payload,
    hideLabel,
    labelClassName,
    config,
    labelKey
  ]);
  if (!active || !payload?.length) {
    return null;
  }
  const nestLabel = payload.length === 1 && indicator !== "dot";
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      className: cn(
        "grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl",
        className
      ),
      children: [
        !nestLabel ? tooltipLabel : null,
        /* @__PURE__ */ jsx18("div", { className: "grid gap-1.5", children: payload.filter((item) => item.type !== "none").map((item, index) => {
          const key = `${nameKey || item.name || item.dataKey || "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const indicatorColor = color || item.payload.fill || item.color;
          return /* @__PURE__ */ jsx18(
            "div",
            {
              className: cn(
                "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground",
                indicator === "dot" && "items-center"
              ),
              children: formatter && item?.value !== void 0 && item.name ? formatter(item.value, item.name, item, index, item.payload) : /* @__PURE__ */ jsxs5(Fragment, { children: [
                itemConfig?.icon ? /* @__PURE__ */ jsx18(itemConfig.icon, {}) : !hideIndicator && /* @__PURE__ */ jsx18(
                  "div",
                  {
                    className: cn(
                      "shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)",
                      {
                        "h-2.5 w-2.5": indicator === "dot",
                        "w-1": indicator === "line",
                        "w-0 border-[1.5px] border-dashed bg-transparent": indicator === "dashed",
                        "my-0.5": nestLabel && indicator === "dashed"
                      }
                    ),
                    style: {
                      "--color-bg": indicatorColor,
                      "--color-border": indicatorColor
                    }
                  }
                ),
                /* @__PURE__ */ jsxs5(
                  "div",
                  {
                    className: cn(
                      "flex flex-1 justify-between leading-none",
                      nestLabel ? "items-end" : "items-center"
                    ),
                    children: [
                      /* @__PURE__ */ jsxs5("div", { className: "grid gap-1.5", children: [
                        nestLabel ? tooltipLabel : null,
                        /* @__PURE__ */ jsx18("span", { className: "text-muted-foreground", children: itemConfig?.label || item.name })
                      ] }),
                      item.value && /* @__PURE__ */ jsx18("span", { className: "font-mono font-normal text-foreground tabular-nums", children: item.value.toLocaleString() })
                    ]
                  }
                )
              ] })
            },
            item.dataKey
          );
        }) })
      ]
    }
  );
}
var ChartLegend = RechartsPrimitive.Legend;
function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  nameKey
}) {
  const { config } = useChart();
  if (!payload?.length) {
    return null;
  }
  return /* @__PURE__ */ jsx18(
    "div",
    {
      className: cn(
        "flex items-center justify-center gap-4",
        verticalAlign === "top" ? "pb-3" : "pt-3",
        className
      ),
      children: payload.filter((item) => item.type !== "none").map((item) => {
        const key = `${nameKey || item.dataKey || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        return /* @__PURE__ */ jsxs5(
          "div",
          {
            className: cn(
              "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
            ),
            children: [
              itemConfig?.icon && !hideIcon ? /* @__PURE__ */ jsx18(itemConfig.icon, {}) : /* @__PURE__ */ jsx18(
                "div",
                {
                  className: "h-2 w-2 shrink-0 rounded-[2px]",
                  style: {
                    backgroundColor: item.color
                  }
                }
              ),
              itemConfig?.label
            ]
          },
          item.value
        );
      })
    }
  );
}
function getPayloadConfigFromPayload(config, payload, key) {
  if (typeof payload !== "object" || payload === null) {
    return void 0;
  }
  const payloadPayload = "payload" in payload && typeof payload.payload === "object" && payload.payload !== null ? payload.payload : void 0;
  let configLabelKey = key;
  if (key in payload && typeof payload[key] === "string") {
    configLabelKey = payload[key];
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key] === "string") {
    configLabelKey = payloadPayload[key];
  }
  return configLabelKey in config ? config[configLabelKey] : config[key];
}

// src/components/animate-ui/primitives/radix/checkbox.tsx
import { Checkbox as CheckboxPrimitive } from "radix-ui";
import { motion as motion3 } from "motion/react";
import { jsx as jsx19 } from "react/jsx-runtime";
var [CheckboxProvider, useCheckbox] = getStrictContext("CheckboxContext");
function Checkbox({
  defaultChecked,
  checked,
  onCheckedChange,
  disabled,
  required,
  name,
  value,
  ...props
}) {
  const [isChecked, setIsChecked] = useControlledState({
    value: checked,
    defaultValue: defaultChecked,
    onChange: onCheckedChange
  });
  return /* @__PURE__ */ jsx19(CheckboxProvider, { value: { isChecked, setIsChecked }, children: /* @__PURE__ */ jsx19(
    CheckboxPrimitive.Root,
    {
      defaultChecked,
      checked,
      onCheckedChange: setIsChecked,
      disabled,
      required,
      name,
      value,
      asChild: true,
      children: /* @__PURE__ */ jsx19(
        motion3.button,
        {
          "data-slot": "checkbox",
          whileTap: { scale: 0.95 },
          whileHover: { scale: 1.05 },
          ...props
        }
      )
    }
  ) });
}
function CheckboxIndicator(props) {
  const { isChecked } = useCheckbox();
  return /* @__PURE__ */ jsx19(CheckboxPrimitive.Indicator, { forceMount: true, asChild: true, children: /* @__PURE__ */ jsx19(
    motion3.svg,
    {
      "data-slot": "checkbox-indicator",
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "3.5",
      stroke: "currentColor",
      initial: "unchecked",
      animate: isChecked ? "checked" : "unchecked",
      ...props,
      children: isChecked === "indeterminate" ? /* @__PURE__ */ jsx19(
        motion3.line,
        {
          x1: "5",
          y1: "12",
          x2: "19",
          y2: "12",
          strokeLinecap: "round",
          initial: { pathLength: 0, opacity: 0 },
          animate: {
            pathLength: 1,
            opacity: 1,
            transition: { duration: 0.2 }
          }
        }
      ) : /* @__PURE__ */ jsx19(
        motion3.path,
        {
          strokeLinecap: "round",
          strokeLinejoin: "round",
          d: "M4.5 12.75l6 6 9-13.5",
          variants: {
            checked: {
              pathLength: 1,
              opacity: 1,
              transition: {
                duration: 0.2
              }
            },
            unchecked: {
              pathLength: 0,
              opacity: 0,
              transition: {
                duration: 0.2
              }
            }
          }
        }
      )
    }
  ) });
}

// src/components/ui/checkbox.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
function Checkbox2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx20(
    Checkbox,
    {
      "data-slot": "checkbox",
      className: cn(
        "peer grid size-4 shrink-0 cursor-pointer place-content-center rounded-[4px] border-none bg-white shadow-checkbox inset-shadow-checkbox transition-[background-color,box-shadow] duration-150 ease-out outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:bg-primary data-[state=checked]:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.1),rgb(255_255_255/0))] data-[state=checked]:bg-blend-screen data-[state=checked]:shadow-checkbox-checked data-[state=checked]:inset-shadow-checkbox-checked data-[state=checked]:text-primary-foreground dark:bg-input/30 dark:shadow-none dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.25)] dark:aria-invalid:ring-destructive/40 dark:data-[state=checked]:bg-blue-600 dark:data-[state=checked]:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.12),rgb(255_255_255/0))] dark:data-[state=checked]:text-white dark:data-[state=checked]:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:data-[state=checked]:shadow-none dark:hover:data-[state=checked]:bg-primary-hover dark:active:data-[state=checked]:bg-primary-active",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx20(
        CheckboxIndicator,
        {
          "data-slot": "checkbox-indicator",
          className: "size-3.5 text-current"
        }
      )
    }
  );
}

// src/components/animate-ui/primitives/radix/collapsible.tsx
import { Collapsible as CollapsiblePrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence3, motion as motion4 } from "motion/react";
import { jsx as jsx21 } from "react/jsx-runtime";
var [CollapsibleProvider, useCollapsible] = getStrictContext("CollapsibleContext");
function Collapsible(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx21(CollapsibleProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx21(
    CollapsiblePrimitive.Root,
    {
      "data-slot": "collapsible",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function CollapsibleTrigger(props) {
  return /* @__PURE__ */ jsx21(CollapsiblePrimitive.Trigger, { "data-slot": "collapsible-trigger", ...props });
}
function CollapsibleContent({
  keepRendered = false,
  transition = { duration: 0.35, ease: "easeInOut" },
  ...props
}) {
  const { isOpen } = useCollapsible();
  return /* @__PURE__ */ jsx21(AnimatePresence3, { children: keepRendered ? /* @__PURE__ */ jsx21(CollapsiblePrimitive.Content, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx21(
    motion4.div,
    {
      "data-slot": "collapsible-content",
      layout: true,
      initial: { opacity: 0, height: 0, overflow: "hidden", y: 6 },
      animate: isOpen ? { opacity: 1, height: "auto", overflow: "hidden", y: 0 } : { opacity: 0, height: 0, overflow: "hidden", y: 6 },
      transition,
      ...props
    },
    "collapsible-content"
  ) }) : isOpen && /* @__PURE__ */ jsx21(CollapsiblePrimitive.Content, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx21(
    motion4.div,
    {
      "data-slot": "collapsible-content",
      layout: true,
      initial: { opacity: 0, height: 0, overflow: "hidden", y: 6 },
      animate: { opacity: 1, height: "auto", overflow: "hidden", y: 0 },
      exit: { opacity: 0, height: 0, overflow: "hidden", y: 6 },
      transition,
      ...props
    },
    "collapsible-content"
  ) }) });
}

// src/components/ui/collapsible.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
function Collapsible2({
  ...props
}) {
  return /* @__PURE__ */ jsx22(Collapsible, { "data-slot": "collapsible", ...props });
}
function CollapsibleTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    CollapsibleTrigger,
    {
      "data-slot": "collapsible-trigger",
      ...props
    }
  );
}
function CollapsibleContent2({
  transition = {
    type: "spring",
    stiffness: 500,
    damping: 35,
    mass: 0.6,
    opacity: { duration: 0.12, ease: "easeOut" }
  },
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    CollapsibleContent,
    {
      "data-slot": "collapsible-content",
      transition,
      ...props
    }
  );
}

// src/components/ui/combobox.tsx
import * as React13 from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { ChevronDownIcon as ChevronDownIcon3, XIcon } from "lucide-react";

// src/components/ui/input-group.tsx
import * as React12 from "react";
import { cva as cva6 } from "class-variance-authority";

// src/components/ui/input.tsx
import * as React10 from "react";
import { cva as cva5 } from "class-variance-authority";
import { Check, Copy } from "lucide-react";
import { AnimatePresence as AnimatePresence4, motion as motion5 } from "motion/react";
import { jsx as jsx23, jsxs as jsxs6 } from "react/jsx-runtime";
var inputVariants = cva5(
  "w-full min-w-0 rounded-lg text-sm shadow-[0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,color] duration-150 outline-none selection:bg-blue-100 selection:text-inherit file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-white file:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.7),rgb(255_255_255/0))] file:bg-blend-screen file:px-3 file:font-medium file:text-secondary-foreground file:shadow-btn-secondary file:inset-shadow-btn-secondary file:transition-colors file:duration-150 hover:file:bg-secondary-hover active:file:bg-secondary-active dark:file:bg-gray-800 dark:file:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.06),rgb(255_255_255/0))] dark:file:text-gray-100 dark:file:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:file:bg-secondary-hover dark:active:file:bg-secondary-active placeholder:text-gray-400 focus-visible:[--input-border-color:var(--color-blue-500)] focus-visible:[--input-ring-color:oklch(0.541_0.167_262.9/0.1)] focus-visible:[--input-ring-size:3px] dark:focus-visible:[--input-border-color:var(--color-blue-500)] dark:focus-visible:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:[--input-border-color:var(--color-destructive)] aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] aria-invalid:[--input-ring-size:3px] dark:selection:bg-blue-800/50 dark:aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]",
  {
    variants: {
      inputSize: {
        default: "h-9 px-3 py-0 leading-[16px] file:h-8 file:text-sm",
        sm: "h-8 px-[8px] py-[3px] leading-[14px] text-xs file:h-7 file:text-xs"
      },
      variant: {
        default: "bg-gray-25 inset-shadow-input hover:[--input-border-color:var(--color-gray-300)] dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]",
        copy: "bg-white inset-shadow-input-copy hover:[--input-border-color:var(--color-gray-300)] dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]"
      }
    },
    defaultVariants: {
      inputSize: "default",
      variant: "default"
    }
  }
);
function Input({
  className,
  type,
  inputSize = "default",
  variant = "default",
  htmlSize,
  onFocus,
  showCopyButton = false,
  ...props
}) {
  const inputRef = React10.useRef(null);
  const handleFocus = React10.useCallback(
    (e) => {
      if (variant === "copy") {
        e.currentTarget.select();
      }
      onFocus?.(e);
    },
    [variant, onFocus]
  );
  const inputElement = /* @__PURE__ */ jsx23(
    "input",
    {
      ref: inputRef,
      type,
      "data-slot": "input",
      "data-size": inputSize,
      "data-variant": variant,
      size: htmlSize,
      className: cn(
        inputVariants({ inputSize, variant }),
        type === "file" && "h-auto bg-transparent py-1 text-gray-500 inset-shadow-none shadow-none hover:[--input-border-color:initial] dark:bg-transparent dark:inset-shadow-none",
        showCopyButton && (inputSize === "sm" ? "pr-8" : "pr-9"),
        showCopyButton && "group-hover/input-copy:[--input-border-color:var(--color-gray-300)] dark:group-hover/input-copy:[--input-border-color:rgb(255_255_255/0.2)]",
        !showCopyButton && className
      ),
      onFocus: handleFocus,
      ...props
    }
  );
  if (!showCopyButton) {
    return inputElement;
  }
  return /* @__PURE__ */ jsx23(InputCopyWrapper, { inputRef, inputSize, className, children: inputElement });
}
function InputCopyWrapper({
  inputRef,
  inputSize,
  className,
  children
}) {
  const [copied, setCopied] = React10.useState(false);
  const [ghostText, setGhostText] = React10.useState("");
  const [ghostKey, setGhostKey] = React10.useState(0);
  const timeoutRef = React10.useRef(null);
  React10.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  const handleCopy = async () => {
    const value = inputRef.current?.value ?? "";
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setGhostText(value);
      setGhostKey((k) => k + 1);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      "data-slot": "input-copy-wrapper",
      className: cn(
        "group/input-copy relative inline-flex w-full items-center",
        className
      ),
      children: [
        children,
        ghostText && /* @__PURE__ */ jsx23(
          "div",
          {
            "aria-hidden": true,
            className: cn(
              "pointer-events-none absolute inset-y-0 flex items-center overflow-visible",
              inputSize === "sm" ? "left-2 text-xs" : "left-3 text-sm"
            ),
            children: /* @__PURE__ */ jsx23("span", { className: "whitespace-pre", children: Array.from(ghostText).map((char, i) => /* @__PURE__ */ jsx23(
              motion5.span,
              {
                initial: { y: 0, opacity: 0, filter: "blur(0px)", rotate: 0 },
                animate: {
                  y: -28,
                  opacity: [0, 0.85, 0],
                  filter: "blur(2px)",
                  rotate: -3
                },
                transition: {
                  duration: 0.4,
                  delay: i * 0.012,
                  ease: "easeOut",
                  opacity: {
                    duration: 0.4,
                    delay: i * 0.012,
                    times: [0, 0.4, 1],
                    ease: "linear"
                  }
                },
                style: { display: "inline-block", whiteSpace: "pre" },
                children: char
              },
              i
            )) })
          },
          ghostKey
        ),
        /* @__PURE__ */ jsxs6(
          "div",
          {
            className: cn(
              "absolute top-1/2 -translate-y-1/2",
              inputSize === "sm" ? "right-1" : "right-1.5"
            ),
            children: [
              /* @__PURE__ */ jsx23(
                Button,
                {
                  type: "button",
                  variant: "muted",
                  size: "icon-xs",
                  "aria-label": "Copy",
                  onMouseDown: (e) => e.preventDefault(),
                  onClick: handleCopy,
                  className: cn(
                    "overflow-hidden rounded-[4px] shadow-none hover:shadow-none",
                    copied ? "bg-seafoam-50 hover:bg-seafoam-50" : "hover:bg-gray-50"
                  ),
                  children: /* @__PURE__ */ jsx23(AnimatePresence4, { mode: "wait", initial: false, children: copied ? /* @__PURE__ */ jsx23(
                    motion5.span,
                    {
                      initial: { scale: 0.5, opacity: 0 },
                      animate: { scale: 1, opacity: 1 },
                      exit: { scale: 0.5, opacity: 0 },
                      transition: { duration: 0.08, ease: "easeOut" },
                      className: "flex items-center justify-center",
                      children: /* @__PURE__ */ jsx23(Check, { className: "size-3.5 text-seafoam-700" })
                    },
                    "check"
                  ) : /* @__PURE__ */ jsx23(
                    motion5.span,
                    {
                      initial: { scale: 0.5, opacity: 0 },
                      animate: { scale: 1, opacity: 1 },
                      exit: { scale: 0.5, opacity: 0 },
                      transition: { duration: 0.08, ease: "easeOut" },
                      className: "flex items-center justify-center",
                      children: /* @__PURE__ */ jsx23(Copy, { className: "size-3.5" })
                    },
                    "copy"
                  ) })
                }
              ),
              /* @__PURE__ */ jsx23(AnimatePresence4, { children: copied && /* @__PURE__ */ jsx23(
                motion5.div,
                {
                  role: "status",
                  "aria-live": "polite",
                  initial: { opacity: 0, y: 4 },
                  animate: { opacity: 1, y: 0 },
                  exit: { opacity: 0, y: 4 },
                  transition: { duration: 0.15, ease: "easeOut" },
                  className: "pointer-events-none absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap rounded-lg bg-popover px-2 py-1 text-xs font-medium text-seafoam-700 shadow-md ring-1 ring-black/5 dark:ring-white/15",
                  children: "Copied"
                },
                "copied-label"
              ) })
            ]
          }
        )
      ]
    }
  );
}

// src/components/ui/textarea.tsx
import * as React11 from "react";
import { jsx as jsx24 } from "react/jsx-runtime";
function Textarea({
  className,
  variant = "default",
  onFocus,
  ...props
}) {
  const handleFocus = React11.useCallback(
    (e) => {
      if (variant === "copy") {
        e.currentTarget.select();
      }
      onFocus?.(e);
    },
    [variant, onFocus]
  );
  return /* @__PURE__ */ jsx24(
    "textarea",
    {
      "data-slot": "textarea",
      "data-variant": variant,
      className: cn(
        "flex field-sizing-content min-h-16 w-full rounded-lg px-3 py-2 text-sm shadow-[0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,color] duration-150 outline-none selection:bg-blue-100 selection:text-inherit placeholder:text-gray-400 focus-visible:[--input-border-color:var(--color-blue-500)] focus-visible:[--input-ring-color:oklch(0.541_0.167_262.9/0.1)] focus-visible:[--input-ring-size:3px] dark:focus-visible:[--input-border-color:var(--color-blue-500)] dark:focus-visible:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:[--input-border-color:var(--color-destructive)] aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] aria-invalid:[--input-ring-size:3px] dark:selection:bg-blue-800/50 dark:aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]",
        variant === "copy" ? "bg-white inset-shadow-input-copy hover:[--input-border-color:var(--color-gray-300)] dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]" : "bg-gray-25 inset-shadow-input hover:[--input-border-color:var(--color-gray-300)] dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]",
        className
      ),
      onFocus: handleFocus,
      ...props
    }
  );
}

// src/components/ui/input-group.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
function InputGroup({
  className,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    "div",
    {
      "data-slot": "input-group",
      "data-variant": variant,
      role: "group",
      className: cn(
        "group/input-group relative flex w-full items-center rounded-lg shadow-[0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,color] duration-150 outline-none dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]",
        variant === "copy" ? "bg-white inset-shadow-input-copy hover:[--input-border-color:var(--color-gray-300)]" : "bg-gray-25 inset-shadow-input hover:[--input-border-color:var(--color-gray-300)]",
        "h-9 min-w-0 has-[>textarea]:h-auto",
        // Variants based on alignment.
        "has-[>[data-align=inline-start]]:[&>input]:pl-2",
        "has-[>[data-align=inline-start]_[data-slot=input-group-text]]:[&>input]:pl-1",
        "has-[>[data-align=inline-end]]:[&>input]:pr-2",
        "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3",
        "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3",
        // Focus state.
        "has-[[data-slot=input-group-control]:focus-visible]:[--input-border-color:var(--color-blue-500)] has-[[data-slot=input-group-control]:focus-visible]:[--input-ring-color:oklch(0.541_0.167_262.9/0.1)] has-[[data-slot=input-group-control]:focus-visible]:[--input-ring-size:3px] dark:has-[[data-slot=input-group-control]:focus-visible]:[--input-border-color:var(--color-blue-500)] dark:has-[[data-slot=input-group-control]:focus-visible]:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)]",
        // Error state.
        "has-[[data-slot][aria-invalid=true]]:[--input-border-color:var(--color-destructive)] has-[[data-slot][aria-invalid=true]]:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] has-[[data-slot][aria-invalid=true]]:[--input-ring-size:3px] dark:has-[[data-slot][aria-invalid=true]]:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]",
        className
      ),
      ...props
    }
  );
}
var inputGroupAddonVariants = cva6(
  "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-normal text-gray-400 select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]",
        "inline-end": "order-last pr-3 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]",
        "block-start": "order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3",
        "block-end": "order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3"
      }
    },
    defaultVariants: {
      align: "inline-start"
    }
  }
);
function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    "div",
    {
      role: "group",
      "data-slot": "input-group-addon",
      "data-align": align,
      className: cn(inputGroupAddonVariants({ align }), className),
      onClick: (e) => {
        if (e.target.closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      },
      ...props
    }
  );
}
var inputGroupButtonVariants = cva6(
  "flex items-center gap-2 text-sm shadow-none",
  {
    variants: {
      size: {
        xs: "h-6 gap-1 rounded-[calc(var(--radius-lg)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5",
        "icon-xs": "size-6 rounded-[calc(var(--radius-lg)-5px)] p-0 has-[>svg]:p-0",
        "icon-sm": "size-8 p-0 has-[>svg]:p-0"
      }
    },
    defaultVariants: {
      size: "xs"
    }
  }
);
function InputGroupButton({
  className,
  type = "button",
  variant = "muted",
  size = "xs",
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    Button,
    {
      type,
      "data-size": size,
      variant,
      className: cn(inputGroupButtonVariants({ size }), className),
      ...props
    }
  );
}
function InputGroupText({ className, ...props }) {
  return /* @__PURE__ */ jsx25(
    "span",
    {
      "data-slot": "input-group-text",
      className: cn(
        "flex items-center gap-2 text-sm text-gray-400 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function InputGroupInput({
  className,
  onFocus,
  ...props
}) {
  const handleFocus = React12.useCallback(
    (e) => {
      const group = e.currentTarget.closest("[data-slot=input-group]");
      if (group?.getAttribute("data-variant") === "copy") {
        e.currentTarget.select();
      }
      onFocus?.(e);
    },
    [onFocus]
  );
  return /* @__PURE__ */ jsx25(
    Input,
    {
      "data-slot": "input-group-control",
      className: cn(
        "flex-1 rounded-none border-0 bg-transparent shadow-none inset-shadow-none hover:inset-shadow-none focus-visible:ring-0 focus-visible:shadow-none dark:bg-transparent",
        className
      ),
      onFocus: handleFocus,
      ...props
    }
  );
}
function InputGroupTextarea({
  className,
  onFocus,
  ...props
}) {
  const handleFocus = React12.useCallback(
    (e) => {
      const group = e.currentTarget.closest("[data-slot=input-group]");
      if (group?.getAttribute("data-variant") === "copy") {
        e.currentTarget.select();
      }
      onFocus?.(e);
    },
    [onFocus]
  );
  return /* @__PURE__ */ jsx25(
    Textarea,
    {
      "data-slot": "input-group-control",
      className: cn(
        "flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none inset-shadow-none hover:inset-shadow-none focus-visible:ring-0 focus-visible:shadow-none dark:bg-transparent",
        className
      ),
      onFocus: handleFocus,
      ...props
    }
  );
}

// src/components/ui/combobox.tsx
import { jsx as jsx26, jsxs as jsxs7 } from "react/jsx-runtime";
var ComboboxValueContext = React13.createContext(void 0);
function Combobox(props) {
  const {
    value: controlledValue,
    defaultValue,
    onValueChange,
    ...rest
  } = props;
  const [internalValue, setInternalValue] = React13.useState(controlledValue ?? defaultValue);
  React13.useEffect(() => {
    if (controlledValue !== void 0) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);
  const handleValueChange = React13.useCallback(
    (newValue, eventDetails) => {
      setInternalValue(newValue);
      onValueChange?.(newValue, eventDetails);
    },
    [onValueChange]
  );
  return /* @__PURE__ */ jsx26(ComboboxValueContext.Provider, { value: internalValue, children: /* @__PURE__ */ jsx26(
    ComboboxPrimitive.Root,
    {
      value: controlledValue,
      defaultValue,
      onValueChange: handleValueChange,
      ...rest
    }
  ) });
}
function ComboboxValue({ ...props }) {
  return /* @__PURE__ */ jsx26(ComboboxPrimitive.Value, { "data-slot": "combobox-value", ...props });
}
function ComboboxTrigger({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs7(
    ComboboxPrimitive.Trigger,
    {
      "data-slot": "combobox-trigger",
      className: cn("[&_svg:not([class*='size-'])]:size-4", className),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx26(
          ChevronDownIcon3,
          {
            "data-slot": "combobox-trigger-icon",
            className: "pointer-events-none size-4 text-muted-foreground"
          }
        )
      ]
    }
  );
}
function ComboboxClear({ className, ...props }) {
  return /* @__PURE__ */ jsx26(
    ComboboxPrimitive.Clear,
    {
      "data-slot": "combobox-clear",
      render: /* @__PURE__ */ jsx26(InputGroupButton, { variant: "muted", size: "icon-xs" }),
      className: cn(className),
      ...props,
      children: /* @__PURE__ */ jsx26(XIcon, { className: "pointer-events-none" })
    }
  );
}
function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  ...props
}) {
  return /* @__PURE__ */ jsxs7(InputGroup, { className: cn("w-auto", className), children: [
    /* @__PURE__ */ jsx26(
      ComboboxPrimitive.Input,
      {
        render: /* @__PURE__ */ jsx26(InputGroupInput, { disabled }),
        ...props
      }
    ),
    /* @__PURE__ */ jsxs7(InputGroupAddon, { align: "inline-end", children: [
      showTrigger && /* @__PURE__ */ jsx26(
        InputGroupButton,
        {
          size: "icon-xs",
          variant: "muted",
          asChild: true,
          "data-slot": "input-group-button",
          className: "group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent",
          disabled,
          children: /* @__PURE__ */ jsx26(ComboboxTrigger, {})
        }
      ),
      showClear && /* @__PURE__ */ jsx26(ComboboxClear, { disabled })
    ] }),
    children
  ] });
}
function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  ...props
}) {
  return /* @__PURE__ */ jsx26(ComboboxPrimitive.Portal, { children: /* @__PURE__ */ jsx26(
    ComboboxPrimitive.Positioner,
    {
      side,
      sideOffset,
      align,
      alignOffset,
      anchor,
      className: "isolate z-50",
      children: /* @__PURE__ */ jsx26(
        ComboboxPrimitive.Popup,
        {
          "data-slot": "combobox-content",
          "data-chips": !!anchor,
          className: cn(
            "group/combobox-content relative max-h-96 w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-md ring-1 ring-black/5 duration-100 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[chips=true]:min-w-(--anchor-width) data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:mb-1 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 *:data-[slot=input-group]:shadow-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className
          ),
          ...props
        }
      )
    }
  ) });
}
function ComboboxList({ className, ...props }) {
  return /* @__PURE__ */ jsx26(
    ComboboxPrimitive.List,
    {
      "data-slot": "combobox-list",
      className: cn(
        "max-h-[min(calc(--spacing(96)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto data-empty:p-0",
        className
      ),
      ...props
    }
  );
}
function ComboboxItem({
  className,
  children,
  value,
  ...props
}) {
  const currentValue = React13.useContext(ComboboxValueContext);
  const isChecked = Array.isArray(currentValue) ? currentValue.includes(value) : currentValue === value;
  return /* @__PURE__ */ jsxs7(
    ComboboxPrimitive.Item,
    {
      "data-slot": "combobox-item",
      value,
      className: cn(
        "relative flex w-full cursor-default items-center gap-2 rounded-md py-2 pr-8 pl-3 text-sm font-medium outline-hidden transition-colors duration-150 select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx26(
          "span",
          {
            "data-slot": "combobox-item-indicator",
            className: "pointer-events-none absolute right-2 flex size-4 items-center justify-center",
            children: /* @__PURE__ */ jsx26(AnimatedCheckIcon, { checked: isChecked })
          }
        )
      ]
    }
  );
}
function ComboboxGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx26(
    ComboboxPrimitive.Group,
    {
      "data-slot": "combobox-group",
      className: cn(className),
      ...props
    }
  );
}
function ComboboxLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx26(
    ComboboxPrimitive.GroupLabel,
    {
      "data-slot": "combobox-label",
      className: cn(
        "px-2 py-1.5 text-xs font-medium text-gray-400 pointer-coarse:px-3 pointer-coarse:py-2 pointer-coarse:text-sm",
        className
      ),
      ...props
    }
  );
}
function ComboboxCollection({ ...props }) {
  return /* @__PURE__ */ jsx26(ComboboxPrimitive.Collection, { "data-slot": "combobox-collection", ...props });
}
function ComboboxEmpty({ className, ...props }) {
  return /* @__PURE__ */ jsx26(
    ComboboxPrimitive.Empty,
    {
      "data-slot": "combobox-empty",
      className: cn(
        "hidden w-full justify-center py-2 text-center text-sm text-muted-foreground group-has-[[data-slot=combobox-list][data-empty]]/combobox-content:flex",
        className
      ),
      ...props
    }
  );
}
function ComboboxSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx26(
    ComboboxPrimitive.Separator,
    {
      "data-slot": "combobox-separator",
      className: cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function ComboboxChips({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx26(
    ComboboxPrimitive.Chips,
    {
      "data-slot": "combobox-chips",
      className: cn(
        "flex min-h-9 flex-wrap items-center gap-1.5 rounded-lg bg-gray-25 bg-clip-padding px-2.5 py-1.5 text-sm inset-shadow-input shadow-[0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,color] duration-150 hover:[--input-border-color:var(--color-gray-300)] focus-within:[--input-border-color:var(--color-blue-500)] focus-within:[--input-ring-color:oklch(0.541_0.167_262.9/0.1)] focus-within:[--input-ring-size:3px] has-aria-invalid:[--input-border-color:var(--color-destructive)] has-aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] has-aria-invalid:[--input-ring-size:3px] has-data-[slot=combobox-chip]:px-1.5 dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)] dark:focus-within:[--input-border-color:var(--color-blue-500)] dark:focus-within:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)] dark:has-aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]",
        className
      ),
      ...props
    }
  );
}
function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs7(
    ComboboxPrimitive.Chip,
    {
      "data-slot": "combobox-chip",
      className: cn(
        "flex h-[calc(--spacing(5.5))] w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-normal whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0",
        className
      ),
      ...props,
      children: [
        children,
        showRemove && /* @__PURE__ */ jsx26(
          ComboboxPrimitive.ChipRemove,
          {
            render: /* @__PURE__ */ jsx26(Button, { variant: "muted", size: "icon-xs" }),
            className: "-ml-1 opacity-50 hover:opacity-100",
            "data-slot": "combobox-chip-remove",
            children: /* @__PURE__ */ jsx26(XIcon, { className: "pointer-events-none" })
          }
        )
      ]
    }
  );
}
function ComboboxChipsInput({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx26(
    ComboboxPrimitive.Input,
    {
      "data-slot": "combobox-chip-input",
      className: cn("min-w-16 flex-1 outline-none", className),
      ...props
    }
  );
}
function useComboboxAnchor() {
  return React13.useRef(null);
}

// src/components/ui/command.tsx
import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "lucide-react";

// src/components/ui/dialog.tsx
import { XIcon as XIcon2 } from "lucide-react";

// src/components/animate-ui/primitives/radix/dialog.tsx
import { Dialog as DialogPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence5, motion as motion6 } from "motion/react";
import { jsx as jsx27 } from "react/jsx-runtime";
var [DialogProvider, useDialog] = getStrictContext("DialogContext");
function Dialog(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx27(DialogProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx27(
    DialogPrimitive.Root,
    {
      "data-slot": "dialog",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function DialogTrigger(props) {
  return /* @__PURE__ */ jsx27(DialogPrimitive.Trigger, { "data-slot": "dialog-trigger", ...props });
}
function DialogPortal(props) {
  return /* @__PURE__ */ jsx27(DialogPrimitive.Portal, { "data-slot": "dialog-portal", forceMount: true, ...props });
}
function DialogOverlay({
  transition = { duration: 0.15 },
  ...props
}) {
  const { isOpen } = useDialog();
  return /* @__PURE__ */ jsx27(AnimatePresence5, { children: isOpen && /* @__PURE__ */ jsx27(DialogPrimitive.Overlay, { "data-slot": "dialog-overlay", asChild: true, forceMount: true, children: /* @__PURE__ */ jsx27(
    motion6.div,
    {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition,
      ...props
    },
    "dialog-overlay"
  ) }) });
}
function DialogContent({
  from: _from,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  onInteractOutside,
  transition = { duration: 0.1 },
  style,
  ...props
}) {
  const { isOpen } = useDialog();
  return /* @__PURE__ */ jsx27(AnimatePresence5, { children: isOpen && /* @__PURE__ */ jsx27(
    DialogPrimitive.Content,
    {
      asChild: true,
      forceMount: true,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onInteractOutside,
      children: /* @__PURE__ */ jsx27(
        motion6.div,
        {
          "data-slot": "dialog-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: { willChange: "opacity, transform", ...style },
          ...props
        },
        "dialog-content"
      )
    }
  ) });
}
function DialogClose(props) {
  return /* @__PURE__ */ jsx27(DialogPrimitive.Close, { "data-slot": "dialog-close", ...props });
}
function DialogTitle(props) {
  return /* @__PURE__ */ jsx27(DialogPrimitive.Title, { "data-slot": "dialog-title", ...props });
}
function DialogDescription(props) {
  return /* @__PURE__ */ jsx27(DialogPrimitive.Description, { "data-slot": "dialog-description", ...props });
}

// src/components/ui/dialog.tsx
import { jsx as jsx28, jsxs as jsxs8 } from "react/jsx-runtime";
function Dialog2({
  ...props
}) {
  return /* @__PURE__ */ jsx28(Dialog, { ...props });
}
function DialogTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx28(DialogTrigger, { "data-slot": "dialog-trigger", ...props });
}
function DialogPortal2({
  ...props
}) {
  return /* @__PURE__ */ jsx28(DialogPortal, { "data-slot": "dialog-portal", ...props });
}
function DialogClose2({
  ...props
}) {
  return /* @__PURE__ */ jsx28(DialogClose, { "data-slot": "dialog-close", ...props });
}
function DialogOverlay2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx28(
    DialogOverlay,
    {
      "data-slot": "dialog-overlay",
      className: cn(
        "fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
function DialogContent2({
  className,
  children,
  showCloseButton = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs8(DialogPortal2, { "data-slot": "dialog-portal", children: [
    /* @__PURE__ */ jsx28(DialogOverlay2, {}),
    /* @__PURE__ */ jsxs8(
      DialogContent,
      {
        "data-slot": "dialog-content",
        className: cn(
          "fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg bg-background p-6 shadow-lg ring-1 ring-black/5 duration-200 outline-none dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] sm:max-w-lg",
          className
        ),
        ...props,
        children: [
          children,
          showCloseButton && /* @__PURE__ */ jsxs8(
            DialogClose,
            {
              "data-slot": "dialog-close",
              className: "absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
              children: [
                /* @__PURE__ */ jsx28(XIcon2, {}),
                /* @__PURE__ */ jsx28("span", { className: "sr-only", children: "Close" })
              ]
            }
          )
        ]
      }
    )
  ] });
}
function DialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx28(
    "div",
    {
      "data-slot": "dialog-header",
      className: cn("flex flex-col gap-2 text-center sm:text-left", className),
      ...props
    }
  );
}
function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      "data-slot": "dialog-footer",
      className: cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      ),
      ...props,
      children: [
        children,
        showCloseButton && /* @__PURE__ */ jsx28(DialogClose, { asChild: true, children: /* @__PURE__ */ jsx28(Button, { variant: "muted", children: "Close" }) })
      ]
    }
  );
}
function DialogTitle2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx28(
    DialogTitle,
    {
      "data-slot": "dialog-title",
      className: cn("text-base leading-5 font-medium", className),
      ...props
    }
  );
}
function DialogDescription2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx28(
    DialogDescription,
    {
      "data-slot": "dialog-description",
      className: cn("text-sm leading-5 text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/command.tsx
import { jsx as jsx29, jsxs as jsxs9 } from "react/jsx-runtime";
function Command({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    CommandPrimitive,
    {
      "data-slot": "command",
      className: cn(
        "flex h-full w-full flex-col overflow-hidden rounded-lg bg-popover text-secondary-foreground",
        className
      ),
      ...props
    }
  );
}
function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  className,
  showCloseButton = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs9(Dialog2, { ...props, children: [
    /* @__PURE__ */ jsxs9(DialogHeader, { className: "sr-only", children: [
      /* @__PURE__ */ jsx29(DialogTitle2, { children: title }),
      /* @__PURE__ */ jsx29(DialogDescription2, { children: description })
    ] }),
    /* @__PURE__ */ jsx29(
      DialogContent2,
      {
        className: cn("overflow-hidden p-0", className),
        showCloseButton,
        children: /* @__PURE__ */ jsx29(Command, { className: "**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-gray-400 [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-3 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5", children })
      }
    )
  ] });
}
function CommandInput({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs9(
    "div",
    {
      "data-slot": "command-input-wrapper",
      className: "flex h-9 items-center gap-2 border-b border-gray-100 px-3 dark:border-white/[0.08]",
      children: [
        /* @__PURE__ */ jsx29(SearchIcon, { className: "size-4 shrink-0 opacity-50" }),
        /* @__PURE__ */ jsx29(
          CommandPrimitive.Input,
          {
            "data-slot": "command-input",
            className: cn(
              "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
              className
            ),
            ...props
          }
        )
      ]
    }
  );
}
function CommandList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    CommandPrimitive.List,
    {
      "data-slot": "command-list",
      className: cn(
        "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
        className
      ),
      ...props
    }
  );
}
function CommandEmpty({
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    CommandPrimitive.Empty,
    {
      "data-slot": "command-empty",
      className: "py-6 text-center text-sm",
      ...props
    }
  );
}
function CommandGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    CommandPrimitive.Group,
    {
      "data-slot": "command-group",
      className: cn(
        "overflow-hidden p-1 text-secondary-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-gray-400",
        className
      ),
      ...props
    }
  );
}
function CommandSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    CommandPrimitive.Separator,
    {
      "data-slot": "command-separator",
      className: cn("-mx-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function CommandItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    CommandPrimitive.Item,
    {
      "data-slot": "command-item",
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-hidden transition-colors duration-150 select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props
    }
  );
}
function CommandShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    "span",
    {
      "data-slot": "command-shortcut",
      className: cn("ml-auto text-xs font-normal text-gray-400", className),
      ...props
    }
  );
}

// src/components/ui/context-menu.tsx
import { ChevronRightIcon as ChevronRightIcon2, CircleIcon } from "lucide-react";
import { ContextMenu as ContextMenuPrimitive } from "radix-ui";
import { jsx as jsx30, jsxs as jsxs10 } from "react/jsx-runtime";
function ContextMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx30(ContextMenuPrimitive.Root, { "data-slot": "context-menu", ...props });
}
function ContextMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx30(ContextMenuPrimitive.Trigger, { "data-slot": "context-menu-trigger", ...props });
}
function ContextMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx30(ContextMenuPrimitive.Group, { "data-slot": "context-menu-group", ...props });
}
function ContextMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx30(ContextMenuPrimitive.Portal, { "data-slot": "context-menu-portal", ...props });
}
function ContextMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx30(ContextMenuPrimitive.Sub, { "data-slot": "context-menu-sub", ...props });
}
function ContextMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    ContextMenuPrimitive.RadioGroup,
    {
      "data-slot": "context-menu-radio-group",
      ...props
    }
  );
}
function ContextMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs10(
    ContextMenuPrimitive.SubTrigger,
    {
      "data-slot": "context-menu-sub-trigger",
      "data-inset": inset,
      className: cn(
        "flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[inset]:pl-8 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx30(ChevronRightIcon2, { className: "ml-auto" })
      ]
    }
  );
}
function ContextMenuSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    ContextMenuPrimitive.SubContent,
    {
      "data-slot": "context-menu-sub-content",
      className: cn(
        "z-50 min-w-[8rem] origin-(--radix-context-menu-content-transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  );
}
function ContextMenuContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(ContextMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx30(
    ContextMenuPrimitive.Content,
    {
      "data-slot": "context-menu-content",
      className: cn(
        "z-50 max-h-(--radix-context-menu-content-available-height) min-w-[8rem] origin-(--radix-context-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover p-1 text-secondary-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  ) });
}
function ContextMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    ContextMenuPrimitive.Item,
    {
      "data-slot": "context-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:text-red-400 dark:data-[variant=destructive]:focus:bg-red-500/15 dark:data-[variant=destructive]:focus:text-red-400 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive! dark:data-[variant=destructive]:*:[svg]:text-red-400! data-[variant=destructive]:focus:[&_[data-slot=context-menu-shortcut]]:text-destructive/50 dark:data-[variant=destructive]:focus:[&_[data-slot=context-menu-shortcut]]:text-red-400/50",
        className
      ),
      ...props
    }
  );
}
function ContextMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs10(
    ContextMenuPrimitive.CheckboxItem,
    {
      "data-slot": "context-menu-checkbox-item",
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-md py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx30("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx30(AnimatedCheckIcon, { checked: checked === true }) }),
        children
      ]
    }
  );
}
function ContextMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs10(
    ContextMenuPrimitive.RadioItem,
    {
      "data-slot": "context-menu-radio-item",
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx30("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx30(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx30(CircleIcon, { className: "size-2 fill-current text-primary" }) }) }),
        children
      ]
    }
  );
}
function ContextMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    ContextMenuPrimitive.Label,
    {
      "data-slot": "context-menu-label",
      "data-inset": inset,
      className: cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function ContextMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    ContextMenuPrimitive.Separator,
    {
      "data-slot": "context-menu-separator",
      className: cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function ContextMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    "span",
    {
      "data-slot": "context-menu-shortcut",
      className: cn(
        "ml-auto text-xs font-normal text-gray-400",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/data-table.tsx
import * as React15 from "react";

// src/components/ui/spinner.tsx
import { Loader2Icon } from "lucide-react";
import { jsx as jsx31 } from "react/jsx-runtime";
function Spinner({ className, ...props }) {
  return /* @__PURE__ */ jsx31(
    Loader2Icon,
    {
      role: "status",
      "aria-label": "Loading",
      className: cn("size-4 animate-spin", className),
      ...props
    }
  );
}

// src/hooks/use-horizontal-fade.tsx
import * as React14 from "react";
function useHorizontalFade() {
  const [showLeft, setShowLeft] = React14.useState(false);
  const [showRight, setShowRight] = React14.useState(false);
  const cleanupRef = React14.useRef(null);
  const ref = React14.useCallback((node) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!node) {
      setShowLeft(false);
      setShowRight(false);
      return;
    }
    let rafId = 0;
    let lastLeft = false;
    let lastRight = false;
    const commit = () => {
      rafId = 0;
      const { scrollLeft, scrollWidth, clientWidth } = node;
      const left = scrollLeft > 2;
      const right = scrollLeft + clientWidth < scrollWidth - 2;
      if (left !== lastLeft) {
        lastLeft = left;
        setShowLeft(left);
      }
      if (right !== lastRight) {
        lastRight = right;
        setShowRight(right);
      }
    };
    const schedule = () => {
      if (rafId === 0) rafId = requestAnimationFrame(commit);
    };
    commit();
    node.addEventListener("scroll", schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(node);
    cleanupRef.current = () => {
      node.removeEventListener("scroll", schedule);
      observer.disconnect();
      if (rafId !== 0) cancelAnimationFrame(rafId);
    };
  }, []);
  const style = {
    "--sa-left-fade": showLeft ? 1 : 0,
    "--sa-right-fade": showRight ? 1 : 0
  };
  return { ref, style };
}

// src/components/ui/data-table.tsx
import { jsx as jsx32, jsxs as jsxs11 } from "react/jsx-runtime";
function useAnimatedHeight({
  columnsLength,
  gridTemplateColumns,
  hasEmptyContent,
  isLoading,
  rowKeys
}) {
  const containerRef = React15.useRef(null);
  const contentRef = React15.useRef(null);
  const frameRef = React15.useRef(null);
  const [height, setHeight] = React15.useState(void 0);
  const measureHeight = React15.useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) {
      return;
    }
    const computed = getComputedStyle(container);
    const borderHeight = (Number.parseFloat(computed.borderTopWidth || "0") || 0) + (Number.parseFloat(computed.borderBottomWidth || "0") || 0);
    const contentHeight = Math.ceil(
      content.getBoundingClientRect().height + borderHeight
    );
    const minHeight = Math.ceil(
      Number.parseFloat(computed.minHeight || "0") || 0
    );
    const nextHeight = Math.max(contentHeight, minHeight);
    setHeight((current) => current === nextHeight ? current : nextHeight);
  }, []);
  React15.useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }
    measureHeight();
    const scheduleMeasure = () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = null;
          measureHeight();
        });
      });
    };
    scheduleMeasure();
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [
    columnsLength,
    gridTemplateColumns,
    hasEmptyContent,
    isLoading,
    measureHeight,
    rowKeys
  ]);
  return { containerRef, contentRef, height };
}
function DataTable({
  columns,
  data,
  rowKey,
  onRowClick,
  headerClassName,
  rowClassName,
  isLoading = false,
  loadingContent,
  emptyContent,
  className,
  style,
  ...containerProps
}) {
  const gridTemplateColumns = columns.map((col) => col.width ?? "auto").join(" ");
  const subgridRow = {
    display: "grid",
    gridTemplateColumns: "subgrid",
    gridColumn: "1 / -1"
  };
  const { ref: scrollRef, style: fadeStyle } = useHorizontalFade();
  const hasEmptyContent = !isLoading && data.length === 0 && emptyContent != null;
  const rowKeys = React15.useMemo(
    () => data.map((row, index) => String(rowKey ? rowKey(row, index) : index)).join("|"),
    [data, rowKey]
  );
  const {
    containerRef,
    contentRef,
    height: containerHeight
  } = useAnimatedHeight({
    columnsLength: columns.length,
    gridTemplateColumns,
    hasEmptyContent,
    isLoading,
    rowKeys
  });
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      ref: containerRef,
      className: cn(
        "relative w-full overflow-hidden rounded-lg border text-sm transition-[height] duration-150 ease-in-out",
        (isLoading || hasEmptyContent) && "min-h-[120px]",
        className
      ),
      style: { ...style, height: containerHeight },
      ...containerProps,
      children: [
        /* @__PURE__ */ jsx32(
          "div",
          {
            ref: contentRef,
            className: "relative",
            children: /* @__PURE__ */ jsxs11(
              "div",
              {
                ref: scrollRef,
                "data-slot": "data-table",
                role: "table",
                "data-fade-edges-x": "true",
                className: "grid w-full overflow-x-auto",
                style: { ...fadeStyle, gridTemplateColumns },
                children: [
                  /* @__PURE__ */ jsx32(
                    "div",
                    {
                      "data-slot": "data-table-header",
                      role: "row",
                      className: cn("border-b", headerClassName),
                      style: subgridRow,
                      children: columns.map((col, colIndex) => /* @__PURE__ */ jsx32(
                        "div",
                        {
                          "data-slot": "data-table-head",
                          role: "columnheader",
                          className: cn(
                            "flex items-center h-10 px-2 text-left font-medium whitespace-nowrap text-gray-500",
                            colIndex === 0 && "pl-4",
                            colIndex === columns.length - 1 && "pr-4",
                            col.className
                          ),
                          children: col.header
                        },
                        col.accessor ?? colIndex
                      ))
                    }
                  ),
                  data.map((row, rowIndex) => {
                    const key = rowKey ? rowKey(row, rowIndex) : rowIndex;
                    const resolvedRowClassName = typeof rowClassName === "function" ? rowClassName(row, rowIndex) : rowClassName;
                    return /* @__PURE__ */ jsx32(
                      "div",
                      {
                        "data-slot": "data-table-row",
                        role: "row",
                        className: cn(
                          "transition-colors hover:bg-muted/50",
                          rowIndex < data.length - 1 && "border-b",
                          onRowClick && "cursor-pointer",
                          resolvedRowClassName
                        ),
                        style: subgridRow,
                        onClick: onRowClick ? () => onRowClick(row, rowIndex) : void 0,
                        children: columns.map((col, colIndex) => {
                          const value = row[col.accessor];
                          return /* @__PURE__ */ jsx32(
                            "div",
                            {
                              "data-slot": "data-table-cell",
                              role: "cell",
                              className: cn(
                                "flex items-center p-2 whitespace-nowrap",
                                colIndex === 0 && "pl-4",
                                colIndex === columns.length - 1 && "pr-4",
                                col.className
                              ),
                              children: col.cell ? col.cell({ value, row, rowIndex }) : String(value ?? "")
                            },
                            col.accessor ?? colIndex
                          );
                        })
                      },
                      key
                    );
                  })
                ]
              }
            )
          }
        ),
        isLoading && /* @__PURE__ */ jsx32(
          "div",
          {
            "data-slot": "data-table-loading",
            className: "pointer-events-none absolute inset-x-0 bottom-0 top-[41px] z-10 grid min-h-20 place-items-center bg-background/80",
            children: loadingContent ?? /* @__PURE__ */ jsx32(Spinner, { className: "size-5 text-muted-foreground" })
          }
        ),
        hasEmptyContent && /* @__PURE__ */ jsx32(
          "div",
          {
            "data-slot": "data-table-empty",
            className: "pointer-events-none absolute inset-x-0 top-10 z-10 grid min-h-20 place-items-center",
            children: emptyContent
          }
        )
      ]
    }
  );
}

// src/components/ui/direction.tsx
import { Direction } from "radix-ui";
import { jsx as jsx33 } from "react/jsx-runtime";
function DirectionProvider({
  dir,
  direction,
  children
}) {
  return /* @__PURE__ */ jsx33(Direction.DirectionProvider, { dir: direction ?? dir, children });
}
var useDirection = Direction.useDirection;

// src/components/ui/drawer.tsx
import { Drawer as DrawerPrimitive } from "vaul";
import { jsx as jsx34, jsxs as jsxs12 } from "react/jsx-runtime";
function Drawer({
  ...props
}) {
  return /* @__PURE__ */ jsx34(DrawerPrimitive.Root, { "data-slot": "drawer", ...props });
}
function DrawerTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx34(DrawerPrimitive.Trigger, { "data-slot": "drawer-trigger", ...props });
}
function DrawerPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx34(DrawerPrimitive.Portal, { "data-slot": "drawer-portal", ...props });
}
function DrawerClose({
  ...props
}) {
  return /* @__PURE__ */ jsx34(DrawerPrimitive.Close, { "data-slot": "drawer-close", ...props });
}
function DrawerOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx34(
    DrawerPrimitive.Overlay,
    {
      "data-slot": "drawer-overlay",
      className: cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
}
function DrawerContent({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs12(DrawerPortal, { "data-slot": "drawer-portal", children: [
    /* @__PURE__ */ jsx34(DrawerOverlay, {}),
    /* @__PURE__ */ jsxs12(
      DrawerPrimitive.Content,
      {
        "data-slot": "drawer-content",
        className: cn(
          "group/drawer-content fixed z-50 flex h-auto flex-col bg-background dark:shadow-[0_0_30px_-4px_rgb(0_0_0/0.5)]",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t",
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsx34("div", { className: "mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full bg-muted group-data-[vaul-drawer-direction=bottom]/drawer-content:block" }),
          children
        ]
      }
    )
  ] });
}
function DrawerHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx34(
    "div",
    {
      "data-slot": "drawer-header",
      className: cn(
        "flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left",
        className
      ),
      ...props
    }
  );
}
function DrawerFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx34(
    "div",
    {
      "data-slot": "drawer-footer",
      className: cn("mt-auto flex flex-col gap-2 p-4", className),
      ...props
    }
  );
}
function DrawerTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx34(
    DrawerPrimitive.Title,
    {
      "data-slot": "drawer-title",
      className: cn("font-semibold text-foreground", className),
      ...props
    }
  );
}
function DrawerDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx34(
    DrawerPrimitive.Description,
    {
      "data-slot": "drawer-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/dropdown-menu.tsx
import { ChevronRightIcon as ChevronRightIcon3, CircleIcon as CircleIcon2 } from "lucide-react";

// src/components/animate-ui/primitives/radix/dropdown-menu.tsx
import * as React17 from "react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence6, motion as motion7 } from "motion/react";

// src/hooks/use-data-state.tsx
import * as React16 from "react";
function parseDatasetValue(value) {
  if (value === null) return null;
  if (value === "" || value === "true") return true;
  if (value === "false") return false;
  return value;
}
function useDataState(key, forwardedRef, onChange) {
  const localRef = React16.useRef(null);
  React16.useImperativeHandle(forwardedRef, () => localRef.current);
  const getSnapshot = () => {
    const el = localRef.current;
    return el ? parseDatasetValue(el.getAttribute(`data-${key}`)) : null;
  };
  const subscribe = (callback) => {
    const el = localRef.current;
    if (!el) return () => {
    };
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.attributeName === `data-${key}`) {
          callback();
          break;
        }
      }
    });
    observer.observe(el, {
      attributes: true,
      attributeFilter: [`data-${key}`]
    });
    return () => observer.disconnect();
  };
  const value = React16.useSyncExternalStore(subscribe, getSnapshot);
  React16.useEffect(() => {
    if (onChange) onChange(value);
  }, [value, onChange]);
  return [value, localRef];
}

// src/components/animate-ui/primitives/radix/dropdown-menu.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var [DropdownMenuProvider, useDropdownMenu] = getStrictContext("DropdownMenuContext");
var [DropdownMenuSubProvider, useDropdownMenuSub] = getStrictContext("DropdownMenuSubContext");
function DropdownMenu(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  const [highlightedValue, setHighlightedValue] = React17.useState(
    null
  );
  return /* @__PURE__ */ jsx35(
    DropdownMenuProvider,
    {
      value: { isOpen, setIsOpen, highlightedValue, setHighlightedValue },
      children: /* @__PURE__ */ jsx35(
        DropdownMenuPrimitive.Root,
        {
          "data-slot": "dropdown-menu",
          ...props,
          onOpenChange: setIsOpen
        }
      )
    }
  );
}
function DropdownMenuTrigger(props) {
  return /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.Trigger,
    {
      "data-slot": "dropdown-menu-trigger",
      ...props
    }
  );
}
function DropdownMenuPortal(props) {
  return /* @__PURE__ */ jsx35(DropdownMenuPrimitive.Portal, { "data-slot": "dropdown-menu-portal", ...props });
}
function DropdownMenuGroup(props) {
  return /* @__PURE__ */ jsx35(DropdownMenuPrimitive.Group, { "data-slot": "dropdown-menu-group", ...props });
}
function DropdownMenuSub(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx35(DropdownMenuSubProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.Sub,
    {
      "data-slot": "dropdown-menu-sub",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function DropdownMenuRadioGroup(props) {
  return /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.RadioGroup,
    {
      "data-slot": "dropdown-menu-radio-group",
      ...props
    }
  );
}
function DropdownMenuSubTrigger({
  disabled,
  textValue,
  ...props
}) {
  const { setHighlightedValue } = useDropdownMenu();
  const [, highlightedRef] = useDataState(
    "highlighted",
    void 0,
    (value) => {
      if (value === true) {
        const el = highlightedRef.current;
        const v = el?.dataset.value || el?.id || null;
        if (v) setHighlightedValue(v);
      }
    }
  );
  return /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.SubTrigger,
    {
      ref: highlightedRef,
      disabled,
      textValue,
      asChild: true,
      children: /* @__PURE__ */ jsx35(
        motion7.div,
        {
          "data-slot": "dropdown-menu-sub-trigger",
          "data-disabled": disabled,
          ...props
        }
      )
    }
  );
}
function DropdownMenuSubContent({
  loop,
  onEscapeKeyDown,
  onPointerDownOutside,
  onFocusOutside,
  onInteractOutside,
  sideOffset,
  alignOffset,
  avoidCollisions,
  collisionBoundary,
  collisionPadding,
  arrowPadding,
  sticky,
  hideWhenDetached,
  transition = { duration: 0.1 },
  style,
  container,
  ...props
}) {
  const { isOpen } = useDropdownMenuSub();
  return /* @__PURE__ */ jsx35(AnimatePresence6, { children: isOpen && /* @__PURE__ */ jsx35(DropdownMenuPortal, { forceMount: true, container, children: /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.SubContent,
    {
      asChild: true,
      forceMount: true,
      loop,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      sideOffset,
      alignOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      children: /* @__PURE__ */ jsx35(
        motion7.div,
        {
          "data-slot": "dropdown-menu-sub-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: { willChange: "opacity, transform", ...style },
          ...props
        },
        "dropdown-menu-sub-content"
      )
    }
  ) }) });
}
function DropdownMenuContent({
  loop,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  onFocusOutside,
  onInteractOutside,
  side,
  sideOffset,
  align,
  alignOffset,
  avoidCollisions,
  collisionBoundary,
  collisionPadding,
  arrowPadding,
  sticky,
  hideWhenDetached,
  transition = { duration: 0.1 },
  style,
  container,
  ...props
}) {
  const { isOpen } = useDropdownMenu();
  return /* @__PURE__ */ jsx35(AnimatePresence6, { children: isOpen && /* @__PURE__ */ jsx35(DropdownMenuPortal, { forceMount: true, container, children: /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.Content,
    {
      asChild: true,
      loop,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      side,
      sideOffset,
      align,
      alignOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      children: /* @__PURE__ */ jsx35(
        motion7.div,
        {
          "data-slot": "dropdown-menu-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: { willChange: "opacity, transform", ...style },
          ...props
        },
        "dropdown-menu-content"
      )
    }
  ) }) });
}
function DropdownMenuItem({
  disabled,
  onSelect,
  textValue,
  ...props
}) {
  const { setHighlightedValue } = useDropdownMenu();
  const [, highlightedRef] = useDataState(
    "highlighted",
    void 0,
    (value) => {
      if (value === true) {
        const el = highlightedRef.current;
        const v = el?.dataset.value || el?.id || null;
        if (v) setHighlightedValue(v);
      }
    }
  );
  return /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.Item,
    {
      ref: highlightedRef,
      disabled,
      onSelect,
      textValue,
      asChild: true,
      children: /* @__PURE__ */ jsx35(
        motion7.div,
        {
          "data-slot": "dropdown-menu-item",
          "data-disabled": disabled,
          ...props
        }
      )
    }
  );
}
function DropdownMenuCheckboxItem({
  checked,
  onCheckedChange,
  disabled,
  onSelect,
  textValue,
  ...props
}) {
  const { setHighlightedValue } = useDropdownMenu();
  const [, highlightedRef] = useDataState(
    "highlighted",
    void 0,
    (value) => {
      if (value === true) {
        const el = highlightedRef.current;
        const v = el?.dataset.value || el?.id || null;
        if (v) setHighlightedValue(v);
      }
    }
  );
  return /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.CheckboxItem,
    {
      ref: highlightedRef,
      checked,
      onCheckedChange,
      disabled,
      onSelect,
      textValue,
      asChild: true,
      children: /* @__PURE__ */ jsx35(
        motion7.div,
        {
          "data-slot": "dropdown-menu-checkbox-item",
          "data-disabled": disabled,
          ...props
        }
      )
    }
  );
}
function DropdownMenuRadioItem({
  value,
  disabled,
  onSelect,
  textValue,
  ...props
}) {
  const { setHighlightedValue } = useDropdownMenu();
  const [, highlightedRef] = useDataState(
    "highlighted",
    void 0,
    (value2) => {
      if (value2 === true) {
        const el = highlightedRef.current;
        const v = el?.dataset.value || el?.id || null;
        if (v) setHighlightedValue(v);
      }
    }
  );
  return /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.RadioItem,
    {
      ref: highlightedRef,
      value,
      disabled,
      onSelect,
      textValue,
      asChild: true,
      children: /* @__PURE__ */ jsx35(
        motion7.div,
        {
          "data-slot": "dropdown-menu-radio-item",
          "data-disabled": disabled,
          ...props
        }
      )
    }
  );
}
function DropdownMenuLabel(props) {
  return /* @__PURE__ */ jsx35(DropdownMenuPrimitive.Label, { "data-slot": "dropdown-menu-label", ...props });
}
function DropdownMenuSeparator(props) {
  return /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.Separator,
    {
      "data-slot": "dropdown-menu-separator",
      ...props
    }
  );
}
function DropdownMenuShortcut(props) {
  return /* @__PURE__ */ jsx35("span", { "data-slot": "dropdown-menu-shortcut", ...props });
}
function DropdownMenuItemIndicator(props) {
  return /* @__PURE__ */ jsx35(
    DropdownMenuPrimitive.ItemIndicator,
    {
      "data-slot": "dropdown-menu-item-indicator",
      asChild: true,
      children: /* @__PURE__ */ jsx35(motion7.div, { ...props })
    }
  );
}

// src/components/ui/dropdown-menu.tsx
import { jsx as jsx36, jsxs as jsxs13 } from "react/jsx-runtime";
function DropdownMenu2({
  ...props
}) {
  return /* @__PURE__ */ jsx36(DropdownMenu, { ...props });
}
function DropdownMenuPortal2({
  ...props
}) {
  return /* @__PURE__ */ jsx36(DropdownMenuPortal, { ...props });
}
function DropdownMenuTrigger2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    DropdownMenuTrigger,
    {
      className: cn(
        "group [&_[data-slot=dropdown-chevron]]:text-gray-400 [&_[data-slot=dropdown-chevron]]:transition-colors [&_[data-slot=dropdown-chevron]]:duration-150 hover:[&_[data-slot=dropdown-chevron]]:text-gray-500",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuContent2({
  className,
  sideOffset = 2,
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    DropdownMenuContent,
    {
      sideOffset,
      className: cn(
        "z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover p-1 text-secondary-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuGroup2({
  ...props
}) {
  return /* @__PURE__ */ jsx36(DropdownMenuGroup, { ...props });
}
function DropdownMenuItem2({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    DropdownMenuItem,
    {
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "relative flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-gray-50 focus:text-accent-foreground dark:focus:bg-white/[0.06] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:text-red-400 dark:data-[variant=destructive]:focus:bg-red-500/15 dark:data-[variant=destructive]:focus:text-red-400 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive! dark:data-[variant=destructive]:*:[svg]:text-red-400! data-[variant=destructive]:focus:[&_[data-slot=dropdown-menu-shortcut]]:text-destructive/50 dark:data-[variant=destructive]:focus:[&_[data-slot=dropdown-menu-shortcut]]:text-red-400/50",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuCheckboxItem2({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs13(
    DropdownMenuCheckboxItem,
    {
      className: cn(
        "relative flex cursor-pointer items-center gap-2 rounded-lg py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-gray-50 focus:text-accent-foreground dark:focus:bg-white/[0.06] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx36("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx36(AnimatedCheckIcon, { checked: checked === true }) }),
        children
      ]
    }
  );
}
function DropdownMenuRadioGroup2({
  ...props
}) {
  return /* @__PURE__ */ jsx36(DropdownMenuRadioGroup, { ...props });
}
function DropdownMenuRadioItem2({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs13(
    DropdownMenuRadioItem,
    {
      className: cn(
        "relative flex cursor-pointer items-center gap-2 rounded-lg py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-gray-50 focus:text-accent-foreground dark:focus:bg-white/[0.06] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx36("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx36(DropdownMenuItemIndicator, { children: /* @__PURE__ */ jsx36(CircleIcon2, { className: "size-2 fill-current text-primary" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuLabel2({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    DropdownMenuLabel,
    {
      "data-inset": inset,
      className: cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSeparator2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    DropdownMenuSeparator,
    {
      className: cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function DropdownMenuShortcut2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    DropdownMenuShortcut,
    {
      className: cn(
        "ml-auto text-xs font-normal text-gray-400",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSub2({
  ...props
}) {
  return /* @__PURE__ */ jsx36(DropdownMenuSub, { ...props });
}
function DropdownMenuSubTrigger2({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs13(
    DropdownMenuSubTrigger,
    {
      "data-inset": inset,
      className: cn(
        "flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-gray-50 focus:text-accent-foreground dark:focus:bg-white/[0.06] data-[inset]:pl-8 data-[state=open]:bg-gray-50 data-[state=open]:text-accent-foreground dark:data-[state=open]:bg-white/[0.06] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx36(ChevronRightIcon3, { className: "ml-auto size-4" })
      ]
    }
  );
}
function DropdownMenuSubContent2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    DropdownMenuSubContent,
    {
      className: cn(
        "z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/empty.tsx
import { cva as cva7 } from "class-variance-authority";
import { jsx as jsx37 } from "react/jsx-runtime";
function Empty({ className, ...props }) {
  return /* @__PURE__ */ jsx37(
    "div",
    {
      "data-slot": "empty",
      className: cn(
        "flex min-w-0 flex-1 flex-col items-center justify-center gap-6 rounded-lg border-dashed p-6 text-center text-balance md:p-12",
        className
      ),
      ...props
    }
  );
}
function EmptyHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx37(
    "div",
    {
      "data-slot": "empty-header",
      className: cn(
        "flex max-w-sm flex-col items-center gap-2 text-center",
        className
      ),
      ...props
    }
  );
}
var emptyMediaVariants = cva7(
  "mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground [&_svg:not([class*='size-'])]:size-6"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function EmptyMedia({
  className,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx37(
    "div",
    {
      "data-slot": "empty-icon",
      "data-variant": variant,
      className: cn(emptyMediaVariants({ variant, className })),
      ...props
    }
  );
}
function EmptyTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx37(
    "div",
    {
      "data-slot": "empty-title",
      className: cn("text-lg font-medium tracking-tight", className),
      ...props
    }
  );
}
function EmptyDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx37(
    "div",
    {
      "data-slot": "empty-description",
      className: cn(
        "text-sm/relaxed text-muted-foreground [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      ),
      ...props
    }
  );
}
function EmptyContent({ className, ...props }) {
  return /* @__PURE__ */ jsx37(
    "div",
    {
      "data-slot": "empty-content",
      className: cn(
        "flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm text-balance",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/field.tsx
import { useMemo as useMemo3, useRef as useRef8 } from "react";
import { cva as cva8 } from "class-variance-authority";

// src/components/ui/label.tsx
import { Label as LabelPrimitive } from "radix-ui";
import { jsx as jsx38 } from "react/jsx-runtime";
function Label({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx38(
    LabelPrimitive.Root,
    {
      "data-slot": "label",
      className: cn(
        "flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/field.tsx
import { jsx as jsx39, jsxs as jsxs14 } from "react/jsx-runtime";
function FieldSet({ className, ...props }) {
  return /* @__PURE__ */ jsx39(
    "fieldset",
    {
      "data-slot": "field-set",
      className: cn(
        "flex flex-col gap-6",
        "has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3",
        className
      ),
      ...props
    }
  );
}
function FieldLegend({
  className,
  variant = "legend",
  ...props
}) {
  return /* @__PURE__ */ jsx39(
    "legend",
    {
      "data-slot": "field-legend",
      "data-variant": variant,
      className: cn(
        "mb-3 font-medium",
        "data-[variant=legend]:text-base",
        "data-[variant=label]:text-sm",
        className
      ),
      ...props
    }
  );
}
var fieldGroupVariants = cva8(
  "group/field-group @container/field-group flex w-full",
  {
    variants: {
      variant: {
        default: "gap-7 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4",
        // Connected container that mirrors the ui-kit OptionGroup. The outer border
        // is a non-inset box-shadow halo (shadow-btn-secondary-flat) so it renders
        // outside the element and blends with the surrounding shadows/background,
        // rather than a solid border. The "-flat" token matches the OptionGroup's
        // shadow exactly — secondary halo + a 1px drop, without the larger soft
        // layer the plain secondary button shadow adds. Each direct Field child
        // becomes a padded cell; the dividers between cells are added by the
        // orientation compound variants below (a bottom border when stacked, a
        // right border when side-by-side).
        outline: [
          "gap-0 overflow-hidden rounded-lg bg-background shadow-btn-secondary-flat",
          // Dark mode mirrors the dropdown/select surface: an input-tinted background
          // with a hairline light edge (the light-mode shadow halo is invisible on a
          // dark background), so the group reads as the same kind of raised control.
          "dark:bg-input/30 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)]",
          // Explicit duration (matching the rest of the kit's transitions) so the
          // hover background eases in/out smoothly in both light and dark mode —
          // transition-colors alone leaves the duration at 0 and the color snaps.
          "[&>[data-slot=field]]:p-4 [&>[data-slot=field]]:transition-colors [&>[data-slot=field]]:duration-150",
          // Light: a subtle muted tint on hover.
          "[&>[data-slot=field]:hover]:bg-muted/30",
          // Dark: the muted tint is nearly invisible against the input-tinted
          // surface, so lighten with a translucent white overlay (the kit's dark
          // hover convention). The resting colour is that SAME white at zero alpha
          // — not `transparent`, which is black-at-zero-alpha — so the background
          // stays white through the whole fade. Fading to plain `transparent`
          // instead interpolates toward transparent-black, which is invisible on
          // the dark surface and makes hovering out look like an instant snap.
          "dark:[&>[data-slot=field]]:bg-[rgb(255_255_255/0)]",
          "dark:[&>[data-slot=field]:hover]:bg-[rgb(255_255_255/0.04)]"
        ]
      },
      // Arrangement of the direct Field children. `responsive` stacks them
      // vertically on small viewports and lays them out side-by-side (equal width)
      // at the `md` breakpoint and up — the OptionGroup horizontal layout, but it
      // collapses to a vertical list on mobile. A viewport breakpoint (not a
      // container query) is used so the behavior is self-contained and doesn't
      // depend on an ancestor establishing a container context.
      orientation: {
        vertical: "flex-col",
        horizontal: "flex-row [&>[data-slot=field]]:flex-1",
        responsive: "flex-col md:flex-row md:[&>[data-slot=field]]:flex-1"
      }
    },
    compoundVariants: [
      // Outline dividers: a soft hairline (the border token at 60% so it reads
      // as a subtle separator rather than a hard line) between stacked rows…
      {
        variant: "outline",
        orientation: "vertical",
        className: "[&>[data-slot=field]]:border-b [&>[data-slot=field]]:border-border/60 dark:[&>[data-slot=field]]:border-white/[0.08] [&>[data-slot=field]:last-child]:border-b-0"
      },
      // …and between side-by-side columns.
      {
        variant: "outline",
        orientation: "horizontal",
        className: "[&>[data-slot=field]]:border-r [&>[data-slot=field]]:border-border/60 dark:[&>[data-slot=field]]:border-white/[0.08] [&>[data-slot=field]:last-child]:border-r-0"
      },
      // Responsive flips the divider direction along with the flex direction:
      // bottom borders while stacked, right borders once side-by-side.
      {
        variant: "outline",
        orientation: "responsive",
        className: [
          "[&>[data-slot=field]]:border-border/60 dark:[&>[data-slot=field]]:border-white/[0.08]",
          "[&>[data-slot=field]]:border-b [&>[data-slot=field]:last-child]:border-b-0",
          "md:[&>[data-slot=field]]:border-b-0 md:[&>[data-slot=field]]:border-r md:[&>[data-slot=field]:last-child]:border-r-0"
        ]
      }
    ],
    defaultVariants: {
      variant: "default",
      orientation: "vertical"
    }
  }
);
function FieldGroup({
  className,
  variant = "default",
  orientation = "vertical",
  ...props
}) {
  return /* @__PURE__ */ jsx39(
    "div",
    {
      "data-slot": "field-group",
      "data-variant": variant,
      "data-orientation": orientation,
      className: cn(fieldGroupVariants({ variant, orientation }), className),
      ...props
    }
  );
}
var fieldVariants = cva8(
  "group/field flex w-full gap-2 data-[invalid=true]:text-destructive has-[[role=checkbox]]:cursor-pointer has-[[role=radio]]:cursor-pointer has-[[role=switch]]:cursor-pointer",
  {
    variants: {
      orientation: {
        vertical: ["flex-col [&>*]:w-full [&>.sr-only]:w-auto"],
        horizontal: [
          "flex-row items-center",
          "[&>[data-slot=field-label]]:flex-auto",
          "has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px"
        ],
        responsive: [
          "flex-col @md/field-group:flex-row @md/field-group:items-center [&>*]:w-full @md/field-group:[&>*]:w-auto [&>.sr-only]:w-auto",
          "@md/field-group:[&>[data-slot=field-label]]:flex-auto",
          "@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px"
        ]
      }
    },
    defaultVariants: {
      orientation: "vertical"
    }
  }
);
function Field({
  className,
  orientation = "vertical",
  onClick,
  ...props
}) {
  const fieldRef = useRef8(null);
  const handleClick = (event) => {
    onClick?.(event);
    if (event.defaultPrevented) {
      return;
    }
    const target = event.target;
    if (target.closest(
      'label, button, a, input, select, textarea, [role="checkbox"], [role="radio"], [role="switch"]'
    )) {
      return;
    }
    fieldRef.current?.querySelector(
      '[role="checkbox"], [role="radio"], [role="switch"]'
    )?.click();
  };
  return /* @__PURE__ */ jsx39(
    "div",
    {
      ref: fieldRef,
      role: "group",
      "data-slot": "field",
      "data-orientation": orientation,
      className: cn(fieldVariants({ orientation }), className),
      onClick: handleClick,
      ...props
    }
  );
}
function FieldContent({ className, ...props }) {
  return /* @__PURE__ */ jsx39(
    "div",
    {
      "data-slot": "field-content",
      className: cn(
        "group/field-content flex flex-1 flex-col gap-0.5 leading-snug",
        className
      ),
      ...props
    }
  );
}
function FieldLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx39(
    Label,
    {
      "data-slot": "field-label",
      className: cn(
        "group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50",
        "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border [&>*]:data-[slot=field]:p-4",
        "has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5 dark:has-data-[state=checked]:bg-primary/10",
        className
      ),
      ...props
    }
  );
}
function FieldTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx39(
    "div",
    {
      "data-slot": "field-label",
      className: cn(
        "flex w-fit items-center gap-2 text-sm leading-snug font-medium group-data-[disabled=true]/field:opacity-50",
        className
      ),
      ...props
    }
  );
}
function FieldDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx39(
    "p",
    {
      "data-slot": "field-description",
      className: cn(
        "text-sm leading-normal font-normal text-muted-foreground group-has-[[data-orientation=horizontal]]/field:text-balance",
        "last:mt-0 nth-last-2:-mt-1 [[data-variant=legend]+&]:-mt-1.5",
        "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      ),
      ...props
    }
  );
}
function FieldSeparator({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs14(
    "div",
    {
      "data-slot": "field-separator",
      "data-content": !!children,
      className: cn(
        "relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx39(Separator, { className: "absolute inset-0 top-1/2" }),
        children && /* @__PURE__ */ jsx39(
          "span",
          {
            className: "relative mx-auto block w-fit bg-background px-2 text-muted-foreground",
            "data-slot": "field-separator-content",
            children
          }
        )
      ]
    }
  );
}
function FieldError({
  className,
  children,
  errors,
  ...props
}) {
  const content = useMemo3(() => {
    if (children) {
      return children;
    }
    if (!errors?.length) {
      return null;
    }
    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values()
    ];
    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message;
    }
    return /* @__PURE__ */ jsx39("ul", { className: "ml-4 flex list-disc flex-col gap-1", children: uniqueErrors.map(
      (error, index) => error?.message && /* @__PURE__ */ jsx39("li", { children: error.message }, index)
    ) });
  }, [children, errors]);
  if (!content) {
    return null;
  }
  return /* @__PURE__ */ jsx39(
    "div",
    {
      role: "alert",
      "data-slot": "field-error",
      className: cn("text-sm font-normal text-destructive", className),
      ...props,
      children: content
    }
  );
}

// src/components/ui/form.tsx
import * as React18 from "react";
import { Slot as Slot5 } from "radix-ui";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState
} from "react-hook-form";
import { jsx as jsx40 } from "react/jsx-runtime";
var Form = FormProvider;
var FormFieldContext = React18.createContext(
  {}
);
var FormField = ({
  ...props
}) => {
  return /* @__PURE__ */ jsx40(FormFieldContext.Provider, { value: { name: props.name }, children: /* @__PURE__ */ jsx40(Controller, { ...props }) });
};
var useFormField = () => {
  const fieldContext = React18.useContext(FormFieldContext);
  const itemContext = React18.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }
  const { id } = itemContext;
  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState
  };
};
var FormItemContext = React18.createContext(
  {}
);
function FormItem({ className, ...props }) {
  const id = React18.useId();
  return /* @__PURE__ */ jsx40(FormItemContext.Provider, { value: { id }, children: /* @__PURE__ */ jsx40(
    "div",
    {
      "data-slot": "form-item",
      className: cn("grid gap-2", className),
      ...props
    }
  ) });
}
function FormLabel({
  className,
  ...props
}) {
  const { error, formItemId } = useFormField();
  return /* @__PURE__ */ jsx40(
    Label,
    {
      "data-slot": "form-label",
      "data-error": !!error,
      className: cn("data-[error=true]:text-destructive", className),
      htmlFor: formItemId,
      ...props
    }
  );
}
function FormControl({ ...props }) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  return /* @__PURE__ */ jsx40(
    Slot5.Root,
    {
      "data-slot": "form-control",
      id: formItemId,
      "aria-describedby": !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`,
      "aria-invalid": !!error,
      ...props
    }
  );
}
function FormDescription({ className, ...props }) {
  const { formDescriptionId } = useFormField();
  return /* @__PURE__ */ jsx40(
    "p",
    {
      "data-slot": "form-description",
      id: formDescriptionId,
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function FormMessage({ className, ...props }) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;
  if (!body) {
    return null;
  }
  return /* @__PURE__ */ jsx40(
    "p",
    {
      "data-slot": "form-message",
      id: formMessageId,
      className: cn("text-sm text-destructive", className),
      ...props,
      children: body
    }
  );
}

// src/components/animate-ui/primitives/radix/hover-card.tsx
import { HoverCard as HoverCardPrimitive } from "radix-ui";
import {
  AnimatePresence as AnimatePresence7,
  motion as motion8,
  useMotionValue,
  useSpring
} from "motion/react";
import { jsx as jsx41 } from "react/jsx-runtime";
var [HoverCardProvider, useHoverCard] = getStrictContext("HoverCardContext");
function HoverCard({
  followCursor = false,
  followCursorSpringOptions = { stiffness: 200, damping: 17 },
  ...props
}) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  return /* @__PURE__ */ jsx41(
    HoverCardProvider,
    {
      value: {
        isOpen,
        setIsOpen,
        x,
        y,
        followCursor,
        followCursorSpringOptions
      },
      children: /* @__PURE__ */ jsx41(
        HoverCardPrimitive.Root,
        {
          "data-slot": "hover-card",
          ...props,
          onOpenChange: setIsOpen
        }
      )
    }
  );
}
function HoverCardTrigger({ onMouseMove, ...props }) {
  const { x, y, followCursor } = useHoverCard();
  const handleMouseMove = (event) => {
    onMouseMove?.(event);
    const target = event.currentTarget.getBoundingClientRect();
    if (followCursor === "x" || followCursor === true) {
      const eventOffsetX = event.clientX - target.left;
      const offsetXFromCenter = (eventOffsetX - target.width / 2) / 2;
      x.set(offsetXFromCenter);
    }
    if (followCursor === "y" || followCursor === true) {
      const eventOffsetY = event.clientY - target.top;
      const offsetYFromCenter = (eventOffsetY - target.height / 2) / 2;
      y.set(offsetYFromCenter);
    }
  };
  return /* @__PURE__ */ jsx41(
    HoverCardPrimitive.Trigger,
    {
      "data-slot": "hover-card-trigger",
      onMouseMove: handleMouseMove,
      ...props
    }
  );
}
function HoverCardPortal(props) {
  const { isOpen } = useHoverCard();
  return /* @__PURE__ */ jsx41(AnimatePresence7, { children: isOpen && /* @__PURE__ */ jsx41(
    HoverCardPrimitive.Portal,
    {
      forceMount: true,
      "data-slot": "hover-card-portal",
      ...props
    }
  ) });
}
function HoverCardContent({
  align,
  alignOffset,
  side,
  sideOffset,
  avoidCollisions,
  collisionBoundary,
  collisionPadding,
  arrowPadding,
  sticky,
  hideWhenDetached,
  style,
  transition = { type: "spring", stiffness: 600, damping: 35 },
  ...props
}) {
  const { x, y, followCursor, followCursorSpringOptions } = useHoverCard();
  const translateX = useSpring(x, followCursorSpringOptions);
  const translateY = useSpring(y, followCursorSpringOptions);
  return /* @__PURE__ */ jsx41(
    HoverCardPrimitive.Content,
    {
      asChild: true,
      forceMount: true,
      align,
      alignOffset,
      side,
      sideOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      children: /* @__PURE__ */ jsx41(
        motion8.div,
        {
          "data-slot": "hover-card-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: {
            x: followCursor === "x" || followCursor === true ? translateX : void 0,
            y: followCursor === "y" || followCursor === true ? translateY : void 0,
            ...style
          },
          ...props
        },
        "hover-card-content"
      )
    }
  );
}

// src/components/ui/hover-card.tsx
import { jsx as jsx42 } from "react/jsx-runtime";
function HoverCard2({
  ...props
}) {
  return /* @__PURE__ */ jsx42(HoverCard, { ...props });
}
function HoverCardTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx42(HoverCardTrigger, { "data-slot": "hover-card-trigger", ...props });
}
function HoverCardContent2({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx42(HoverCardPortal, { children: /* @__PURE__ */ jsx42(
    HoverCardContent,
    {
      "data-slot": "hover-card-content",
      align,
      sideOffset,
      className: cn(
        "z-50 w-64 origin-(--radix-hover-card-content-transform-origin) rounded-lg bg-popover p-4 text-popover-foreground shadow-md ring-1 ring-black/5 outline-hidden dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props
    }
  ) });
}

// src/components/ui/input-otp.tsx
import * as React19 from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";
import { jsx as jsx43, jsxs as jsxs15 } from "react/jsx-runtime";
function InputOTP({
  className,
  containerClassName,
  ...props
}) {
  return /* @__PURE__ */ jsx43(
    OTPInput,
    {
      "data-slot": "input-otp",
      containerClassName: cn(
        "flex items-center gap-2 has-disabled:opacity-50",
        containerClassName
      ),
      className: cn("disabled:cursor-not-allowed", className),
      ...props
    }
  );
}
function InputOTPGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx43(
    "div",
    {
      "data-slot": "input-otp-group",
      className: cn(
        "flex items-center rounded-lg bg-gray-25 inset-shadow-input transition-[color,box-shadow] duration-150 hover:inset-shadow-input-hover dark:bg-input/30 dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.1)] dark:hover:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.2)]",
        className
      ),
      ...props
    }
  );
}
function InputOTPSlot({
  index,
  className,
  ...props
}) {
  const inputOTPContext = React19.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};
  return /* @__PURE__ */ jsxs15(
    "div",
    {
      "data-slot": "input-otp-slot",
      "data-active": isActive,
      className: cn(
        "relative my-px flex h-9 w-9 items-center justify-center border-r border-gray-100 text-sm font-medium transition-all outline-none first:rounded-l-lg last:rounded-r-lg last:border-r-0 data-[active=true]:z-10 data-[active=true]:ring-2 data-[active=true]:ring-blue-500/10 data-[active=true]:shadow-[0_0_0_1px_var(--color-blue-500)] aria-invalid:shadow-[0_0_0_1px_var(--color-destructive)] data-[active=true]:aria-invalid:ring-destructive/20 dark:border-gray-700 dark:data-[active=true]:aria-invalid:ring-destructive/40",
        className
      ),
      ...props,
      children: [
        char,
        hasFakeCaret && /* @__PURE__ */ jsx43("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx43("div", { className: "h-4 w-px animate-caret-blink bg-foreground duration-1000" }) })
      ]
    }
  );
}
function InputOTPSeparator({ ...props }) {
  return /* @__PURE__ */ jsx43("div", { "data-slot": "input-otp-separator", role: "separator", ...props, children: /* @__PURE__ */ jsx43(MinusIcon, {}) });
}

// src/components/ui/item.tsx
import { cva as cva9 } from "class-variance-authority";
import { Slot as Slot6 } from "radix-ui";
import { jsx as jsx44 } from "react/jsx-runtime";
function ItemGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx44(
    "div",
    {
      role: "list",
      "data-slot": "item-group",
      className: cn("group/item-group flex flex-col", className),
      ...props
    }
  );
}
function ItemSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx44(
    Separator,
    {
      "data-slot": "item-separator",
      orientation: "horizontal",
      className: cn("my-0", className),
      ...props
    }
  );
}
var itemVariants = cva9(
  "group/item flex flex-wrap items-center rounded-lg border border-transparent text-sm transition-colors duration-100 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [a]:transition-colors [a]:hover:bg-accent/50",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border-border",
        muted: "bg-muted/50"
      },
      size: {
        default: "gap-4 p-4",
        sm: "gap-2.5 px-4 py-3"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Item({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot6.Root : "div";
  return /* @__PURE__ */ jsx44(
    Comp,
    {
      "data-slot": "item",
      "data-variant": variant,
      "data-size": size,
      className: cn(itemVariants({ variant, size, className })),
      ...props
    }
  );
}
var itemMediaVariants = cva9(
  "flex shrink-0 items-center justify-center gap-2 group-has-[[data-slot=item-description]]/item:translate-y-0.5 group-has-[[data-slot=item-description]]/item:self-start [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "size-8 rounded-sm border bg-muted [&_svg:not([class*='size-'])]:size-4",
        image: "size-10 overflow-hidden rounded-sm [&_img]:size-full [&_img]:object-cover"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function ItemMedia({
  className,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx44(
    "div",
    {
      "data-slot": "item-media",
      "data-variant": variant,
      className: cn(itemMediaVariants({ variant, className })),
      ...props
    }
  );
}
function ItemContent({ className, ...props }) {
  return /* @__PURE__ */ jsx44(
    "div",
    {
      "data-slot": "item-content",
      className: cn(
        "flex flex-1 flex-col gap-1 [&+[data-slot=item-content]]:flex-none",
        className
      ),
      ...props
    }
  );
}
function ItemTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx44(
    "div",
    {
      "data-slot": "item-title",
      className: cn(
        "flex w-fit items-center gap-2 text-sm leading-snug font-medium",
        className
      ),
      ...props
    }
  );
}
function ItemDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx44(
    "p",
    {
      "data-slot": "item-description",
      className: cn(
        "line-clamp-2 text-sm leading-normal font-normal text-balance text-muted-foreground",
        "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      ),
      ...props
    }
  );
}
function ItemActions({ className, ...props }) {
  return /* @__PURE__ */ jsx44(
    "div",
    {
      "data-slot": "item-actions",
      className: cn("flex items-center gap-2", className),
      ...props
    }
  );
}
function ItemHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx44(
    "div",
    {
      "data-slot": "item-header",
      className: cn(
        "flex basis-full items-center justify-between gap-2",
        className
      ),
      ...props
    }
  );
}
function ItemFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx44(
    "div",
    {
      "data-slot": "item-footer",
      className: cn(
        "flex basis-full items-center justify-between gap-2",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/kbd.tsx
import { jsx as jsx45 } from "react/jsx-runtime";
function Kbd({ className, ...props }) {
  return /* @__PURE__ */ jsx45(
    "kbd",
    {
      "data-slot": "kbd",
      className: cn(
        "pointer-events-none inline-flex h-5 w-fit min-w-5 items-center justify-center gap-1 rounded-sm bg-muted px-1 font-sans text-xs font-normal text-muted-foreground select-none",
        "[&_svg:not([class*='size-'])]:size-3",
        "[[data-slot=tooltip-content]_&]:bg-background/20 [[data-slot=tooltip-content]_&]:text-background dark:[[data-slot=tooltip-content]_&]:bg-background/10",
        className
      ),
      ...props
    }
  );
}
function KbdGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx45(
    "kbd",
    {
      "data-slot": "kbd-group",
      className: cn("inline-flex items-center gap-1", className),
      ...props
    }
  );
}

// src/components/ui/logo.tsx
import { jsx as jsx46, jsxs as jsxs16 } from "react/jsx-runtime";
function LogoWordmark({
  size = 16,
  color = "currentColor",
  className,
  ...props
}) {
  const width = size / 19 * 87;
  return /* @__PURE__ */ jsxs16(
    "svg",
    {
      width,
      height: size,
      viewBox: "0 0 87 19",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      className,
      ...props,
      children: [
        /* @__PURE__ */ jsx46("g", { clipPath: "url(#logo-wordmark-clip)", children: /* @__PURE__ */ jsx46(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M28.1811 2.16278V0H33.0581V18.654H29.814V3.24417C29.814 2.95736 29.7 2.68231 29.4972 2.47951C29.2944 2.27671 29.0194 2.16278 28.7326 2.16278H28.1811ZM8.77018 15.5284C9.29957 15.2419 9.74358 14.8203 10.057 14.3064L10.1219 14.155L12.7281 15.9826L12.6632 16.0691C12.0259 16.9766 11.1804 17.7182 10.1976 18.2319C9.19389 18.7455 8.08091 19.0089 6.95345 18.9997C5.7259 19.011 4.51718 18.6976 3.44975 18.0913C2.40616 17.4929 1.53747 16.6317 0.930117 15.5933C0.314039 14.5407 -0.00720686 13.3417 0.000122672 12.1221C-0.00561525 10.9027 0.31552 9.70399 0.930117 8.65078C1.52795 7.59667 2.39821 6.72268 3.44975 6.12034C4.51633 5.51074 5.72498 5.19365 6.95345 5.20115C8.00027 5.19459 9.03515 5.42373 9.98134 5.87161C10.8982 6.30469 11.7071 6.93635 12.3496 7.72079L12.4145 7.79649L10.003 9.81869L9.93808 9.73217C9.59507 9.28032 9.15541 8.91085 8.65123 8.65078C8.12907 8.37267 7.545 8.23129 6.95345 8.23985C6.28793 8.23221 5.63317 8.40806 5.06102 8.74811C4.50633 9.09381 4.05155 9.57841 3.74173 10.1539C3.41084 10.7568 3.2396 11.4343 3.24429 12.1221C3.2507 12.7994 3.42535 13.4646 3.75254 14.0577C4.05492 14.6449 4.51569 15.1356 5.08265 15.4743C5.64807 15.8116 6.29512 15.9874 6.95345 15.9826C7.5882 15.9915 8.21432 15.835 8.77018 15.5284ZM20.687 5.2123C21.926 5.20317 23.1455 5.52016 24.2232 6.13147C25.2819 6.72689 26.16 7.59749 26.7644 8.6511C27.3809 9.70747 27.7021 10.9101 27.6944 12.1332C27.7018 13.3528 27.3805 14.5518 26.7644 15.6044C26.1519 16.6462 25.2753 17.5079 24.2232 18.1024C23.1445 18.71 21.925 19.0232 20.687 19.0108C19.4595 19.0221 18.2508 18.7087 17.1833 18.1024C16.1367 17.5044 15.2644 16.6433 14.6529 15.6044C14.0368 14.5518 13.7156 13.3528 13.7229 12.1332C13.7152 10.9101 14.0364 9.70747 14.6529 8.6511C15.2565 7.60053 16.1302 6.73057 17.1833 6.13147C18.2499 5.52187 19.4586 5.20479 20.687 5.2123ZM22.5903 15.4314C23.1513 15.1044 23.6115 14.6293 23.9204 14.058C24.254 13.4523 24.4253 12.7706 24.4178 12.0791C24.4225 11.3914 24.2513 10.7139 23.9204 10.111C23.6106 9.53548 23.1558 9.05088 22.6011 8.70518C22.029 8.36513 21.3742 8.18928 20.7087 8.19692C20.0465 8.18903 19.3952 8.36497 18.827 8.70518C18.2668 9.04741 17.8077 9.53263 17.4969 10.111C17.1694 10.7147 17.0018 11.3923 17.0103 12.0791C16.9942 12.7689 17.1582 13.451 17.4861 14.058C17.7874 14.6313 18.2448 15.1074 18.8054 15.4314C19.3746 15.769 20.0252 15.9448 20.687 15.9397C21.3557 15.9449 22.0133 15.7693 22.5903 15.4314ZM45.0398 12.4575C45.0541 13.1281 44.9057 13.7923 44.6072 14.3931C44.3423 14.8879 43.9456 15.2996 43.461 15.5827C42.9837 15.8446 42.4482 15.9822 41.9038 15.9828C41.3906 15.9957 40.8832 15.8725 40.4331 15.6259C40.0208 15.3789 39.6842 15.0236 39.4598 14.5986C39.2129 14.1097 39.0903 13.5674 39.103 13.0198V5.52575H35.8588V13.6686C35.8422 14.6184 36.0693 15.5566 36.5185 16.3937C36.9441 17.1918 37.5846 17.8549 38.3676 18.3078C39.2054 18.7784 40.1536 19.0173 41.1144 18.9999C42.1583 19.0109 43.1846 18.73 44.0774 18.1888C44.4767 17.9355 44.8403 17.63 45.1588 17.2804V18.6971H48.2623V5.52575H45.0182L45.0398 12.4575ZM66.0079 5.21235C66.9256 5.20566 67.8299 5.43265 68.6357 5.87198C69.4093 6.31173 70.0514 6.95012 70.4957 7.72116C70.9585 8.4714 71.1985 9.33773 71.1878 10.2192V18.7513H67.9436V11.041C67.949 10.5533 67.8378 10.0713 67.6192 9.63522C67.4159 9.23107 67.1054 8.89063 66.7216 8.65116C66.331 8.4131 65.8814 8.28946 65.424 8.2943C64.9729 8.28634 64.5294 8.41038 64.1479 8.65116C63.7628 8.89567 63.4465 9.23437 63.2287 9.63522C62.9981 10.0677 62.8791 10.5509 62.8827 11.041V18.7513H59.6385V11.041C59.6406 10.5524 59.5257 10.0703 59.3033 9.63522C59.0894 9.23511 58.7767 8.89635 58.3949 8.65116C58.0042 8.38091 57.5399 8.23743 57.0648 8.24022C56.6178 8.2368 56.179 8.36057 55.7996 8.59708C55.412 8.83852 55.0949 9.17797 54.8804 9.58115C54.658 10.0163 54.5431 10.4983 54.5452 10.987V18.6973H51.301V5.52594H54.264V6.95337C54.569 6.55993 54.9339 6.21692 55.3454 5.93688C56.0822 5.45816 56.9432 5.20624 57.8218 5.21235C58.6427 5.20363 59.4491 5.42866 60.1468 5.86117C60.7499 6.22657 61.2605 6.72615 61.6391 7.32105C62.0829 6.74989 62.6385 6.27522 63.272 5.92605C64.1045 5.44969 65.0488 5.20337 66.0079 5.21235ZM85.8517 7.85075C85.4218 7.04573 84.7733 6.3785 83.9809 5.92588C83.1544 5.44443 82.2122 5.19767 81.2558 5.21218C80.1986 5.20035 79.1598 5.4891 78.2603 6.04484C77.8655 6.29163 77.5023 6.58578 77.1789 6.92076V5.52578H74.0429V18.6971H77.2871V11.7654C77.2695 11.1025 77.4143 10.4454 77.7088 9.85133C77.9904 9.33688 78.4198 8.91874 78.9416 8.65099C79.4194 8.37944 79.96 8.23776 80.5096 8.24006C81.017 8.22906 81.5179 8.35615 81.9587 8.60774C82.3864 8.86626 82.7315 9.24139 82.9536 9.68913C83.1987 10.1671 83.3213 10.6985 83.3104 11.2355V18.7403H86.5546V10.6191C86.5654 9.65087 86.3231 8.69656 85.8517 7.85075Z",
            fill: color
          }
        ) }),
        /* @__PURE__ */ jsx46("defs", { children: /* @__PURE__ */ jsx46("clipPath", { id: "logo-wordmark-clip", children: /* @__PURE__ */ jsx46("rect", { width: "86.5111", height: "19", fill: "white" }) }) })
      ]
    }
  );
}
function LogoIcon({
  size = 16,
  color = "currentColor",
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx46(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 16 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      className,
      ...props,
      children: /* @__PURE__ */ jsx46(
        "path",
        {
          d: "M6 6V10H8C9.06087 10 10.0783 10.4214 10.8284 11.1716C11.5786 11.9217 12 12.9391 12 14V16H4C2.93913 16 1.92172 15.5786 1.17157 14.8284C0.421427 14.0783 0 13.0609 0 12V4C0 2.93913 0.421427 1.92172 1.17157 1.17157C1.92172 0.421427 2.93913 0 4 0L12 0C13.0609 0 14.0783 0.421427 14.8284 1.17157C15.5786 1.92172 16 2.93913 16 4V6H6Z",
          fill: color
        }
      )
    }
  );
}
function Logo({ variant = "wordmark", ...props }) {
  if (variant === "icon") {
    return /* @__PURE__ */ jsx46(LogoIcon, { ...props });
  }
  return /* @__PURE__ */ jsx46(LogoWordmark, { ...props });
}

// src/components/ui/menubar.tsx
import { ChevronRightIcon as ChevronRightIcon4, CircleIcon as CircleIcon3 } from "lucide-react";
import { Menubar as MenubarPrimitive } from "radix-ui";
import { jsx as jsx47, jsxs as jsxs17 } from "react/jsx-runtime";
function Menubar({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx47(
    MenubarPrimitive.Root,
    {
      "data-slot": "menubar",
      className: cn(
        "flex h-9 items-center gap-1 rounded-lg bg-background p-1 ring-1 ring-black/5 shadow-xs dark:ring-white/15 dark:shadow-none",
        className
      ),
      ...props
    }
  );
}
function MenubarMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx47(MenubarPrimitive.Menu, { "data-slot": "menubar-menu", ...props });
}
function MenubarGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx47(MenubarPrimitive.Group, { "data-slot": "menubar-group", ...props });
}
function MenubarPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx47(MenubarPrimitive.Portal, { "data-slot": "menubar-portal", ...props });
}
function MenubarRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx47(MenubarPrimitive.RadioGroup, { "data-slot": "menubar-radio-group", ...props });
}
function MenubarTrigger({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx47(
    MenubarPrimitive.Trigger,
    {
      "data-slot": "menubar-trigger",
      className: cn(
        "flex items-center rounded-md px-2 py-1 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
        className
      ),
      ...props
    }
  );
}
function MenubarContent({
  className,
  align = "start",
  alignOffset = -4,
  sideOffset = 8,
  ...props
}) {
  return /* @__PURE__ */ jsx47(MenubarPortal, { children: /* @__PURE__ */ jsx47(
    MenubarPrimitive.Content,
    {
      "data-slot": "menubar-content",
      align,
      alignOffset,
      sideOffset,
      className: cn(
        "z-50 min-w-[12rem] origin-(--radix-menubar-content-transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  ) });
}
function MenubarItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx47(
    MenubarPrimitive.Item,
    {
      "data-slot": "menubar-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:text-red-400 dark:data-[variant=destructive]:focus:bg-red-500/15 dark:data-[variant=destructive]:focus:text-red-400 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive! dark:data-[variant=destructive]:*:[svg]:text-red-400! data-[variant=destructive]:focus:[&_[data-slot=menubar-shortcut]]:text-destructive/50 dark:data-[variant=destructive]:focus:[&_[data-slot=menubar-shortcut]]:text-red-400/50",
        className
      ),
      ...props
    }
  );
}
function MenubarCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs17(
    MenubarPrimitive.CheckboxItem,
    {
      "data-slot": "menubar-checkbox-item",
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-md py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx47("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx47(AnimatedCheckIcon, { checked: checked === true }) }),
        children
      ]
    }
  );
}
function MenubarRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs17(
    MenubarPrimitive.RadioItem,
    {
      "data-slot": "menubar-radio-item",
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-md py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx47("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx47(MenubarPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx47(CircleIcon3, { className: "size-2 fill-current text-primary" }) }) }),
        children
      ]
    }
  );
}
function MenubarLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx47(
    MenubarPrimitive.Label,
    {
      "data-slot": "menubar-label",
      "data-inset": inset,
      className: cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function MenubarSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx47(
    MenubarPrimitive.Separator,
    {
      "data-slot": "menubar-separator",
      className: cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function MenubarShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx47(
    "span",
    {
      "data-slot": "menubar-shortcut",
      className: cn(
        "ml-auto text-xs font-normal text-gray-400",
        className
      ),
      ...props
    }
  );
}
function MenubarSub({
  ...props
}) {
  return /* @__PURE__ */ jsx47(MenubarPrimitive.Sub, { "data-slot": "menubar-sub", ...props });
}
function MenubarSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs17(
    MenubarPrimitive.SubTrigger,
    {
      "data-slot": "menubar-sub-trigger",
      "data-inset": inset,
      className: cn(
        "flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-none transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[inset]:pl-8 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx47(ChevronRightIcon4, { className: "ml-auto h-4 w-4" })
      ]
    }
  );
}
function MenubarSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx47(
    MenubarPrimitive.SubContent,
    {
      "data-slot": "menubar-sub-content",
      className: cn(
        "z-50 min-w-[8rem] origin-(--radix-menubar-content-transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/native-select.tsx
import { ChevronDownIcon as ChevronDownIcon4 } from "lucide-react";
import { jsx as jsx48, jsxs as jsxs18 } from "react/jsx-runtime";
function NativeSelect({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsxs18(
    "div",
    {
      className: "group/native-select relative w-fit has-[select:disabled]:opacity-50",
      "data-slot": "native-select-wrapper",
      children: [
        /* @__PURE__ */ jsx48(
          "select",
          {
            "data-slot": "native-select",
            "data-size": size,
            className: cn(
              "h-9 w-full min-w-0 appearance-none rounded-lg border border-input bg-white px-3 pr-9 text-sm leading-none font-medium shadow-[0_1px_1px_0_rgb(0_0_0/0.03)] transition-[color,border-color,box-shadow] duration-150 outline-none placeholder:text-gray-400 hover:border-gray-200 active:shadow-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 data-[size=sm]:h-8 dark:border-white/10 dark:bg-input/30 dark:shadow-none dark:hover:border-white/20 dark:hover:bg-input/50",
              "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
              "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
              className
            ),
            ...props
          }
        ),
        /* @__PURE__ */ jsx48(
          ChevronDownIcon4,
          {
            className: "pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground opacity-50 select-none",
            "aria-hidden": "true",
            "data-slot": "native-select-icon"
          }
        )
      ]
    }
  );
}
function NativeSelectOption({ ...props }) {
  return /* @__PURE__ */ jsx48("option", { "data-slot": "native-select-option", ...props });
}
function NativeSelectOptGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx48(
    "optgroup",
    {
      "data-slot": "native-select-optgroup",
      className: cn(className),
      ...props
    }
  );
}

// src/components/ui/navigation-menu.tsx
import { cva as cva10 } from "class-variance-authority";
import { ChevronDownIcon as ChevronDownIcon5 } from "lucide-react";
import { NavigationMenu as NavigationMenuPrimitive } from "radix-ui";
import { jsx as jsx49, jsxs as jsxs19 } from "react/jsx-runtime";
function NavigationMenu({
  className,
  children,
  viewport = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs19(
    NavigationMenuPrimitive.Root,
    {
      "data-slot": "navigation-menu",
      "data-viewport": viewport,
      className: cn(
        "group/navigation-menu relative flex max-w-max flex-1 items-center justify-center",
        className
      ),
      ...props,
      children: [
        children,
        viewport && /* @__PURE__ */ jsx49(NavigationMenuViewport, {})
      ]
    }
  );
}
function NavigationMenuList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx49(
    NavigationMenuPrimitive.List,
    {
      "data-slot": "navigation-menu-list",
      className: cn(
        "group flex flex-1 list-none items-center justify-center gap-1",
        className
      ),
      ...props
    }
  );
}
function NavigationMenuItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx49(
    NavigationMenuPrimitive.Item,
    {
      "data-slot": "navigation-menu-item",
      className: cn("relative", className),
      ...props
    }
  );
}
var navigationMenuTriggerStyle = cva10(
  "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-[color,box-shadow] outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50 data-[state=open]:text-accent-foreground data-[state=open]:hover:bg-accent data-[state=open]:focus:bg-accent"
);
function NavigationMenuTrigger({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs19(
    NavigationMenuPrimitive.Trigger,
    {
      "data-slot": "navigation-menu-trigger",
      className: cn(navigationMenuTriggerStyle(), "group", className),
      ...props,
      children: [
        children,
        " ",
        /* @__PURE__ */ jsx49(
          ChevronDownIcon5,
          {
            className: "relative top-[1px] ml-1 size-3 transition duration-300 group-data-[state=open]:rotate-180",
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
}
function NavigationMenuContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx49(
    NavigationMenuPrimitive.Content,
    {
      "data-slot": "navigation-menu-content",
      className: cn(
        "top-0 left-0 w-full p-2 pr-2.5 data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 data-[motion^=from-]:animate-in data-[motion^=from-]:fade-in data-[motion^=to-]:animate-out data-[motion^=to-]:fade-out md:absolute md:w-auto",
        "group-data-[viewport=false]/navigation-menu:top-full group-data-[viewport=false]/navigation-menu:mt-1.5 group-data-[viewport=false]/navigation-menu:overflow-hidden group-data-[viewport=false]/navigation-menu:rounded-md group-data-[viewport=false]/navigation-menu:border group-data-[viewport=false]/navigation-menu:bg-popover group-data-[viewport=false]/navigation-menu:text-popover-foreground group-data-[viewport=false]/navigation-menu:shadow-md group-data-[viewport=false]/navigation-menu:ring-1 group-data-[viewport=false]/navigation-menu:ring-black/5 dark:group-data-[viewport=false]/navigation-menu:border-transparent dark:group-data-[viewport=false]/navigation-menu:ring-white/15 dark:group-data-[viewport=false]/navigation-menu:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] group-data-[viewport=false]/navigation-menu:duration-200 **:data-[slot=navigation-menu-link]:focus:ring-0 **:data-[slot=navigation-menu-link]:focus:outline-none group-data-[viewport=false]/navigation-menu:data-[state=closed]:animate-out group-data-[viewport=false]/navigation-menu:data-[state=closed]:fade-out-0 group-data-[viewport=false]/navigation-menu:data-[state=closed]:zoom-out-95 group-data-[viewport=false]/navigation-menu:data-[state=open]:animate-in group-data-[viewport=false]/navigation-menu:data-[state=open]:fade-in-0 group-data-[viewport=false]/navigation-menu:data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  );
}
function NavigationMenuViewport({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx49(
    "div",
    {
      className: cn(
        "absolute top-full left-0 isolate z-50 flex justify-center"
      ),
      children: /* @__PURE__ */ jsx49(
        NavigationMenuPrimitive.Viewport,
        {
          "data-slot": "navigation-menu-viewport",
          className: cn(
            "origin-top-center relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md ring-1 ring-black/5 dark:border-transparent dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:zoom-in-90 md:w-[var(--radix-navigation-menu-viewport-width)]",
            className
          ),
          ...props
        }
      )
    }
  );
}
function NavigationMenuLink({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx49(
    NavigationMenuPrimitive.Link,
    {
      "data-slot": "navigation-menu-link",
      className: cn(
        "flex flex-col gap-1 rounded-sm p-2 text-sm transition-all outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 data-[active=true]:bg-accent/50 data-[active=true]:text-accent-foreground data-[active=true]:hover:bg-accent data-[active=true]:focus:bg-accent [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props
    }
  );
}
function NavigationMenuIndicator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx49(
    NavigationMenuPrimitive.Indicator,
    {
      "data-slot": "navigation-menu-indicator",
      className: cn(
        "top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:animate-in data-[state=visible]:fade-in",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx49("div", { className: "relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" })
    }
  );
}

// src/components/ui/overflow.tsx
import * as React20 from "react";
import { createPortal } from "react-dom";
import { jsx as jsx50, jsxs as jsxs20 } from "react/jsx-runtime";
var VIEWPORT_MARGIN = 16;
var PILL_PADDING_X = 10;
var PILL_PADDING_Y = 6;
var roundToDevicePixel = (value) => {
  const pixelRatio = window.devicePixelRatio || 1;
  return Math.round(value * pixelRatio) / pixelRatio;
};
function TextOverflow({
  align = "left",
  delay = 500,
  hoverContent,
  alwaysShow = false,
  nowrap = false,
  children,
  className,
  ...props
}) {
  const textRef = React20.useRef(null);
  const timerRef = React20.useRef(null);
  const [truncated, setTruncated] = React20.useState(false);
  const [visible, setVisible] = React20.useState(false);
  const [mounted, setMounted] = React20.useState(false);
  const [position, setPosition] = React20.useState(null);
  const [textStyle, setTextStyle] = React20.useState({});
  const shouldShow = alwaysShow || truncated;
  const checkTruncation = React20.useCallback(() => {
    if (alwaysShow) return;
    const el = textRef.current;
    if (!el) return;
    setTruncated(el.scrollWidth > el.clientWidth);
  }, [alwaysShow]);
  React20.useEffect(() => {
    checkTruncation();
    const el = textRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation, children]);
  React20.useEffect(() => {
    if (!visible || !textRef.current) {
      const unmountTimer = setTimeout(() => {
        setMounted(false);
        setPosition(null);
      }, 150);
      return () => clearTimeout(unmountTimer);
    }
    const rect = textRef.current.getBoundingClientRect();
    const computed = window.getComputedStyle(textRef.current);
    setTextStyle({
      fontSize: computed.fontSize,
      fontWeight: computed.fontWeight,
      fontFamily: computed.fontFamily,
      letterSpacing: computed.letterSpacing,
      lineHeight: computed.lineHeight
    });
    const viewportWidth = window.innerWidth;
    const top = roundToDevicePixel(rect.top - PILL_PADDING_Y);
    if (align === "right") {
      setPosition({
        top,
        right: roundToDevicePixel(viewportWidth - rect.right - PILL_PADDING_X),
        maxWidth: Math.floor(rect.right + PILL_PADDING_X - VIEWPORT_MARGIN)
      });
    } else {
      setPosition({
        top,
        left: roundToDevicePixel(rect.left - PILL_PADDING_X),
        maxWidth: Math.floor(viewportWidth - rect.left + PILL_PADDING_X - VIEWPORT_MARGIN)
      });
    }
    setMounted(true);
  }, [visible, align]);
  function handleMouseEnter() {
    if (!shouldShow) return;
    if (delay === 0) {
      setVisible(true);
    } else {
      timerRef.current = setTimeout(() => setVisible(true), delay);
    }
  }
  function handleMouseLeave() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }
  const pillContent = hoverContent ?? children;
  const pill = mounted && position && /* @__PURE__ */ jsx50(
    "span",
    {
      "data-slot": "overflow-reveal",
      className: cn(
        "pointer-events-none fixed z-50 transition-opacity duration-100 ease-out",
        visible && position ? "opacity-100" : "opacity-0"
      ),
      style: {
        top: position.top,
        left: position.left,
        right: position.right
      },
      children: /* @__PURE__ */ jsx50(
        "span",
        {
          className: cn(
            "inline-block bg-popover px-2.5 py-1.5 text-secondary-foreground shadow-sm ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_4px_12px_-2px_rgb(0_0_0/0.4)]",
            nowrap ? "whitespace-nowrap rounded-full" : "text-pretty rounded-xl"
          ),
          style: {
            maxWidth: nowrap ? void 0 : position.maxWidth,
            ...textStyle
          },
          children: pillContent
        }
      )
    }
  );
  return /* @__PURE__ */ jsxs20(
    "span",
    {
      "data-slot": "overflow",
      className: cn(
        "relative inline-flex max-w-full",
        shouldShow && "cursor-pointer",
        className
      ),
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ...props,
      children: [
        /* @__PURE__ */ jsx50(
          "span",
          {
            ref: textRef,
            "data-slot": "overflow-text",
            className: "invisible truncate",
            "aria-hidden": "true",
            children
          }
        ),
        /* @__PURE__ */ jsx50(
          "span",
          {
            "data-slot": "overflow-visible-text",
            className: cn(
              "absolute inset-0 truncate transition-opacity duration-100",
              visible ? "opacity-0" : "opacity-100"
            ),
            children
          }
        ),
        typeof document !== "undefined" && pill && createPortal(pill, document.body)
      ]
    }
  );
}

// src/components/ui/pagination.tsx
import {
  ChevronLeftIcon as ChevronLeftIcon2,
  ChevronRightIcon as ChevronRightIcon5,
  MoreHorizontalIcon
} from "lucide-react";
import { jsx as jsx51, jsxs as jsxs21 } from "react/jsx-runtime";
function Pagination({ className, ...props }) {
  return /* @__PURE__ */ jsx51(
    "nav",
    {
      role: "navigation",
      "aria-label": "pagination",
      "data-slot": "pagination",
      className: cn("mx-auto flex w-full justify-center", className),
      ...props
    }
  );
}
function PaginationContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx51(
    "ul",
    {
      "data-slot": "pagination-content",
      className: cn("flex flex-row items-center gap-1", className),
      ...props
    }
  );
}
function PaginationItem({ ...props }) {
  return /* @__PURE__ */ jsx51("li", { "data-slot": "pagination-item", ...props });
}
function PaginationLink({
  className,
  isActive,
  size = "icon",
  ...props
}) {
  return /* @__PURE__ */ jsx51(
    "a",
    {
      "aria-current": isActive ? "page" : void 0,
      "data-slot": "pagination-link",
      "data-active": isActive,
      className: cn(
        buttonVariants({
          variant: isActive ? "secondary" : "muted",
          size
        }),
        className
      ),
      ...props
    }
  );
}
function PaginationPrevious({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs21(
    PaginationLink,
    {
      "aria-label": "Go to previous page",
      size: "default",
      className: cn("gap-1 px-2.5 sm:pl-2.5", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx51(ChevronLeftIcon2, {}),
        /* @__PURE__ */ jsx51("span", { className: "hidden sm:block", children: "Previous" })
      ]
    }
  );
}
function PaginationNext({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs21(
    PaginationLink,
    {
      "aria-label": "Go to next page",
      size: "default",
      className: cn("gap-1 px-2.5 sm:pr-2.5", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx51("span", { className: "hidden sm:block", children: "Next" }),
        /* @__PURE__ */ jsx51(ChevronRightIcon5, {})
      ]
    }
  );
}
function PaginationEllipsis({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs21(
    "span",
    {
      "aria-hidden": true,
      "data-slot": "pagination-ellipsis",
      className: cn("flex size-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx51(MoreHorizontalIcon, { className: "size-4" }),
        /* @__PURE__ */ jsx51("span", { className: "sr-only", children: "More pages" })
      ]
    }
  );
}

// src/components/animate-ui/primitives/radix/popover.tsx
import * as React21 from "react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence8, motion as motion9 } from "motion/react";
import { jsx as jsx52 } from "react/jsx-runtime";
var [PopoverProvider, usePopover] = getStrictContext("PopoverContext");
function Popover(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx52(PopoverProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx52(
    PopoverPrimitive.Root,
    {
      "data-slot": "popover",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function PopoverTrigger(props) {
  return /* @__PURE__ */ jsx52(PopoverPrimitive.Trigger, { "data-slot": "popover-trigger", ...props });
}
function PopoverPortal(props) {
  return /* @__PURE__ */ jsx52(PopoverPrimitive.Portal, { forceMount: true, "data-slot": "popover-portal", ...props });
}
var PopoverContent = React21.forwardRef(
  ({
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    align,
    alignOffset,
    side,
    sideOffset,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    arrowPadding,
    sticky,
    hideWhenDetached,
    transition = { duration: 0.1 },
    style,
    ...props
  }, ref) => {
    const { isOpen } = usePopover();
    return /* @__PURE__ */ jsx52(AnimatePresence8, { children: isOpen && /* @__PURE__ */ jsx52(
      PopoverPrimitive.Content,
      {
        asChild: true,
        forceMount: true,
        align,
        alignOffset,
        side,
        sideOffset,
        avoidCollisions,
        collisionBoundary,
        collisionPadding,
        arrowPadding,
        sticky,
        hideWhenDetached,
        onOpenAutoFocus,
        onCloseAutoFocus,
        onEscapeKeyDown,
        onPointerDownOutside,
        onInteractOutside,
        onFocusOutside,
        children: /* @__PURE__ */ jsx52(
          motion9.div,
          {
            ref,
            "data-slot": "popover-content",
            initial: { opacity: 0, scale: 0.98 },
            animate: { opacity: 1, scale: 1 },
            exit: { opacity: 0, scale: 0.98 },
            transition,
            style: { willChange: "opacity, transform", ...style },
            ...props
          },
          "popover-content"
        )
      }
    ) });
  }
);
PopoverContent.displayName = "PopoverContent";
function PopoverAnchor({ ...props }) {
  return /* @__PURE__ */ jsx52(PopoverPrimitive.Anchor, { "data-slot": "popover-anchor", ...props });
}

// src/components/ui/popover.tsx
import { jsx as jsx53 } from "react/jsx-runtime";
function Popover2({
  ...props
}) {
  return /* @__PURE__ */ jsx53(Popover, { ...props });
}
function PopoverTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx53(PopoverTrigger, { "data-slot": "popover-trigger", ...props });
}
function PopoverContent2({
  className,
  align = "center",
  sideOffset = 2,
  ...props
}) {
  return /* @__PURE__ */ jsx53(PopoverPortal, { children: /* @__PURE__ */ jsx53(
    PopoverContent,
    {
      "data-slot": "popover-content",
      align,
      sideOffset,
      className: cn(
        "z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-lg bg-popover p-4 text-secondary-foreground shadow-md ring-1 ring-black/5 outline-hidden dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props
    }
  ) });
}
function PopoverAnchor2({
  ...props
}) {
  return /* @__PURE__ */ jsx53(PopoverAnchor, { "data-slot": "popover-anchor", ...props });
}
function PopoverHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx53(
    "div",
    {
      "data-slot": "popover-header",
      className: cn("flex flex-col gap-1 text-sm", className),
      ...props
    }
  );
}
function PopoverTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx53(
    "div",
    {
      "data-slot": "popover-title",
      className: cn("font-medium", className),
      ...props
    }
  );
}
function PopoverDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx53(
    "p",
    {
      "data-slot": "popover-description",
      className: cn("text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/platform-switcher.tsx
import * as React25 from "react";
import { AnimatePresence as AnimatePresence10, motion as motion12 } from "motion/react";
import { Check as Check2, ChevronDown, LoaderCircle } from "lucide-react";

// src/components/ui/sidebar.tsx
import * as React23 from "react";
import { cva as cva11 } from "class-variance-authority";
import { PanelLeftIcon } from "lucide-react";
import { Slot as Slot7 } from "radix-ui";

// src/components/ui/sheet.tsx
import { XIcon as XIcon3 } from "lucide-react";

// src/components/animate-ui/primitives/radix/sheet.tsx
import { Dialog as SheetPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence9, motion as motion10 } from "motion/react";
import { jsx as jsx54 } from "react/jsx-runtime";
var [SheetProvider, useSheet] = getStrictContext("SheetContext");
function Sheet(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props.open,
    defaultValue: props.defaultOpen,
    onChange: props.onOpenChange
  });
  return /* @__PURE__ */ jsx54(SheetProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx54(SheetPrimitive.Root, { "data-slot": "sheet", ...props, onOpenChange: setIsOpen }) });
}
function SheetTrigger(props) {
  return /* @__PURE__ */ jsx54(SheetPrimitive.Trigger, { "data-slot": "sheet-trigger", ...props });
}
function SheetClose(props) {
  return /* @__PURE__ */ jsx54(SheetPrimitive.Close, { "data-slot": "sheet-close", ...props });
}
function SheetPortal(props) {
  const { isOpen } = useSheet();
  return /* @__PURE__ */ jsx54(AnimatePresence9, { children: isOpen && /* @__PURE__ */ jsx54(SheetPrimitive.Portal, { forceMount: true, "data-slot": "sheet-portal", ...props }) });
}
var OVERLAY_ENTER = { duration: 0.2, ease: [0.32, 0.72, 0, 1] };
var OVERLAY_EXIT = { duration: 0.15, ease: [0.4, 0, 1, 1] };
function SheetOverlay({ transition, ...props }) {
  return /* @__PURE__ */ jsx54(SheetPrimitive.Overlay, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx54(
    motion10.div,
    {
      "data-slot": "sheet-overlay",
      initial: { opacity: 0, filter: "blur(4px)" },
      animate: { opacity: 1, filter: "blur(0px)", transition: transition ?? OVERLAY_ENTER },
      exit: { opacity: 0, filter: "blur(4px)", transition: transition ?? OVERLAY_EXIT },
      style: { willChange: "opacity, filter" },
      ...props
    },
    "sheet-overlay"
  ) });
}
var CONTENT_ENTER = { duration: 0.28, ease: [0.32, 0.72, 0, 1] };
var CONTENT_EXIT = { duration: 0.18, ease: [0.4, 0, 1, 1] };
function SheetContent({
  side = "right",
  transition,
  style,
  children,
  ...props
}) {
  const axis = side === "left" || side === "right" ? "x" : "y";
  const offscreen = {
    right: { x: "100%", opacity: 0 },
    left: { x: "-100%", opacity: 0 },
    top: { y: "-100%", opacity: 0 },
    bottom: { y: "100%", opacity: 0 }
  };
  const positionStyle = {
    right: { top: 0, bottom: 0, right: 0 },
    left: { top: 0, bottom: 0, left: 0 },
    top: { left: 0, right: 0, top: 0 },
    bottom: { left: 0, right: 0, bottom: 0 }
  };
  const animate = {
    [axis]: 0,
    opacity: 1,
    transition: transition ?? CONTENT_ENTER
  };
  const exit = {
    ...offscreen[side],
    transition: transition ?? CONTENT_EXIT
  };
  return /* @__PURE__ */ jsx54(SheetPrimitive.Content, { asChild: true, forceMount: true, ...props, children: /* @__PURE__ */ jsx54(
    motion10.div,
    {
      "data-slot": "sheet-content",
      "data-side": side,
      initial: offscreen[side],
      animate,
      exit,
      style: {
        position: "fixed",
        willChange: "transform, opacity",
        ...positionStyle[side],
        ...style
      },
      children
    },
    "sheet-content"
  ) });
}
function SheetTitle(props) {
  return /* @__PURE__ */ jsx54(SheetPrimitive.Title, { "data-slot": "sheet-title", ...props });
}
function SheetDescription(props) {
  return /* @__PURE__ */ jsx54(SheetPrimitive.Description, { "data-slot": "sheet-description", ...props });
}

// src/components/ui/sheet.tsx
import { jsx as jsx55, jsxs as jsxs22 } from "react/jsx-runtime";
function Sheet2({ ...props }) {
  return /* @__PURE__ */ jsx55(Sheet, { ...props });
}
function SheetTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx55(SheetTrigger, { ...props });
}
function SheetClose2({
  ...props
}) {
  return /* @__PURE__ */ jsx55(SheetClose, { ...props });
}
function SheetPortal2({
  ...props
}) {
  return /* @__PURE__ */ jsx55(SheetPortal, { ...props });
}
function SheetOverlay2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx55(
    SheetOverlay,
    {
      className: cn(
        "fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
function SheetContent2({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs22(SheetPortal2, { children: [
    /* @__PURE__ */ jsx55(SheetOverlay2, {}),
    /* @__PURE__ */ jsxs22(
      SheetContent,
      {
        side,
        className: cn(
          "z-50 flex flex-col gap-4 bg-background shadow-lg dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
          side === "right" && "fixed inset-y-0 right-0 w-3/4 border-l sm:max-w-sm after:absolute after:inset-y-0 after:-right-8 after:w-8 after:bg-background",
          side === "left" && "fixed inset-y-0 left-0 w-3/4 border-r sm:max-w-sm after:absolute after:inset-y-0 after:-left-8 after:w-8 after:bg-background",
          side === "top" && "h-auto border-b after:absolute after:inset-x-0 after:-top-8 after:h-8 after:bg-background",
          side === "bottom" && "h-auto border-t pb-4 after:absolute after:inset-x-0 after:-bottom-8 after:h-8 after:bg-background",
          className
        ),
        ...props,
        children: [
          children,
          showCloseButton && /* @__PURE__ */ jsxs22(SheetClose, { className: "absolute top-4 right-4 inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground focus:outline-hidden dark:hover:bg-white/[0.08]", children: [
            /* @__PURE__ */ jsx55(XIcon3, { className: "size-3.5" }),
            /* @__PURE__ */ jsx55("span", { className: "sr-only", children: "Close" })
          ] })
        ]
      }
    )
  ] });
}
function SheetHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx55(
    "div",
    {
      "data-slot": "sheet-header",
      className: cn("flex flex-col gap-1.5 p-4", className),
      ...props
    }
  );
}
function SheetFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx55(
    "div",
    {
      "data-slot": "sheet-footer",
      className: cn("mt-auto flex flex-col gap-2 p-4", className),
      ...props
    }
  );
}
function SheetTitle2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx55(
    SheetTitle,
    {
      className: cn("font-semibold text-foreground", className),
      ...props
    }
  );
}
function SheetDescription2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx55(
    SheetDescription,
    {
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/scroll-area.tsx
import * as React22 from "react";
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui";
import { jsx as jsx56, jsxs as jsxs23 } from "react/jsx-runtime";
var ScrollArea = React22.forwardRef(({ className, children, fadeEdges = false, ...props }, forwardedRef) => {
  const [showTopFade, setShowTopFade] = React22.useState(false);
  const [showBottomFade, setShowBottomFade] = React22.useState(false);
  const cleanupRef = React22.useRef(null);
  const rootCallbackRef = React22.useCallback(
    (root) => {
      if (typeof forwardedRef === "function") {
        forwardedRef(root);
      } else if (forwardedRef) {
        forwardedRef.current = root;
      }
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (!fadeEdges || !root) {
        setShowTopFade(false);
        setShowBottomFade(false);
        return;
      }
      const viewport = root.querySelector(
        "[data-slot='scroll-area-viewport']"
      );
      if (!viewport) return;
      let rafId = 0;
      let lastTop = false;
      let lastBottom = false;
      const commit = () => {
        rafId = 0;
        const { scrollTop, scrollHeight, clientHeight } = viewport;
        const top = scrollTop > 2;
        const bottom = scrollTop + clientHeight < scrollHeight - 2;
        if (top !== lastTop) {
          lastTop = top;
          setShowTopFade(top);
        }
        if (bottom !== lastBottom) {
          lastBottom = bottom;
          setShowBottomFade(bottom);
        }
      };
      const schedule = () => {
        if (rafId === 0) rafId = requestAnimationFrame(commit);
      };
      commit();
      viewport.addEventListener("scroll", schedule, { passive: true });
      const observer = new ResizeObserver(schedule);
      observer.observe(viewport);
      cleanupRef.current = () => {
        viewport.removeEventListener("scroll", schedule);
        observer.disconnect();
        if (rafId !== 0) cancelAnimationFrame(rafId);
      };
    },
    [fadeEdges, forwardedRef]
  );
  const fadeStyle = fadeEdges ? {
    "--sa-top-fade": showTopFade ? 1 : 0,
    "--sa-bottom-fade": showBottomFade ? 1 : 0
  } : void 0;
  return /* @__PURE__ */ jsxs23(
    ScrollAreaPrimitive.Root,
    {
      ref: rootCallbackRef,
      "data-slot": "scroll-area",
      "data-fade-edges": fadeEdges ? "true" : void 0,
      className: cn("relative overflow-hidden rounded-[8px]", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx56(
          ScrollAreaPrimitive.Viewport,
          {
            "data-slot": "scroll-area-viewport",
            className: "h-full max-h-[inherit] w-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1",
            style: fadeStyle,
            children
          }
        ),
        /* @__PURE__ */ jsx56(ScrollBar, {}),
        /* @__PURE__ */ jsx56(ScrollAreaPrimitive.Corner, {})
      ]
    }
  );
});
ScrollArea.displayName = "ScrollArea";
function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}) {
  return /* @__PURE__ */ jsx56(
    ScrollAreaPrimitive.ScrollAreaScrollbar,
    {
      "data-slot": "scroll-area-scrollbar",
      orientation,
      className: cn(
        "flex touch-none p-px transition-colors select-none",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx56(
        ScrollAreaPrimitive.ScrollAreaThumb,
        {
          "data-slot": "scroll-area-thumb",
          className: "relative flex-1 rounded-full bg-border"
        }
      )
    }
  );
}

// src/components/ui/skeleton.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx57(
    "div",
    {
      "data-slot": "skeleton",
      className: cn("animate-pulse rounded-md bg-gray-200 dark:bg-gray-700", className),
      ...props
    }
  );
}

// src/components/ui/sidebar.tsx
import { Fragment as Fragment2, jsx as jsx58, jsxs as jsxs24 } from "react/jsx-runtime";
var SIDEBAR_COOKIE_NAME = "sidebar_state";
var SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
var SIDEBAR_WIDTH = "264px";
var SIDEBAR_WIDTH_MOBILE = "18rem";
var SIDEBAR_KEYBOARD_SHORTCUT = "b";
var SidebarContext = React23.createContext(null);
function useSidebar() {
  const context = React23.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}
function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React23.useState(false);
  const [_open, _setOpen] = React23.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React23.useCallback(
    (value) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );
  const toggleSidebar = React23.useCallback(() => {
    if (isMobile) {
      setOpenMobile((prev) => !prev);
    } else if (open) {
      setOpen(false);
    } else {
      setOpenMobile((prev) => !prev);
    }
  }, [isMobile, open, setOpen, setOpenMobile]);
  React23.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        if (isMobile) {
          setOpenMobile((prev) => !prev);
        } else {
          setOpen((prev) => !prev);
          setOpenMobile(false);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobile, setOpen, setOpenMobile]);
  const state = open ? "expanded" : "collapsed";
  const contextValue = React23.useMemo(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );
  return /* @__PURE__ */ jsx58(SidebarContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx58(
    "div",
    {
      "data-slot": "sidebar-wrapper",
      style: {
        "--sidebar-width": SIDEBAR_WIDTH,
        ...style
      },
      className: cn(
        "group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar",
        className
      ),
      ...props,
      children
    }
  ) });
}
function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}) {
  const { isMobile, open, state, openMobile, setOpenMobile } = useSidebar();
  if (collapsible === "none") {
    return /* @__PURE__ */ jsx58(
      "div",
      {
        "data-slot": "sidebar",
        className: cn(
          "flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground",
          className
        ),
        ...props,
        children
      }
    );
  }
  const sheet = /* @__PURE__ */ jsx58(Sheet2, { open: openMobile, onOpenChange: setOpenMobile, children: /* @__PURE__ */ jsxs24(
    SheetContent2,
    {
      "data-sidebar": "sidebar",
      "data-slot": "sidebar",
      "data-mobile": isMobile ? "true" : void 0,
      className: "w-(--sidebar-width) border-0 bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden before:absolute before:inset-y-0 before:-left-16 before:w-16 before:bg-sidebar shadow-[0_-1px_0_rgb(0_0_0/0.15),0_1px_0_rgb(0_0_0/0.15),1px_0_0_rgb(0_0_0/0.15)] dark:shadow-[0_-1px_0_rgb(255_255_255/0.12),0_1px_0_rgb(255_255_255/0.12),1px_0_0_rgb(255_255_255/0.12)]",
      style: {
        "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
        top: "env(safe-area-inset-top)",
        bottom: "env(safe-area-inset-bottom)"
      },
      side,
      children: [
        /* @__PURE__ */ jsxs24(SheetHeader, { className: "sr-only", children: [
          /* @__PURE__ */ jsx58(SheetTitle2, { children: "Sidebar" }),
          /* @__PURE__ */ jsx58(SheetDescription2, { children: "Displays the sidebar." })
        ] }),
        /* @__PURE__ */ jsx58("div", { className: "flex h-full w-full flex-col", children })
      ]
    }
  ) });
  if (isMobile) {
    return sheet;
  }
  return /* @__PURE__ */ jsxs24(Fragment2, { children: [
    /* @__PURE__ */ jsx58(
      "div",
      {
        className: "group peer hidden text-sidebar-foreground lg:block",
        "data-state": state,
        "data-variant": variant,
        "data-side": side,
        "data-slot": "sidebar",
        ...props,
        children: open && /* @__PURE__ */ jsxs24(Fragment2, { children: [
          /* @__PURE__ */ jsx58(
            "div",
            {
              "data-slot": "sidebar-gap",
              className: cn(
                "relative w-(--sidebar-width) bg-transparent",
                "group-data-[side=right]:rotate-180"
              )
            }
          ),
          /* @__PURE__ */ jsx58(
            "div",
            {
              "data-slot": "sidebar-container",
              className: cn(
                "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) lg:flex",
                side === "left" ? "left-0" : "right-0",
                // Adjust the padding for floating and inset variants.
                variant === "floating" || variant === "inset" ? "p-2" : "group-data-[side=left]:border-r group-data-[side=right]:border-l",
                className
              ),
              children: /* @__PURE__ */ jsx58(
                "div",
                {
                  "data-sidebar": "sidebar",
                  "data-slot": "sidebar-inner",
                  className: "flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-sm",
                  children
                }
              )
            }
          )
        ] })
      }
    ),
    !open && sheet
  ] });
}
function SidebarTrigger({
  className,
  onClick,
  ...props
}) {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsxs24(
    Button,
    {
      type: "button",
      "data-sidebar": "trigger",
      "data-slot": "sidebar-trigger",
      variant: "muted",
      size: "icon",
      className: cn("size-7", className),
      onClick: (event) => {
        onClick?.(event);
        toggleSidebar();
      },
      ...props,
      children: [
        /* @__PURE__ */ jsx58(PanelLeftIcon, {}),
        /* @__PURE__ */ jsx58("span", { className: "sr-only", children: "Toggle Sidebar" })
      ]
    }
  );
}
function SidebarRail({ className, ...props }) {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsx58(
    "button",
    {
      "data-sidebar": "rail",
      "data-slot": "sidebar-rail",
      "aria-label": "Toggle Sidebar",
      tabIndex: -1,
      onClick: toggleSidebar,
      title: "Toggle Sidebar",
      className: cn(
        "absolute inset-y-0 z-20 hidden w-4 translate-x-0 transition-all ease-linear group-data-[side=left]:-right-2 group-data-[side=right]:-left-2 after:absolute after:inset-y-0 after:left-full hover:bg-sidebar sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        className
      ),
      ...props
    }
  );
}
function SidebarInset({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx58(
    "main",
    {
      "data-slot": "sidebar-inset",
      className: cn(
        "relative flex w-full flex-1 flex-col bg-background",
        "lg:peer-data-[variant=inset]:m-2 lg:peer-data-[variant=inset]:ml-0 lg:peer-data-[variant=inset]:rounded-lg lg:peer-data-[variant=inset]:shadow-[0_0_0_1px_oklch(0.150_0.014_264.0/0.05),0_1px_2px_oklch(0.150_0.014_264.0/0.1)] lg:peer-data-[variant=inset]:overflow-hidden lg:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 dark:lg:peer-data-[variant=inset]:shadow-[0_0_0_1px_oklch(1_0_0/0.1),0_0_0_0.5px_oklch(1_0_0/0.05)]",
        className
      ),
      ...props,
      children
    }
  );
}
function SidebarInput({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx58(
    Input,
    {
      "data-slot": "sidebar-input",
      "data-sidebar": "input",
      className: cn("h-8 w-full bg-background shadow-none", className),
      ...props
    }
  );
}
function SidebarHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx58(
    "div",
    {
      "data-slot": "sidebar-header",
      "data-sidebar": "header",
      className: cn(
        "flex flex-col gap-2 p-2 [&>*:first-child]:pr-0",
        className
      ),
      ...props
    }
  );
}
function SidebarFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx58(
    "div",
    {
      "data-slot": "sidebar-footer",
      "data-sidebar": "footer",
      className: cn("flex flex-col gap-2", className),
      ...props
    }
  );
}
function SidebarSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx58("div", { className: "px-1", "data-slot": "sidebar-separator-wrapper", children: /* @__PURE__ */ jsx58(
    Separator,
    {
      "data-slot": "sidebar-separator",
      "data-sidebar": "separator",
      className: cn("bg-sidebar-border", className),
      ...props
    }
  ) });
}
function SidebarContent({ className, children }) {
  return /* @__PURE__ */ jsx58(
    ScrollArea,
    {
      fadeEdges: true,
      "data-slot": "sidebar-content",
      "data-sidebar": "content",
      className: cn(
        "min-h-0 flex-1 overflow-hidden rounded-none",
        className
      ),
      children: /* @__PURE__ */ jsx58("div", { className: "flex flex-col gap-2", children })
    }
  );
}
function SidebarGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx58(
    "div",
    {
      "data-slot": "sidebar-group",
      "data-sidebar": "group",
      className: cn("relative flex w-full min-w-0 flex-col p-2", className),
      ...props
    }
  );
}
function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot7.Root : "div";
  return /* @__PURE__ */ jsx58(
    Comp,
    {
      "data-slot": "sidebar-group-label",
      "data-sidebar": "group-label",
      className: cn(
        "flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 ring-sidebar-ring outline-hidden focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        className
      ),
      ...props
    }
  );
}
function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot7.Root : "button";
  return /* @__PURE__ */ jsx58(
    Comp,
    {
      "data-slot": "sidebar-group-action",
      "data-sidebar": "group-action",
      className: cn(
        "absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-[transform,opacity] duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 lg:after:hidden",
        className
      ),
      ...props
    }
  );
}
function SidebarGroupContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx58(
    "div",
    {
      "data-slot": "sidebar-group-content",
      "data-sidebar": "group-content",
      className: cn("w-full text-sm", className),
      ...props
    }
  );
}
function SidebarMenu({ className, ...props }) {
  return /* @__PURE__ */ jsx58(
    "ul",
    {
      "data-slot": "sidebar-menu",
      "data-sidebar": "menu",
      className: cn("flex w-full min-w-0 flex-col gap-1", className),
      ...props
    }
  );
}
function SidebarMenuItem({ className, ...props }) {
  return /* @__PURE__ */ jsx58(
    "li",
    {
      "data-slot": "sidebar-menu-item",
      "data-sidebar": "menu-item",
      className: cn("group/menu-item relative", className),
      ...props
    }
  );
}
var sidebarMenuButtonVariants = cva11(
  "peer/menu-button relative flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm font-medium ring-sidebar-ring outline-hidden transition-[background-color,color] duration-150 group-has-data-[sidebar=menu-action]/menu-item:pr-8 [&>span]:text-gray-600 dark:[&>span]:text-sidebar-foreground/80 [&_svg]:[--icon-color:var(--color-gray-500)]! dark:[&_svg]:[--icon-color:hsl(var(--sidebar-icon))] [&_svg]:[--icon-background:hsl(var(--sidebar))] hover:bg-gray-100 dark:hover:bg-gray-800 hover:[&_svg]:[--icon-background:var(--color-gray-100)] dark:hover:[&_svg]:[--icon-background:var(--color-gray-800)] focus-visible:ring-2 active:bg-gray-100 dark:active:bg-gray-800 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:hover:bg-sidebar-accent data-[active=true]:[&>span]:text-sidebar-accent-foreground! data-[active=true]:[&_svg]:[--icon-color:var(--color-sidebar-accent-foreground)]! data-[active=true]:[&_svg]:[--icon-background:hsl(var(--sidebar-accent))] data-[active=true]:hover:[&_svg]:[--icon-background:hsl(var(--sidebar-accent))] data-[state=open]:hover:bg-gray-100 dark:data-[state=open]:hover:bg-gray-800 data-[suppress-active=true]:bg-transparent data-[suppress-active=true]:active:bg-transparent data-[suppress-active=true]:hover:bg-gray-100 dark:data-[suppress-active=true]:hover:bg-gray-800 data-[active-parent=true]:hover:bg-transparent! dark:data-[active-parent=true]:hover:bg-transparent! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&_[data-slot=dropdown-chevron]]:text-gray-400 [&_[data-slot=dropdown-chevron]]:transition-colors [&_[data-slot=dropdown-chevron]]:duration-150 hover:[&_[data-slot=dropdown-chevron]]:text-gray-500",
  {
    variants: {
      variant: {
        default: "hover:bg-gray-100 dark:hover:bg-gray-800",
        outline: "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-gray-100 dark:hover:bg-gray-800 hover:shadow-[0_0_0_1px_var(--color-gray-100)] dark:hover:shadow-[0_0_0_1px_var(--color-gray-800)]"
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var SidebarMenuButton = React23.forwardRef(
  ({
    asChild = false,
    isActive = false,
    variant = "default",
    size = "default",
    className,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot7.Root : "button";
    return /* @__PURE__ */ jsx58(
      Comp,
      {
        ref,
        "data-slot": "sidebar-menu-button",
        "data-sidebar": "menu-button",
        "data-size": size,
        "data-active": isActive,
        className: cn(sidebarMenuButtonVariants({ variant, size }), className),
        ...props
      }
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";
function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}) {
  const Comp = asChild ? Slot7.Root : "button";
  return /* @__PURE__ */ jsx58(
    Comp,
    {
      "data-slot": "sidebar-menu-action",
      "data-sidebar": "menu-action",
      className: cn(
        "absolute top-1.5 right-1 flex aspect-square w-5 cursor-pointer items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-[transform,opacity] duration-200 peer-hover/menu-button:text-sidebar-accent-foreground hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-50 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-gray-400 [&>svg]:transition-colors [&>svg]:duration-150 hover:[&>svg]:text-gray-500",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 lg:after:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        showOnHover && "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground data-[state=open]:opacity-100 lg:opacity-0",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuBadge({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx58(
    "div",
    {
      "data-slot": "sidebar-menu-badge",
      "data-sidebar": "menu-badge",
      className: cn(
        "pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-sidebar-foreground tabular-nums select-none",
        "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}) {
  const [width] = React23.useState(() => `${Math.floor(Math.random() * 40) + 50}%`);
  return /* @__PURE__ */ jsxs24(
    "div",
    {
      "data-slot": "sidebar-menu-skeleton",
      "data-sidebar": "menu-skeleton",
      className: cn("flex h-8 items-center gap-2 rounded-md px-2", className),
      ...props,
      children: [
        showIcon && /* @__PURE__ */ jsx58(
          Skeleton,
          {
            className: "size-4 rounded-md",
            "data-sidebar": "menu-skeleton-icon"
          }
        ),
        /* @__PURE__ */ jsx58(
          Skeleton,
          {
            className: "h-4 max-w-(--skeleton-width) flex-1",
            "data-sidebar": "menu-skeleton-text",
            style: {
              "--skeleton-width": width
            }
          }
        )
      ]
    }
  );
}
function SidebarMenuSub({ className, ...props }) {
  return /* @__PURE__ */ jsx58(
    "ul",
    {
      "data-slot": "sidebar-menu-sub",
      "data-sidebar": "menu-sub",
      className: cn(
        "ml-3.5 mr-0 flex min-w-0 translate-x-px flex-col gap-1 pl-3 pr-0.5 py-0.5",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuSubItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx58(
    "li",
    {
      "data-slot": "sidebar-menu-sub-item",
      "data-sidebar": "menu-sub-item",
      className: cn("group/menu-sub-item relative", className),
      ...props
    }
  );
}
function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}) {
  const Comp = asChild ? Slot7.Root : "a";
  return /* @__PURE__ */ jsx58(
    Comp,
    {
      "data-slot": "sidebar-menu-sub-button",
      "data-sidebar": "menu-sub-button",
      "data-size": size,
      "data-active": isActive,
      className: cn(
        "relative flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 font-medium text-sidebar-foreground/60 ring-sidebar-ring outline-hidden transition-[background-color,color] duration-150 hover:bg-gray-100 dark:hover:bg-gray-800 focus-visible:ring-2 active:bg-gray-100 dark:active:bg-gray-800 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:hover:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        className
      ),
      ...props
    }
  );
}

// src/components/animate-ui/primitives/radix/switch.tsx
import * as React24 from "react";
import { Switch as SwitchPrimitives } from "radix-ui";
import {
  motion as motion11
} from "motion/react";
import { jsx as jsx59 } from "react/jsx-runtime";
var [SwitchProvider, useSwitch] = getStrictContext("SwitchContext");
function Switch(props) {
  const [isPressed, setIsPressed] = React24.useState(false);
  const [isChecked, setIsChecked] = useControlledState({
    value: props.checked,
    defaultValue: props.defaultChecked,
    onChange: props.onCheckedChange
  });
  return /* @__PURE__ */ jsx59(
    SwitchProvider,
    {
      value: { isChecked, setIsChecked, isPressed, setIsPressed },
      children: /* @__PURE__ */ jsx59(SwitchPrimitives.Root, { ...props, onCheckedChange: setIsChecked, asChild: true, children: /* @__PURE__ */ jsx59(
        motion11.button,
        {
          "data-slot": "switch",
          whileTap: "tap",
          initial: false,
          onTapStart: () => setIsPressed(true),
          onTapCancel: () => setIsPressed(false),
          onTap: () => setIsPressed(false),
          ...props
        }
      ) })
    }
  );
}
function SwitchThumb({
  pressedAnimation,
  transition = { type: "spring", stiffness: 300, damping: 25 },
  ...props
}) {
  const { isPressed } = useSwitch();
  return /* @__PURE__ */ jsx59(SwitchPrimitives.Thumb, { asChild: true, children: /* @__PURE__ */ jsx59(
    motion11.div,
    {
      "data-slot": "switch-thumb",
      whileTap: "tab",
      layout: true,
      transition,
      animate: isPressed ? pressedAnimation : void 0,
      ...props
    }
  ) });
}

// src/components/ui/switch.tsx
import { jsx as jsx60 } from "react/jsx-runtime";
function Switch2({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx60(
    Switch,
    {
      "data-size": size,
      className: cn(
        "peer group/switch inline-flex shrink-0 items-center rounded-full border border-black/10 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-[1.15rem] data-[size=default]:w-8 data-[size=sm]:h-3.5 data-[size=sm]:w-6 data-[state=checked]:border-transparent data-[state=checked]:bg-primary data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-input/80 dark:data-[state=unchecked]:border-white/15 data-[state=checked]:justify-end data-[state=unchecked]:justify-start px-px",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx60(
        SwitchThumb,
        {
          className: cn(
            "pointer-events-none block rounded-full bg-background shadow-[0_0_0_0.5px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06)] group-data-[size=default]/switch:size-3.5 group-data-[size=sm]/switch:size-2.5 dark:shadow-[0_0_0_0.5px_rgba(255,255,255,0.1),0_1px_2px_rgba(0,0,0,0.2)] dark:data-[state=checked]:bg-primary-foreground dark:data-[state=unchecked]:bg-foreground"
          ),
          transition: { type: "spring", stiffness: 700, damping: 30 }
        }
      )
    }
  );
}

// src/components/ui/platform-switcher.tsx
import { Fragment as Fragment3, jsx as jsx61, jsxs as jsxs25 } from "react/jsx-runtime";
function getItemId(item) {
  return item.id ?? item.name;
}
function PlatformSwitcher({
  platforms,
  value,
  onValueChange,
  isLive,
  onLiveChange,
  liveLabels = { live: "Live", sandbox: "Sandbox" },
  renderIcon,
  groupLabel = "Platforms",
  className,
  onAcceptInvite,
  isLoading = false
}) {
  const { isMobile } = useSidebar();
  const [isOpen, setIsOpen] = React25.useState(false);
  const activePlatform = React25.useMemo(
    () => platforms.find((p) => getItemId(p) === value) ?? platforms[0],
    [platforms, value]
  );
  const hasToggle = isLive !== void 0 && onLiveChange !== void 0;
  return /* @__PURE__ */ jsx61(SidebarMenu, { children: /* @__PURE__ */ jsx61(SidebarMenuItem, { children: /* @__PURE__ */ jsxs25(DropdownMenu2, { open: isOpen, onOpenChange: setIsOpen, children: [
    /* @__PURE__ */ jsx61(DropdownMenuTrigger2, { asChild: true, children: /* @__PURE__ */ jsxs25(
      SidebarMenuButton,
      {
        className: cn(
          "h-auto gap-2 overflow-hidden rounded-lg border border-gray-200 shadow-none hover:bg-gray-50 hover:border-gray-300 data-[state=open]:bg-gray-50 dark:border-white/10 dark:hover:bg-white/[0.06] dark:hover:border-white/[0.15] dark:data-[state=open]:bg-white/[0.06] focus-visible:ring-0 focus-visible:bg-gray-50 focus-visible:border-gray-300 dark:focus-visible:bg-white/[0.06] dark:focus-visible:border-white/[0.15]",
          className
        ),
        children: [
          /* @__PURE__ */ jsx61("div", { className: "flex size-[18px] shrink-0 items-center justify-center", children: isLoading ? /* @__PURE__ */ jsx61(LoaderCircle, { className: "size-4 animate-spin text-gray-500" }) : renderIcon?.(activePlatform, true) }),
          /* @__PURE__ */ jsx61("div", { className: "grid flex-1 gap-px text-left text-sm leading-tight", children: /* @__PURE__ */ jsx61("span", { className: "relative overflow-hidden truncate font-medium text-gray-600 dark:text-sidebar-foreground/80", children: /* @__PURE__ */ jsx61(AnimatePresence10, { initial: false, children: /* @__PURE__ */ jsx61(
            motion12.span,
            {
              initial: { opacity: 0, x: -8 },
              animate: { opacity: isLoading ? 0.4 : 1, x: 0 },
              exit: { opacity: 0, x: 8, position: "absolute" },
              transition: { duration: 0.12, ease: "easeOut" },
              className: "block truncate",
              children: activePlatform.name
            },
            activePlatform.name
          ) }) }) }),
          /* @__PURE__ */ jsx61(
            ChevronDown,
            {
              "data-slot": "dropdown-chevron",
              className: cn(
                "size-4 shrink-0 transition-transform duration-200 ease-out",
                isOpen && "rotate-180"
              )
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs25(
      DropdownMenuContent2,
      {
        className: "min-w-72 max-w-md rounded-lg",
        side: "bottom",
        align: "start",
        sideOffset: 4,
        children: [
          hasToggle && /* @__PURE__ */ jsxs25(Fragment3, { children: [
            /* @__PURE__ */ jsxs25(
              "button",
              {
                type: "button",
                onClick: () => onLiveChange?.(!isLive),
                className: "flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-white/[0.06]",
                children: [
                  /* @__PURE__ */ jsx61("span", { className: "relative overflow-hidden text-sm font-medium", children: /* @__PURE__ */ jsx61(AnimatePresence10, { initial: false, children: /* @__PURE__ */ jsx61(
                    motion12.span,
                    {
                      initial: { opacity: 0, x: -8 },
                      animate: { opacity: 1, x: 0 },
                      exit: { opacity: 0, x: 8, position: "absolute" },
                      transition: { duration: 0.12, ease: "easeOut" },
                      className: "block",
                      children: isLive ? liveLabels.live : liveLabels.sandbox
                    },
                    isLive ? "live" : "sandbox"
                  ) }) }),
                  /* @__PURE__ */ jsx61(
                    Switch2,
                    {
                      checked: isLive,
                      tabIndex: -1,
                      className: cn(
                        "pointer-events-none",
                        "data-[state=unchecked]:bg-[oklch(0.750_0.155_40.5)]",
                        "data-[state=checked]:bg-[oklch(0.695_0.119_165.5)]",
                        "data-[state=checked]:border-[oklch(0.595_0.119_165.5)]"
                      )
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsx61(DropdownMenuSeparator2, {})
          ] }),
          platforms.some((p) => p.isInvite) && /* @__PURE__ */ jsxs25(Fragment3, { children: [
            /* @__PURE__ */ jsx61(DropdownMenuLabel2, { className: "text-xs text-muted-foreground", children: "Invitations" }),
            /* @__PURE__ */ jsx61("div", { className: "flex flex-col gap-1 mb-2", children: platforms.filter((p) => p.isInvite === true).map((platform) => {
              const itemId = getItemId(platform);
              return /* @__PURE__ */ jsx61(
                "div",
                {
                  className: "flex flex-col gap-2 rounded-md p-3 hover:bg-gray-50 dark:hover:bg-white/[0.06]",
                  children: /* @__PURE__ */ jsxs25("div", { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsxs25("div", { className: "grid flex-1 text-left text-sm leading-tight", children: [
                      /* @__PURE__ */ jsx61("span", { className: "font-medium", children: platform.name }),
                      platform.description && /* @__PURE__ */ jsx61("span", { className: "text-xs font-normal text-muted-foreground", children: platform.description })
                    ] }),
                    /* @__PURE__ */ jsx61(
                      "button",
                      {
                        type: "button",
                        onClick: (e) => {
                          e.stopPropagation();
                          onAcceptInvite?.(itemId);
                        },
                        className: "ml-auto inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md bg-white bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.7),rgb(255_255_255/0))] bg-blend-screen text-secondary-foreground shadow-btn-secondary inset-shadow-btn-secondary hover:bg-secondary-hover active:bg-secondary-active dark:bg-gray-800 dark:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.06),rgb(255_255_255/0))] dark:text-gray-100 dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:bg-secondary-hover dark:active:bg-secondary-active px-3 py-1.5 text-xs font-medium transition-all",
                        children: "Accept"
                      }
                    )
                  ] })
                },
                itemId
              );
            }) }),
            /* @__PURE__ */ jsx61(DropdownMenuSeparator2, {})
          ] }),
          /* @__PURE__ */ jsx61(DropdownMenuLabel2, { className: "text-xs text-muted-foreground", children: groupLabel }),
          /* @__PURE__ */ jsx61("div", { className: "flex flex-col gap-px", children: platforms.filter((p) => !p.isInvite).map((platform) => {
            const itemId = getItemId(platform);
            const isActive = itemId === getItemId(activePlatform);
            return /* @__PURE__ */ jsxs25(
              DropdownMenuItem2,
              {
                onClick: () => onValueChange(itemId),
                className: cn(
                  "gap-2 p-2",
                  isActive && "bg-gray-50 dark:bg-white/[0.06]"
                ),
                children: [
                  /* @__PURE__ */ jsxs25("div", { className: "grid flex-1 text-left text-sm leading-tight", children: [
                    /* @__PURE__ */ jsx61("span", { className: "font-medium", children: platform.name }),
                    platform.description && /* @__PURE__ */ jsx61("span", { className: "text-xs font-normal text-muted-foreground", children: platform.description })
                  ] }),
                  /* @__PURE__ */ jsx61(Check2, { className: cn("ml-auto size-4 shrink-0 box-content pl-4", !isActive && "invisible") })
                ]
              },
              itemId
            );
          }) })
        ]
      }
    )
  ] }) }) });
}

// src/components/ui/progress.tsx
import * as React26 from "react";

// src/components/animate-ui/primitives/radix/progress.tsx
import { Progress as ProgressPrimitive } from "radix-ui";
import { motion as motion13 } from "motion/react";
import { jsx as jsx62 } from "react/jsx-runtime";
var [ProgressProvider, useProgress] = getStrictContext("ProgressContext");
function Progress(props) {
  return /* @__PURE__ */ jsx62(ProgressProvider, { value: { value: props.value ?? 0 }, children: /* @__PURE__ */ jsx62(ProgressPrimitive.Root, { "data-slot": "progress", ...props }) });
}
var MotionProgressIndicator = motion13.create(ProgressPrimitive.Indicator);
function ProgressIndicator({
  transition = { type: "spring", stiffness: 100, damping: 30 },
  ...props
}) {
  const { value } = useProgress();
  return /* @__PURE__ */ jsx62(
    MotionProgressIndicator,
    {
      "data-slot": "progress-indicator",
      animate: { x: `-${100 - (value || 0)}%` },
      transition,
      ...props
    }
  );
}

// src/components/ui/progress.tsx
import { jsx as jsx63 } from "react/jsx-runtime";
var progressColors = {
  blue: {
    track: "oklch(0.573 0.146 261.4 / 0.2)",
    trackDark: "oklch(0.573 0.146 261.4 / 0.35)",
    indicator: "oklch(0.573 0.146 261.4)",
    indicatorDark: "oklch(0.725 0.107 259.7)",
    shadow: "inset 0 0 0 1px oklch(0.327 0.118 267.8 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.725 0.107 259.7 / 0.15)"
  },
  cyan: {
    track: "oklch(0.816 0.101 220.4 / 0.2)",
    trackDark: "oklch(0.816 0.101 220.4 / 0.3)",
    indicator: "oklch(0.816 0.101 220.4)",
    indicatorDark: "oklch(0.851 0.085 208.0)",
    shadow: "inset 0 0 0 1px oklch(0.495 0.106 238.3 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.851 0.085 208.0 / 0.15)"
  },
  seafoam: {
    track: "oklch(0.842 0.131 156.6 / 0.2)",
    trackDark: "oklch(0.842 0.131 156.6 / 0.3)",
    indicator: "oklch(0.842 0.131 156.6)",
    indicatorDark: "oklch(0.880 0.118 156.0)",
    shadow: "inset 0 0 0 1px oklch(0.533 0.093 177.4 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.880 0.118 156.0 / 0.15)"
  },
  green: {
    track: "oklch(0.818 0.168 131.2 / 0.2)",
    trackDark: "oklch(0.818 0.168 131.2 / 0.3)",
    indicator: "oklch(0.818 0.168 131.2)",
    indicatorDark: "oklch(0.861 0.139 126.8)",
    shadow: "inset 0 0 0 1px oklch(0.532 0.117 135.1 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.861 0.139 126.8 / 0.15)"
  },
  yellow: {
    track: "oklch(0.898 0.140 99.3 / 0.2)",
    trackDark: "oklch(0.898 0.140 99.3 / 0.3)",
    indicator: "oklch(0.898 0.140 99.3)",
    indicatorDark: "oklch(0.913 0.134 100.8)",
    shadow: "inset 0 0 0 1px oklch(0.618 0.131 71.3 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.913 0.134 100.8 / 0.15)"
  },
  orange: {
    track: "oklch(0.715 0.154 52.8 / 0.2)",
    trackDark: "oklch(0.715 0.154 52.8 / 0.35)",
    indicator: "oklch(0.715 0.154 52.8)",
    indicatorDark: "oklch(0.790 0.118 64.6)",
    shadow: "inset 0 0 0 1px oklch(0.455 0.141 39.0 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.790 0.118 64.6 / 0.15)"
  },
  red: {
    track: "oklch(0.599 0.184 12.9 / 0.2)",
    trackDark: "oklch(0.599 0.184 12.9 / 0.35)",
    indicator: "oklch(0.599 0.184 12.9)",
    indicatorDark: "oklch(0.697 0.159 10.5)",
    shadow: "inset 0 0 0 1px oklch(0.356 0.134 11.9 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.697 0.159 10.5 / 0.15)"
  },
  pink: {
    track: "oklch(0.575 0.178 325.3 / 0.2)",
    trackDark: "oklch(0.575 0.178 325.3 / 0.35)",
    indicator: "oklch(0.575 0.178 325.3)",
    indicatorDark: "oklch(0.690 0.155 323.8)",
    shadow: "inset 0 0 0 1px oklch(0.336 0.144 318.4 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.690 0.155 323.8 / 0.15)"
  },
  purple: {
    track: "oklch(0.583 0.183 294.0 / 0.2)",
    trackDark: "oklch(0.583 0.183 294.0 / 0.35)",
    indicator: "oklch(0.583 0.183 294.0)",
    indicatorDark: "oklch(0.666 0.169 296.2)",
    shadow: "inset 0 0 0 1px oklch(0.328 0.147 293.4 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.666 0.169 296.2 / 0.15)"
  },
  gray: {
    track: "oklch(0.597 0.014 271.2 / 0.2)",
    trackDark: "oklch(0.597 0.014 271.2 / 0.35)",
    indicator: "oklch(0.597 0.014 271.2)",
    indicatorDark: "oklch(0.745 0.015 272.6)",
    shadow: "inset 0 0 0 1px oklch(0.365 0.016 270.9 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.745 0.015 272.6 / 0.15)"
  }
};
function useDarkMode() {
  const [isDark, setIsDark] = React26.useState(
    () => typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false
  );
  React26.useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(el.classList.contains("dark"));
    });
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}
function Progress2({
  className,
  value,
  color = "blue",
  ...props
}) {
  const isDark = useDarkMode();
  const colors = progressColors[color];
  return /* @__PURE__ */ jsx63(
    Progress,
    {
      "data-slot": "progress",
      "data-color": color,
      className: cn(
        "relative h-2 w-full overflow-hidden rounded-full",
        className
      ),
      style: {
        backgroundColor: isDark ? colors.trackDark : colors.track,
        boxShadow: isDark ? colors.shadowDark : colors.shadow
      },
      value,
      ...props,
      children: /* @__PURE__ */ jsx63(
        ProgressIndicator,
        {
          "data-slot": "progress-indicator",
          className: "h-full w-full flex-1",
          style: {
            backgroundColor: isDark ? colors.indicatorDark : colors.indicator
          }
        }
      )
    }
  );
}

// src/components/animate-ui/primitives/radix/radio-group.tsx
import * as React27 from "react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence11, motion as motion14 } from "motion/react";
import { jsx as jsx64 } from "react/jsx-runtime";
var [RadioGroupProvider, useRadioGroup] = getStrictContext("RadioGroupContext");
var [RadioGroupItemProvider, useRadioGroupItem] = getStrictContext("RadioGroupItemContext");
function RadioGroup(props) {
  const [value, setValue] = useControlledState({
    value: props.value ?? void 0,
    defaultValue: props.defaultValue,
    onChange: props.onValueChange
  });
  return /* @__PURE__ */ jsx64(RadioGroupProvider, { value: { value, setValue }, children: /* @__PURE__ */ jsx64(
    RadioGroupPrimitive.Root,
    {
      "data-slot": "radio-group",
      ...props,
      onValueChange: setValue
    }
  ) });
}
function RadioGroupIndicator({
  transition = { type: "spring", stiffness: 500, damping: 30 },
  ...props
}) {
  const { isChecked } = useRadioGroupItem();
  return /* @__PURE__ */ jsx64(AnimatePresence11, { children: isChecked && /* @__PURE__ */ jsx64(
    RadioGroupPrimitive.Indicator,
    {
      "data-slot": "radio-group-indicator",
      asChild: true,
      forceMount: true,
      children: /* @__PURE__ */ jsx64(
        motion14.div,
        {
          "data-slot": "radio-group-indicator-circle",
          initial: { opacity: 0, scale: 0 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0 },
          transition,
          ...props
        },
        "radio-group-indicator-circle"
      )
    }
  ) });
}
function RadioGroupItem({
  value: valueProps,
  disabled,
  required,
  ...props
}) {
  const { value } = useRadioGroup();
  const [isChecked, setIsChecked] = React27.useState(value === valueProps);
  React27.useEffect(() => {
    setIsChecked(value === valueProps);
  }, [value, valueProps]);
  return /* @__PURE__ */ jsx64(RadioGroupItemProvider, { value: { isChecked, setIsChecked }, children: /* @__PURE__ */ jsx64(
    RadioGroupPrimitive.Item,
    {
      asChild: true,
      value: valueProps,
      disabled,
      required,
      children: /* @__PURE__ */ jsx64(
        motion14.button,
        {
          "data-slot": "radio-group-item",
          whileHover: { scale: 1.05 },
          whileTap: { scale: 0.95 },
          ...props
        }
      )
    }
  ) });
}

// src/components/ui/radio-group.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
function RadioGroup2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx65(
    RadioGroup,
    {
      className: cn("grid gap-3", className),
      ...props
    }
  );
}
function RadioGroupItem2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx65(
    RadioGroupItem,
    {
      className: cn(
        "group/radio relative aspect-square size-4 shrink-0 rounded-full bg-white text-primary shadow-checkbox inset-shadow-checkbox transition-[color,box-shadow] duration-150 outline-none hover:inset-shadow-checkbox-hover focus-visible:inset-shadow-[0_0_0_1px_var(--color-blue-500)] focus-visible:shadow-[0_0_0_3px_oklch(0.541_0.167_262.9/0.1)] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:inset-shadow-[0_0_0_1px_var(--color-destructive)] aria-invalid:shadow-[0_0_0_3px_oklch(0.469_0.173_10.9/0.2)] dark:bg-input/30 dark:shadow-none dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.25)] dark:focus-visible:inset-shadow-[0_0_0_1px_var(--color-blue-500)] dark:focus-visible:shadow-[0_0_0_3px_oklch(0.541_0.167_262.9/0.25)] dark:aria-invalid:ring-destructive/40",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx65(
        RadioGroupIndicator,
        {
          className: "absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary dark:bg-blue-500"
        }
      )
    }
  );
}

// src/components/ui/resizable.tsx
import { GripVerticalIcon } from "lucide-react";
import * as ResizablePrimitive from "react-resizable-panels";
import { jsx as jsx66 } from "react/jsx-runtime";
function ResizablePanelGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx66(
    ResizablePrimitive.Group,
    {
      "data-slot": "resizable-panel-group",
      className: cn(
        "flex h-full w-full aria-[orientation=vertical]:flex-col",
        className
      ),
      ...props
    }
  );
}
function ResizablePanel({ ...props }) {
  return /* @__PURE__ */ jsx66(ResizablePrimitive.Panel, { "data-slot": "resizable-panel", ...props });
}
function ResizableHandle({
  withHandle,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx66(
    ResizablePrimitive.Separator,
    {
      "data-slot": "resizable-handle",
      className: cn(
        "relative flex w-px items-center justify-center bg-border after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-hidden aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full aria-[orientation=horizontal]:after:left-0 aria-[orientation=horizontal]:after:h-1 aria-[orientation=horizontal]:after:w-full aria-[orientation=horizontal]:after:translate-x-0 aria-[orientation=horizontal]:after:-translate-y-1/2 [&[aria-orientation=horizontal]>div]:rotate-90",
        className
      ),
      ...props,
      children: withHandle && /* @__PURE__ */ jsx66("div", { className: "z-10 flex h-4 w-3 items-center justify-center rounded-xs border bg-border", children: /* @__PURE__ */ jsx66(GripVerticalIcon, { className: "size-2.5" }) })
    }
  );
}

// src/components/ui/select.tsx
import * as React28 from "react";
import { cva as cva12 } from "class-variance-authority";
import { ChevronDownIcon as ChevronDownIcon6, ChevronUpIcon } from "lucide-react";
import { Select as SelectPrimitive } from "radix-ui";
import { jsx as jsx67, jsxs as jsxs26 } from "react/jsx-runtime";
var selectTriggerVariants = cva12(
  "flex w-fit cursor-pointer items-center justify-between gap-2 rounded-lg border-none px-3 py-2 text-sm font-medium text-gray-600 whitespace-nowrap transition-[color,box-shadow] duration-150 outline-none focus-visible:ring-[3px] focus-visible:ring-blue-500/15 focus-visible:shadow-[0_0_0_1px_var(--color-blue-500)] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:shadow-[0_0_0_1px_var(--color-destructive)] aria-invalid:ring-destructive/20 data-[placeholder]:text-gray-400 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 dark:focus-visible:shadow-[0_0_0_1px_var(--color-blue-500)] dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
  {
    variants: {
      variant: {
        default: "bg-white shadow-btn-secondary hover:shadow-btn-secondary-hover dark:bg-input/30 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.25)]",
        outline: "bg-transparent shadow-[0_0_0_1px_rgb(0_0_0/0.08)] hover:shadow-[0_0_0_1px_rgb(0_0_0/0.16)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.2)]"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var SelectValueContext = React28.createContext(void 0);
function Select({
  value: controlledValue,
  defaultValue,
  onValueChange,
  ...props
}) {
  const [internalValue, setInternalValue] = React28.useState(
    controlledValue ?? defaultValue
  );
  React28.useEffect(() => {
    if (controlledValue !== void 0) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);
  const handleValueChange = React28.useCallback(
    (newValue) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange]
  );
  return /* @__PURE__ */ jsx67(SelectValueContext.Provider, { value: internalValue, children: /* @__PURE__ */ jsx67(
    SelectPrimitive.Root,
    {
      "data-slot": "select",
      value: controlledValue,
      defaultValue,
      onValueChange: handleValueChange,
      ...props
    }
  ) });
}
function SelectGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx67(SelectPrimitive.Group, { "data-slot": "select-group", ...props });
}
function SelectValue({
  ...props
}) {
  return /* @__PURE__ */ jsx67(SelectPrimitive.Value, { "data-slot": "select-value", ...props });
}
function SelectTrigger({
  className,
  size = "default",
  variant = "default",
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs26(
    SelectPrimitive.Trigger,
    {
      "data-slot": "select-trigger",
      "data-size": size,
      "data-variant": variant,
      className: cn(selectTriggerVariants({ variant }), className),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx67(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx67(ChevronDownIcon6, { className: "size-4 text-muted-foreground" }) })
      ]
    }
  );
}
function SelectContent({
  className,
  children,
  position = "item-aligned",
  align = "center",
  ...props
}) {
  return /* @__PURE__ */ jsx67(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs26(
    SelectPrimitive.Content,
    {
      "data-slot": "select-content",
      className: cn(
        "relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover text-secondary-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ),
      position,
      align,
      ...props,
      children: [
        /* @__PURE__ */ jsx67(SelectScrollUpButton, {}),
        /* @__PURE__ */ jsx67(
          SelectPrimitive.Viewport,
          {
            className: cn(
              "p-1",
              position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
            ),
            children
          }
        ),
        /* @__PURE__ */ jsx67(SelectScrollDownButton, {})
      ]
    }
  ) });
}
function SelectLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx67(
    SelectPrimitive.Label,
    {
      "data-slot": "select-label",
      className: cn("px-2 py-1.5 text-xs font-medium text-gray-400", className),
      ...props
    }
  );
}
function SelectItem({
  className,
  children,
  value,
  ...props
}) {
  const currentValue = React28.useContext(SelectValueContext);
  const isChecked = currentValue === value;
  return /* @__PURE__ */ jsxs26(
    SelectPrimitive.Item,
    {
      "data-slot": "select-item",
      value,
      className: cn(
        "relative flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 pr-8 text-sm font-medium outline-hidden transition-colors duration-150 select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx67(
          "span",
          {
            "data-slot": "select-item-indicator",
            className: "absolute right-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx67(AnimatedCheckIcon, { checked: isChecked })
          }
        ),
        /* @__PURE__ */ jsx67(SelectPrimitive.ItemText, { children })
      ]
    }
  );
}
function SelectSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx67(
    SelectPrimitive.Separator,
    {
      "data-slot": "select-separator",
      className: cn("pointer-events-none -mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function SelectScrollUpButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx67(
    SelectPrimitive.ScrollUpButton,
    {
      "data-slot": "select-scroll-up-button",
      className: cn(
        "flex cursor-default items-center justify-center py-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx67(ChevronUpIcon, { className: "size-4" })
    }
  );
}
function SelectScrollDownButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx67(
    SelectPrimitive.ScrollDownButton,
    {
      "data-slot": "select-scroll-down-button",
      className: cn(
        "flex cursor-default items-center justify-center py-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx67(ChevronDownIcon6, { className: "size-4" })
    }
  );
}

// src/components/ui/slider.tsx
import * as React29 from "react";
import { Slider as SliderPrimitive } from "radix-ui";
import { motion as motion15, useSpring as useSpring2, useTransform } from "motion/react";
import { jsx as jsx68, jsxs as jsxs27 } from "react/jsx-runtime";
function useDarkMode2() {
  const [isDark, setIsDark] = React29.useState(
    () => typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false
  );
  React29.useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(el.classList.contains("dark"));
    });
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}
var springConfig = { stiffness: 500, damping: 20, mass: 0.6 };
function SliderThumb({ isDark }) {
  const [isActive, setIsActive] = React29.useState(false);
  const scaleRaw = useSpring2(1, springConfig);
  const scale = useTransform(scaleRaw, (v) => v);
  React29.useEffect(() => {
    scaleRaw.set(isActive ? 1.25 : 1);
  }, [isActive, scaleRaw]);
  return /* @__PURE__ */ jsx68(SliderPrimitive.Thumb, { asChild: true, children: /* @__PURE__ */ jsx68(
    motion15.span,
    {
      "data-slot": "slider-thumb",
      className: cn(
        "block size-4 shrink-0 rounded-full bg-white outline-none",
        "disabled:pointer-events-none disabled:opacity-50",
        "focus-visible:inset-shadow-[0_0_0_1px_var(--color-blue-500)] focus-visible:shadow-[0_0_0_3px_oklch(0.541_0.167_262.9/0.1)]",
        "dark:bg-gray-950 dark:focus-visible:inset-shadow-[0_0_0_1px_var(--color-blue-500)] dark:focus-visible:shadow-[0_0_0_3px_oklch(0.541_0.167_262.9/0.25)]"
      ),
      style: {
        scale,
        boxShadow: isDark ? "0 0 0 1px rgba(255,255,255,0.12), 0 1px 3px 0 rgba(0,0,0,0.4)" : "0 0 0 1px rgba(0,0,0,0.1), 0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)"
      },
      onPointerDown: () => setIsActive(true),
      onPointerUp: () => setIsActive(false),
      onPointerCancel: () => setIsActive(false),
      onPointerLeave: () => {
        if (isActive) setIsActive(false);
      }
    }
  ) });
}
function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  color = "blue",
  ...props
}) {
  const _values = React29.useMemo(
    () => Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max],
    [value, defaultValue, min, max]
  );
  const isDark = useDarkMode2();
  const colors = progressColors[color];
  return /* @__PURE__ */ jsxs27(
    SliderPrimitive.Root,
    {
      "data-slot": "slider",
      defaultValue,
      value,
      min,
      max,
      className: cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx68(
          SliderPrimitive.Track,
          {
            "data-slot": "slider-track",
            className: "relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-2 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-2",
            style: {
              backgroundColor: isDark ? colors.trackDark : colors.track,
              boxShadow: isDark ? colors.shadowDark : colors.shadow
            },
            children: /* @__PURE__ */ jsx68(
              SliderPrimitive.Range,
              {
                "data-slot": "slider-range",
                className: "absolute rounded-full data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full",
                style: {
                  backgroundColor: isDark ? colors.indicatorDark : colors.indicator
                }
              }
            )
          }
        ),
        Array.from({ length: _values.length }, (_, index) => /* @__PURE__ */ jsx68(SliderThumb, { isDark }, index))
      ]
    }
  );
}

// src/components/ui/snapshot.tsx
import * as React32 from "react";
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  PencilIcon
} from "lucide-react";

// src/components/ui/table.tsx
import * as React30 from "react";
import { jsx as jsx69 } from "react/jsx-runtime";
var TableContext = React30.createContext("default");
function useTableVariant() {
  return React30.useContext(TableContext);
}
function Table({ className, variant = "default", ...props }) {
  const { ref, style } = useHorizontalFade();
  return /* @__PURE__ */ jsx69(TableContext.Provider, { value: variant, children: /* @__PURE__ */ jsx69(
    "div",
    {
      "data-slot": "table-container",
      className: cn(
        "relative w-full",
        variant === "contained" && "overflow-hidden rounded-lg border"
      ),
      children: /* @__PURE__ */ jsx69(
        "div",
        {
          ref,
          style,
          "data-slot": "table-scroll",
          "data-fade-edges-x": "true",
          className: "w-full overflow-x-auto",
          children: /* @__PURE__ */ jsx69(
            "table",
            {
              "data-slot": "table",
              className: cn("w-full caption-bottom text-sm", className),
              ...props
            }
          )
        }
      )
    }
  ) });
}
function TableHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx69(
    "thead",
    {
      "data-slot": "table-header",
      className: cn("[&_tr]:border-b", className),
      ...props
    }
  );
}
function TableBody({ className, ...props }) {
  return /* @__PURE__ */ jsx69(
    "tbody",
    {
      "data-slot": "table-body",
      className: cn("[&_tr:last-child]:border-0", className),
      ...props
    }
  );
}
function TableFooter({ className, ...props }) {
  const variant = useTableVariant();
  return /* @__PURE__ */ jsx69(
    "tfoot",
    {
      "data-slot": "table-footer",
      className: cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        variant === "contained" && "[&>tr:last-child>td:first-child]:rounded-bl-lg [&>tr:last-child>td:last-child]:rounded-br-lg",
        className
      ),
      ...props
    }
  );
}
function TableRow({ className, ...props }) {
  return /* @__PURE__ */ jsx69(
    "tr",
    {
      "data-slot": "table-row",
      className: cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className
      ),
      ...props
    }
  );
}
function TableHead({ className, ...props }) {
  const variant = useTableVariant();
  return /* @__PURE__ */ jsx69(
    "th",
    {
      "data-slot": "table-head",
      className: cn(
        "h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-gray-500 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        variant === "contained" && "first:pl-4 last:pr-4",
        className
      ),
      ...props
    }
  );
}
function TableCell({ className, ...props }) {
  const variant = useTableVariant();
  return /* @__PURE__ */ jsx69(
    "td",
    {
      "data-slot": "table-cell",
      className: cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        variant === "contained" && "first:pl-4 last:pr-4",
        className
      ),
      ...props
    }
  );
}
function TableCaption({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx69(
    "caption",
    {
      "data-slot": "table-caption",
      className: cn("mt-4 text-sm text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/timestamp.tsx
import * as React31 from "react";
import { jsx as jsx70 } from "react/jsx-runtime";
function defaultFormatUTC(date) {
  return date.toLocaleString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short"
  });
}
function Timestamp({
  date,
  children,
  formatHover = defaultFormatUTC,
  align = "left",
  className,
  ...props
}) {
  const parsed = React31.useMemo(
    () => date instanceof Date ? date : new Date(date),
    [date]
  );
  const hoverText = React31.useMemo(
    () => formatHover(parsed),
    [parsed, formatHover]
  );
  return /* @__PURE__ */ jsx70(
    TextOverflow,
    {
      "data-slot": "timestamp",
      align,
      delay: 0,
      alwaysShow: true,
      nowrap: true,
      hoverContent: hoverText,
      className: cn("cursor-pointer", className),
      ...props,
      children
    }
  );
}

// src/components/ui/snapshot.tsx
import { jsx as jsx71, jsxs as jsxs28 } from "react/jsx-runtime";
var TRANSFER_TYPES = [
  "ACH Debit",
  "ACH Credit",
  "Book",
  "Check",
  "International wire",
  "Wire",
  "Realtime"
];
var TRANSFER_TYPE_LABELS = {
  "ACH Debit": "ACH Debit",
  "ACH Credit": "ACH Credit",
  "Book": "Book",
  "Check": "Check",
  "International wire": "Intl Wire",
  "Wire": "Wire",
  "Realtime": "Realtime"
};
var TRANSFER_STATUSES = [
  "issued",
  "submitted",
  "completed",
  "returned",
  "canceled"
];
var FADE_SIZE = 32;
var FADE_DURATION = 150;
function easeOut(t) {
  return 1 - (1 - t) ** 3;
}
function useAnimatedValue(target, duration) {
  const [value, setValue] = React32.useState(target ? 1 : 0);
  const rafRef = React32.useRef(0);
  const startRef = React32.useRef(null);
  React32.useEffect(() => {
    const to = target ? 1 : 0;
    const currentValue = value;
    startRef.current = { time: performance.now(), from: currentValue };
    function tick(now) {
      if (!startRef.current) return;
      const elapsed = now - startRef.current.time;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOut(progress);
      const next = startRef.current.from + (to - startRef.current.from) * eased;
      setValue(next);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return value;
}
function useHorizontalFade2() {
  const [showLeft, setShowLeft] = React32.useState(false);
  const [showRight, setShowRight] = React32.useState(false);
  const ref = React32.useRef(null);
  const leftFade = useAnimatedValue(showLeft, FADE_DURATION);
  const rightFade = useAnimatedValue(showRight, FADE_DURATION);
  React32.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    function update() {
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setShowLeft(scrollLeft > 2);
      setShowRight(scrollLeft + clientWidth < scrollWidth - 2);
    }
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);
  const maskStyle = (() => {
    if (leftFade === 0 && rightFade === 0) return void 0;
    const leftSize = Math.round(leftFade * FADE_SIZE);
    const rightSize = Math.round(rightFade * FADE_SIZE);
    const gradient = `linear-gradient(to right, rgba(0,0,0,${1 - leftFade}) 0px, black ${leftSize}px, black calc(100% - ${rightSize}px), rgba(0,0,0,${1 - rightFade}) 100%)`;
    return {
      maskImage: gradient,
      WebkitMaskImage: gradient
    };
  })();
  return { ref, maskStyle };
}
function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  }).format(value);
}
function formatRelativeTime(timestamp) {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1e3);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "just now";
}
var statusStyles = {
  issued: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  submitted: "bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300",
  completed: "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300",
  returned: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300",
  canceled: "bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-400"
};
function SnapshotEditDialog({
  title,
  filter = {},
  onFilterChange,
  children
}) {
  const [direction, setDirection] = React32.useState(
    filter.direction ?? "all"
  );
  const [selectedTypes, setSelectedTypes] = React32.useState(
    new Set(filter.types ?? TRANSFER_TYPES)
  );
  const [selectedStatuses, setSelectedStatuses] = React32.useState(new Set(filter.statuses ?? TRANSFER_STATUSES));
  const [minAmount, setMinAmount] = React32.useState(
    filter.minAmount?.toString() ?? ""
  );
  const [maxAmount, setMaxAmount] = React32.useState(
    filter.maxAmount?.toString() ?? ""
  );
  function toggleType(type) {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }
  function toggleStatus(status) {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }
  function handleSave() {
    onFilterChange?.({
      direction,
      types: Array.from(selectedTypes),
      statuses: Array.from(selectedStatuses),
      minAmount: minAmount ? parseFloat(minAmount) : void 0,
      maxAmount: maxAmount ? parseFloat(maxAmount) : void 0
    });
  }
  return /* @__PURE__ */ jsxs28(Dialog2, { children: [
    /* @__PURE__ */ jsx71(DialogTrigger2, { asChild: true, children }),
    /* @__PURE__ */ jsxs28(DialogContent2, { showCloseButton: false, children: [
      /* @__PURE__ */ jsxs28(DialogHeader, { children: [
        /* @__PURE__ */ jsx71(DialogTitle2, { children: "Edit Snapshot" }),
        /* @__PURE__ */ jsxs28(DialogDescription2, { children: [
          "Configure the filters for \u201C",
          title,
          "\u201D"
        ] })
      ] }),
      /* @__PURE__ */ jsxs28("div", { "data-slot": "snapshot-edit-form", className: "grid gap-5", children: [
        /* @__PURE__ */ jsxs28("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx71(Label, { children: "Direction" }),
          /* @__PURE__ */ jsxs28(Select, { value: direction, onValueChange: setDirection, children: [
            /* @__PURE__ */ jsx71(SelectTrigger, { className: "w-full", children: /* @__PURE__ */ jsx71(SelectValue, {}) }),
            /* @__PURE__ */ jsxs28(SelectContent, { children: [
              /* @__PURE__ */ jsx71(SelectItem, { value: "all", children: "All" }),
              /* @__PURE__ */ jsx71(SelectItem, { value: "incoming", children: "Incoming" }),
              /* @__PURE__ */ jsx71(SelectItem, { value: "outgoing", children: "Outgoing" })
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxs28("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx71(Label, { children: "Amount range" }),
          /* @__PURE__ */ jsxs28("div", { className: "grid grid-cols-2 gap-3", children: [
            /* @__PURE__ */ jsx71(
              Input,
              {
                type: "number",
                placeholder: "Min",
                value: minAmount,
                onChange: (e) => setMinAmount(e.target.value)
              }
            ),
            /* @__PURE__ */ jsx71(
              Input,
              {
                type: "number",
                placeholder: "Max",
                value: maxAmount,
                onChange: (e) => setMaxAmount(e.target.value)
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs28("div", { className: "grid gap-2.5", children: [
          /* @__PURE__ */ jsx71(Label, { children: "Transfer types" }),
          /* @__PURE__ */ jsx71("div", { className: "grid grid-cols-2 gap-x-4 gap-y-2.5", children: TRANSFER_TYPES.map((type) => /* @__PURE__ */ jsxs28(
            "label",
            {
              className: "flex items-center gap-2 text-sm cursor-pointer",
              children: [
                /* @__PURE__ */ jsx71(
                  Checkbox2,
                  {
                    checked: selectedTypes.has(type),
                    onCheckedChange: () => toggleType(type)
                  }
                ),
                type
              ]
            },
            type
          )) })
        ] }),
        /* @__PURE__ */ jsxs28("div", { className: "grid gap-2.5", children: [
          /* @__PURE__ */ jsx71(Label, { children: "Statuses" }),
          /* @__PURE__ */ jsx71("div", { className: "grid grid-cols-2 gap-x-4 gap-y-2.5", children: TRANSFER_STATUSES.map((status) => /* @__PURE__ */ jsxs28(
            "label",
            {
              className: "flex items-center gap-2 text-sm capitalize cursor-pointer",
              children: [
                /* @__PURE__ */ jsx71(
                  Checkbox2,
                  {
                    checked: selectedStatuses.has(status),
                    onCheckedChange: () => toggleStatus(status)
                  }
                ),
                status
              ]
            },
            status
          )) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs28(DialogFooter, { children: [
        /* @__PURE__ */ jsx71(DialogClose2, { asChild: true, children: /* @__PURE__ */ jsx71(Button, { variant: "muted", children: "Cancel" }) }),
        /* @__PURE__ */ jsx71(DialogClose2, { asChild: true, children: /* @__PURE__ */ jsx71(Button, { onClick: handleSave, children: "Save filters" }) })
      ] })
    ] })
  ] });
}
function DirectionIcon({
  direction,
  className
}) {
  const isIncoming = direction === "incoming";
  const Icon = isIncoming ? ArrowDownLeftIcon : ArrowUpRightIcon;
  return /* @__PURE__ */ jsx71(
    "span",
    {
      "data-slot": "snapshot-direction-icon",
      className: cn(
        "inline-flex size-4 items-center justify-center text-gray-400 dark:text-gray-500",
        className
      ),
      children: /* @__PURE__ */ jsx71(Icon, { className: "size-3.5" })
    }
  );
}
function Snapshot({
  title,
  transfers,
  filter,
  onTransferClick,
  onFilterChange,
  size = "default",
  className,
  ...props
}) {
  const { ref: fadeRef, maskStyle } = useHorizontalFade2();
  return /* @__PURE__ */ jsxs28(
    Card,
    {
      "data-slot": "snapshot",
      size,
      className: cn("overflow-hidden rounded-lg gap-0 py-0 shadow-none", className),
      ...props,
      children: [
        /* @__PURE__ */ jsxs28(CardHeader, { className: "grid-rows-[auto] gap-0 items-center border-b px-4 pt-3 pb-2 [.border-b]:pb-2", children: [
          /* @__PURE__ */ jsx71(CardTitle, { className: "text-sm font-medium tracking-tight", children: title }),
          /* @__PURE__ */ jsx71(CardAction, { className: "self-center", children: /* @__PURE__ */ jsx71(
            SnapshotEditDialog,
            {
              title,
              filter,
              onFilterChange,
              children: /* @__PURE__ */ jsx71(
                Button,
                {
                  variant: "muted",
                  size: "icon-xs",
                  "aria-label": "Edit snapshot filters",
                  children: /* @__PURE__ */ jsx71(PencilIcon, {})
                }
              )
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx71(CardContent, { className: "px-0 py-0", children: /* @__PURE__ */ jsx71(
          "div",
          {
            ref: fadeRef,
            className: "overflow-x-auto [&_[data-slot=table-container]]:overflow-visible",
            style: maskStyle,
            children: /* @__PURE__ */ jsx71(Table, { children: /* @__PURE__ */ jsx71(TableBody, { children: transfers.map((transfer) => {
              const isClickable = !!(onTransferClick || transfer.href);
              const row = /* @__PURE__ */ jsxs28(
                TableRow,
                {
                  "data-slot": "snapshot-row",
                  className: cn(
                    isClickable && "cursor-pointer hover:bg-muted/50 active:bg-muted"
                  ),
                  onClick: onTransferClick ? () => onTransferClick(transfer) : void 0,
                  children: [
                    /* @__PURE__ */ jsx71(TableCell, { className: "pl-4 w-[16px] pr-0", children: /* @__PURE__ */ jsx71(DirectionIcon, { direction: transfer.direction }) }),
                    /* @__PURE__ */ jsx71(TableCell, { className: "w-full pl-1.5", children: /* @__PURE__ */ jsx71("span", { className: "font-medium tracking-tight", children: formatCurrency(transfer.amount) }) }),
                    /* @__PURE__ */ jsx71(TableCell, { className: "font-medium text-muted-foreground", children: TRANSFER_TYPE_LABELS[transfer.type] }),
                    /* @__PURE__ */ jsx71(TableCell, { children: /* @__PURE__ */ jsx71(
                      Badge,
                      {
                        variant: "outline",
                        className: cn(
                          "capitalize border-transparent",
                          statusStyles[transfer.status]
                        ),
                        children: transfer.status
                      }
                    ) }),
                    /* @__PURE__ */ jsx71(TableCell, { className: "pr-4 text-right text-xs text-muted-foreground", children: /* @__PURE__ */ jsx71(Timestamp, { date: transfer.timestamp, align: "right", children: formatRelativeTime(transfer.timestamp) }) })
                  ]
                },
                transfer.id
              );
              if (transfer.href && !onTransferClick) {
                return /* @__PURE__ */ jsx71(
                  "a",
                  {
                    href: transfer.href,
                    className: "contents",
                    children: row
                  },
                  transfer.id
                );
              }
              return row;
            }) }) })
          }
        ) })
      ]
    }
  );
}

// src/components/ui/sonner.tsx
import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon as Loader2Icon2,
  OctagonXIcon,
  TriangleAlertIcon
} from "lucide-react";
import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";
import { jsx as jsx72 } from "react/jsx-runtime";
var Toaster = ({ ...props }) => {
  const { theme = "system" } = useTheme();
  return /* @__PURE__ */ jsx72(
    Sonner,
    {
      theme,
      className: "toaster group",
      icons: {
        success: /* @__PURE__ */ jsx72(CircleCheckIcon, { className: "size-4" }),
        info: /* @__PURE__ */ jsx72(InfoIcon, { className: "size-4" }),
        warning: /* @__PURE__ */ jsx72(TriangleAlertIcon, { className: "size-4" }),
        error: /* @__PURE__ */ jsx72(OctagonXIcon, { className: "size-4" }),
        loading: /* @__PURE__ */ jsx72(Loader2Icon2, { className: "size-4 animate-spin" })
      },
      toastOptions: {
        style: {
          backgroundColor: "var(--color-popover)",
          color: "var(--color-popover-foreground)",
          borderColor: "var(--color-border)",
          opacity: 1
        },
        ...props.toastOptions
      },
      style: {
        "--normal-bg": "var(--color-popover)",
        "--normal-text": "var(--color-popover-foreground)",
        "--normal-border": "var(--color-border)",
        "--border-radius": "var(--radius-lg)",
        "--success-bg": "var(--color-popover)",
        "--success-text": "var(--color-popover-foreground)",
        "--success-border": "var(--color-border)",
        "--error-bg": "var(--color-popover)",
        "--error-text": "var(--color-popover-foreground)",
        "--error-border": "var(--color-border)",
        "--info-bg": "var(--color-popover)",
        "--info-text": "var(--color-popover-foreground)",
        "--info-border": "var(--color-border)",
        "--warning-bg": "var(--color-popover)",
        "--warning-text": "var(--color-popover-foreground)",
        "--warning-border": "var(--color-border)"
      },
      ...props
    }
  );
};

// src/components/ui/status-indicator.tsx
import { cva as cva13 } from "class-variance-authority";
import { jsx as jsx73 } from "react/jsx-runtime";
var modeColors = {
  sandbox: {
    bg: "oklch(0.750 0.155 40.5)",
    border: "oklch(0.580 0.145 40.5)"
  },
  live: {
    bg: "oklch(0.695 0.119 165.5)",
    border: "oklch(0.529 0.100 165.5)"
  }
};
var statusIndicatorVariants = cva13(
  "inline-flex shrink-0 items-center justify-center",
  {
    variants: {
      size: {
        sm: "size-3",
        default: "size-4",
        lg: "size-5",
        xl: "size-6"
      }
    },
    defaultVariants: {
      size: "default"
    }
  }
);
function StatusIndicator({
  mode = "sandbox",
  size,
  className,
  ...props
}) {
  const colors = modeColors[mode];
  return /* @__PURE__ */ jsx73(
    "div",
    {
      "data-slot": "status-indicator",
      "data-mode": mode,
      className: cn(statusIndicatorVariants({ size }), className),
      style: { "--status-border": colors.border },
      ...props,
      children: /* @__PURE__ */ jsx73(
        "div",
        {
          className: "size-1/2 rounded-full shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--status-border)_50%,transparent)] dark:shadow-none",
          style: { backgroundColor: colors.bg }
        }
      )
    }
  );
}

// src/components/ui/tabs.tsx
import * as React33 from "react";
import { cva as cva14 } from "class-variance-authority";
import { motion as motion17 } from "motion/react";

// src/components/animate-ui/primitives/radix/tabs.tsx
import { Tabs as TabsPrimitive } from "radix-ui";
import {
  motion as motion16,
  AnimatePresence as AnimatePresence12
} from "motion/react";
import { jsx as jsx74 } from "react/jsx-runtime";
var [TabsProvider, useTabs] = getStrictContext("TabsContext");
function Tabs(props) {
  const [value, setValue] = useControlledState({
    value: props.value,
    defaultValue: props.defaultValue,
    onChange: props.onValueChange
  });
  return /* @__PURE__ */ jsx74(TabsProvider, { value: { value, setValue }, children: /* @__PURE__ */ jsx74(
    TabsPrimitive.Root,
    {
      "data-slot": "tabs",
      ...props,
      onValueChange: setValue
    }
  ) });
}
function TabsList(props) {
  return /* @__PURE__ */ jsx74(TabsPrimitive.List, { "data-slot": "tabs-list", ...props });
}
function TabsTrigger(props) {
  return /* @__PURE__ */ jsx74(TabsPrimitive.Trigger, { "data-slot": "tabs-trigger", ...props });
}
function TabsContent({
  value,
  forceMount,
  transition = { duration: 0.5, ease: "easeInOut" },
  ...props
}) {
  return /* @__PURE__ */ jsx74(AnimatePresence12, { mode: "wait", children: /* @__PURE__ */ jsx74(TabsPrimitive.Content, { asChild: true, forceMount, value, children: /* @__PURE__ */ jsx74(
    motion16.div,
    {
      "data-slot": "tabs-content",
      layout: true,
      layoutDependency: value,
      initial: { opacity: 0, filter: "blur(4px)" },
      animate: { opacity: 1, filter: "blur(0px)" },
      exit: { opacity: 0, filter: "blur(4px)" },
      transition,
      ...props
    }
  ) }) });
}

// src/components/ui/tabs.tsx
import { jsx as jsx75, jsxs as jsxs29 } from "react/jsx-runtime";
function Tabs2({
  className,
  orientation = "horizontal",
  ...props
}) {
  return /* @__PURE__ */ jsx75(
    Tabs,
    {
      "data-orientation": orientation,
      orientation,
      className: cn(
        "group/tabs flex gap-2 data-[orientation=horizontal]:flex-col",
        className
      ),
      ...props
    }
  );
}
var tabsListVariants = cva14(
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-[orientation=horizontal]/tabs:h-9 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        default: "bg-muted",
        line: "gap-1 bg-transparent",
        ghost: "bg-transparent"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var highlightSpring = { type: "spring", stiffness: 500, damping: 35 };
var TabsListContext = React33.createContext({
  variant: "default",
  layoutId: ""
});
function TabsList2({
  className,
  variant = "default",
  children,
  ...props
}) {
  const layoutId = React33.useId();
  return /* @__PURE__ */ jsx75(TabsListContext.Provider, { value: { variant: variant ?? "default", layoutId }, children: /* @__PURE__ */ jsx75(
    TabsList,
    {
      "data-variant": variant,
      className: cn(tabsListVariants({ variant }), className),
      ...props,
      children
    }
  ) });
}
function TabsTrigger2({
  className,
  ...props
}) {
  const { value: activeValue } = useTabs();
  const { variant, layoutId } = React33.useContext(TabsListContext);
  const isActive = activeValue === props.value;
  return /* @__PURE__ */ jsxs29(
    TabsTrigger,
    {
      className: cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap text-foreground/60 transition-colors duration-150 group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "data-[state=active]:text-foreground dark:data-[state=active]:text-foreground",
        className
      ),
      ...props,
      children: [
        isActive && /* @__PURE__ */ jsx75(
          motion17.span,
          {
            layoutId: `tab-highlight-${layoutId}`,
            className: cn(
              "absolute inset-0 z-0 rounded-md",
              variant === "default" && "bg-background shadow-sm dark:border dark:border-input dark:bg-input/30",
              variant === "line" && "inset-auto bottom-[-4px] left-0 right-0 h-0.5 rounded-full bg-foreground",
              variant === "ghost" && "bg-muted dark:bg-white/[0.06]"
            ),
            transition: highlightSpring
          }
        ),
        /* @__PURE__ */ jsx75("span", { className: "relative z-10", children: props.children })
      ]
    }
  );
}
function TabsContent2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx75(
    TabsContent,
    {
      className: cn("flex-1 outline-none", className),
      ...props
    }
  );
}

// src/components/ui/toggle.tsx
import { cva as cva15 } from "class-variance-authority";

// src/components/animate-ui/primitives/radix/toggle.tsx
import { Toggle as TogglePrimitive } from "radix-ui";
import { motion as motion18, AnimatePresence as AnimatePresence13 } from "motion/react";
import { jsx as jsx76 } from "react/jsx-runtime";
var [ToggleProvider, useToggle] = getStrictContext("ToggleContext");
function Toggle({
  pressed,
  defaultPressed,
  onPressedChange,
  disabled,
  ...props
}) {
  const [isPressed, setIsPressed] = useControlledState({
    value: pressed,
    defaultValue: defaultPressed,
    onChange: onPressedChange
  });
  return /* @__PURE__ */ jsx76(ToggleProvider, { value: { isPressed, setIsPressed, disabled }, children: /* @__PURE__ */ jsx76(
    TogglePrimitive.Root,
    {
      pressed,
      defaultPressed,
      onPressedChange: setIsPressed,
      disabled,
      asChild: true,
      children: /* @__PURE__ */ jsx76(
        motion18.button,
        {
          "data-slot": "toggle",
          whileTap: { scale: 0.95 },
          ...props
        }
      )
    }
  ) });
}

// src/components/ui/toggle.tsx
import { jsx as jsx77 } from "react/jsx-runtime";
var toggleVariants = cva15(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-[color,box-shadow] outline-none hover:bg-muted hover:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground"
      },
      size: {
        default: "h-9 min-w-9 px-2",
        sm: "h-8 min-w-8 px-1.5",
        lg: "h-10 min-w-10 px-2.5"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Toggle2({
  className,
  variant,
  size,
  ...props
}) {
  return /* @__PURE__ */ jsx77(
    Toggle,
    {
      className: cn(toggleVariants({ variant, size, className })),
      ...props
    }
  );
}

// src/components/ui/toggle-group.tsx
import * as React35 from "react";

// src/components/animate-ui/primitives/radix/toggle-group.tsx
import * as React34 from "react";
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence14, motion as motion19 } from "motion/react";
import { Fragment as Fragment4, jsx as jsx78, jsxs as jsxs30 } from "react/jsx-runtime";
var [ToggleGroupProvider, useToggleGroup] = getStrictContext("ToggleGroupContext");
function ToggleGroup(props) {
  const [value, setValue] = useControlledState({
    value: props.value,
    defaultValue: props.defaultValue,
    onChange: props.onValueChange
  });
  return /* @__PURE__ */ jsx78(ToggleGroupProvider, { value: { value, setValue, type: props.type }, children: /* @__PURE__ */ jsx78(
    ToggleGroupPrimitive.Root,
    {
      "data-slot": "toggle-group",
      ...props,
      onValueChange: setValue
    }
  ) });
}
function ToggleGroupItem({ value, disabled, ...props }) {
  return /* @__PURE__ */ jsx78(ToggleGroupPrimitive.Item, { value, disabled, asChild: true, children: /* @__PURE__ */ jsx78(
    motion19.button,
    {
      "data-slot": "toggle-group-item",
      whileTap: { scale: 0.95 },
      ...props
    }
  ) });
}

// src/components/ui/toggle-group.tsx
import { jsx as jsx79 } from "react/jsx-runtime";
var ToggleGroupContext = React35.createContext({
  size: "default",
  variant: "default",
  spacing: 0
});
function ToggleGroup2({
  className,
  variant,
  size,
  spacing = 0,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx79(
    ToggleGroup,
    {
      "data-variant": variant,
      "data-size": size,
      "data-spacing": spacing,
      style: { "--gap": spacing },
      className: cn(
        "group/toggle-group flex w-fit items-center gap-[--spacing(var(--gap))] rounded-md data-[spacing=default]:data-[variant=outline]:shadow-xs",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx79(ToggleGroupContext.Provider, { value: { variant, size, spacing }, children })
    }
  );
}
function ToggleGroupItem2({
  className,
  children,
  variant,
  size,
  ...props
}) {
  const context = React35.useContext(ToggleGroupContext);
  return /* @__PURE__ */ jsx79(
    ToggleGroupItem,
    {
      "data-variant": context.variant || variant,
      "data-size": context.size || size,
      "data-spacing": context.spacing,
      className: cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size
        }),
        "w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10",
        "data-[spacing=0]:rounded-none data-[spacing=0]:shadow-none data-[spacing=0]:first:rounded-l-md data-[spacing=0]:last:rounded-r-md data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l",
        className
      ),
      ...props,
      children
    }
  );
}

// src/components/ui/tooltip.tsx
import * as React37 from "react";

// src/components/animate-ui/primitives/radix/tooltip.tsx
import * as React36 from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import {
  AnimatePresence as AnimatePresence15,
  motion as motion20,
  useMotionValue as useMotionValue2,
  useSpring as useSpring3
} from "motion/react";
import { jsx as jsx80 } from "react/jsx-runtime";
var [LocalTooltipProvider, useTooltip] = getStrictContext("TooltipContext");
function TooltipProvider(props) {
  return /* @__PURE__ */ jsx80(TooltipPrimitive.Provider, { "data-slot": "tooltip-provider", ...props });
}
function Tooltip2({
  followCursor = false,
  followCursorSpringOptions = { stiffness: 200, damping: 17 },
  ...props
}) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  const x = useMotionValue2(0);
  const y = useMotionValue2(0);
  return /* @__PURE__ */ jsx80(
    LocalTooltipProvider,
    {
      value: {
        isOpen,
        setIsOpen,
        x,
        y,
        followCursor,
        followCursorSpringOptions
      },
      children: /* @__PURE__ */ jsx80(
        TooltipPrimitive.Root,
        {
          "data-slot": "tooltip",
          ...props,
          onOpenChange: setIsOpen
        }
      )
    }
  );
}
function TooltipTrigger({ onMouseMove, ...props }) {
  const { x, y, followCursor } = useTooltip();
  const handleMouseMove = (event) => {
    onMouseMove?.(event);
    const target = event.currentTarget.getBoundingClientRect();
    if (followCursor === "x" || followCursor === true) {
      const eventOffsetX = event.clientX - target.left;
      const offsetXFromCenter = (eventOffsetX - target.width / 2) / 2;
      x.set(offsetXFromCenter);
    }
    if (followCursor === "y" || followCursor === true) {
      const eventOffsetY = event.clientY - target.top;
      const offsetYFromCenter = (eventOffsetY - target.height / 2) / 2;
      y.set(offsetYFromCenter);
    }
  };
  return /* @__PURE__ */ jsx80(
    TooltipPrimitive.Trigger,
    {
      "data-slot": "tooltip-trigger",
      onMouseMove: handleMouseMove,
      ...props
    }
  );
}
function TooltipPortal(props) {
  const { isOpen } = useTooltip();
  return /* @__PURE__ */ jsx80(AnimatePresence15, { children: isOpen && /* @__PURE__ */ jsx80(
    TooltipPrimitive.Portal,
    {
      forceMount: true,
      "data-slot": "tooltip-portal",
      ...props
    }
  ) });
}
var TooltipContent = React36.forwardRef(({
  onEscapeKeyDown,
  onPointerDownOutside,
  side,
  sideOffset,
  align,
  alignOffset,
  avoidCollisions,
  collisionBoundary,
  collisionPadding,
  arrowPadding,
  sticky,
  hideWhenDetached,
  style,
  transition = { type: "spring", stiffness: 600, damping: 35 },
  ...props
}, ref) => {
  const { x, y, followCursor, followCursorSpringOptions } = useTooltip();
  const translateX = useSpring3(x, followCursorSpringOptions);
  const translateY = useSpring3(y, followCursorSpringOptions);
  return /* @__PURE__ */ jsx80(
    TooltipPrimitive.Content,
    {
      asChild: true,
      forceMount: true,
      align,
      alignOffset,
      side,
      sideOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      onEscapeKeyDown,
      onPointerDownOutside,
      children: /* @__PURE__ */ jsx80(
        motion20.div,
        {
          ref,
          "data-slot": "popover-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: {
            x: followCursor === "x" || followCursor === true ? translateX : void 0,
            y: followCursor === "y" || followCursor === true ? translateY : void 0,
            ...style
          },
          ...props
        },
        "popover-content"
      )
    }
  );
});
TooltipContent.displayName = "TooltipContent";

// src/components/ui/tooltip.tsx
import { jsx as jsx81 } from "react/jsx-runtime";
function TooltipProvider2({
  delayDuration = 0,
  ...props
}) {
  return /* @__PURE__ */ jsx81(
    TooltipProvider,
    {
      delayDuration,
      ...props
    }
  );
}
function Tooltip3({
  ...props
}) {
  return /* @__PURE__ */ jsx81(Tooltip2, { ...props });
}
function TooltipTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx81(TooltipTrigger, { ...props });
}
var TooltipContent2 = React37.forwardRef(({
  className,
  sideOffset = 0,
  children,
  ...props
}, ref) => {
  return /* @__PURE__ */ jsx81(TooltipPortal, { children: /* @__PURE__ */ jsx81(
    TooltipContent,
    {
      ref,
      sideOffset,
      className: cn(
        "z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-lg bg-popover px-2 py-1 text-xs font-medium text-muted-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props,
      children
    }
  ) });
});
TooltipContent2.displayName = "TooltipContent";
export {
  Accordion2 as Accordion,
  AccordionContent2 as AccordionContent,
  AccordionItem2 as AccordionItem,
  AccordionTrigger2 as AccordionTrigger,
  Alert,
  AlertDescription,
  AlertDialog2 as AlertDialog,
  AlertDialogAction2 as AlertDialogAction,
  AlertDialogCancel2 as AlertDialogCancel,
  AlertDialogContent2 as AlertDialogContent,
  AlertDialogDescription2 as AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay2 as AlertDialogOverlay,
  AlertDialogPortal2 as AlertDialogPortal,
  AlertDialogTitle2 as AlertDialogTitle,
  AlertDialogTrigger2 as AlertDialogTrigger,
  AlertTitle,
  AnimatedCheckIcon,
  AspectRatio,
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
  Badge,
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  ButtonGroup,
  ButtonGroupSeparator,
  ButtonGroupText,
  Calendar,
  CalendarDayButton,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
  Checkbox2 as Checkbox,
  Collapsible2 as Collapsible,
  CollapsibleContent2 as CollapsibleContent,
  CollapsibleTrigger2 as CollapsibleTrigger,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  DataTable,
  Dialog2 as Dialog,
  DialogClose2 as DialogClose,
  DialogContent2 as DialogContent,
  DialogDescription2 as DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay2 as DialogOverlay,
  DialogPortal2 as DialogPortal,
  DialogTitle2 as DialogTitle,
  DialogTrigger2 as DialogTrigger,
  DirectionIcon,
  DirectionProvider,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
  DropdownMenu2 as DropdownMenu,
  DropdownMenuCheckboxItem2 as DropdownMenuCheckboxItem,
  DropdownMenuContent2 as DropdownMenuContent,
  DropdownMenuGroup2 as DropdownMenuGroup,
  DropdownMenuItem2 as DropdownMenuItem,
  DropdownMenuLabel2 as DropdownMenuLabel,
  DropdownMenuPortal2 as DropdownMenuPortal,
  DropdownMenuRadioGroup2 as DropdownMenuRadioGroup,
  DropdownMenuRadioItem2 as DropdownMenuRadioItem,
  DropdownMenuSeparator2 as DropdownMenuSeparator,
  DropdownMenuShortcut2 as DropdownMenuShortcut,
  DropdownMenuSub2 as DropdownMenuSub,
  DropdownMenuSubContent2 as DropdownMenuSubContent,
  DropdownMenuSubTrigger2 as DropdownMenuSubTrigger,
  DropdownMenuTrigger2 as DropdownMenuTrigger,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HoverCard2 as HoverCard,
  HoverCardContent2 as HoverCardContent,
  HoverCardTrigger2 as HoverCardTrigger,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemHeader,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
  Kbd,
  KbdGroup,
  Label,
  Logo,
  LogoIcon,
  LogoWordmark,
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PlatformSwitcher,
  Popover2 as Popover,
  PopoverAnchor2 as PopoverAnchor,
  PopoverContent2 as PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger2 as PopoverTrigger,
  Progress2 as Progress,
  RadioGroup2 as RadioGroup,
  RadioGroupItem2 as RadioGroupItem,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  ScrollArea,
  ScrollBar,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Sheet2 as Sheet,
  SheetClose2 as SheetClose,
  SheetContent2 as SheetContent,
  SheetDescription2 as SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle2 as SheetTitle,
  SheetTrigger2 as SheetTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  Skeleton,
  Slider,
  Snapshot,
  SnapshotEditDialog,
  Spinner,
  StatusIndicator,
  Switch2 as Switch,
  TRANSFER_STATUSES,
  TRANSFER_TYPES,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs2 as Tabs,
  TabsContent2 as TabsContent,
  TabsList2 as TabsList,
  TabsTrigger2 as TabsTrigger,
  TextOverflow,
  Textarea,
  Timestamp,
  Toaster,
  Toggle2 as Toggle,
  ToggleGroup2 as ToggleGroup,
  ToggleGroupItem2 as ToggleGroupItem,
  Tooltip3 as Tooltip,
  TooltipContent2 as TooltipContent,
  TooltipProvider2 as TooltipProvider,
  TooltipTrigger2 as TooltipTrigger,
  badgeVariants,
  buttonGroupVariants,
  buttonVariants,
  cn,
  defaultFormatUTC,
  formatRelativeTime,
  inputVariants,
  navigationMenuTriggerStyle,
  progressColors,
  modeColors as statusIndicatorColors,
  tabsListVariants,
  toggleVariants,
  useComboboxAnchor,
  useDirection,
  useFormField,
  useIsMobile,
  useSidebar
};
//# sourceMappingURL=index.js.map